import { useState, useEffect, useMemo, useCallback, useRef, memo, Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Menu,
  MenuItem,
  IconButton,
  Select,
  Alert,
  Button,
  Snackbar,
  Autocomplete,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  useMediaQuery,
  useTheme,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  DragIndicator,
  Add,
  Delete,
  ExpandMore,
  ChevronRight,
  UnfoldMore,
  UnfoldLess,
  Visibility,
  VisibilityOff,
  ViewColumn,
  Lock,
  Close,
  FirstPage,
  People,
  Description,
  AccountTree,
} from '@mui/icons-material';
import { format, addDays, isWeekend, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import {
  DndContext,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ProjectPlan, PlanningPhase, PlanningTask, PlanningAction, RowType, ActionType, DocumentAssociation } from '../types';
import { Save, Download, Lock as LockIcon, GridOn, CloudUpload } from '@mui/icons-material';
import { ValidationErrorDisplay } from '../components/ValidationErrorDisplay';
import GanttChartView, { projectPlanToGanttData } from '../components/GanttChart/GanttChartView';
import {
  createProject,
  getProject,
  updateProject,
  lockProject,
  ProjectApiError,
  getPlanningSummary,
  getPhaseDetails,
  getProjectPlan,
  ProjectPlanResponse,
  bulkSavePlanning,
  PhaseDetailsResponse,
  BulkSaveRequest,
  recalculateProjectDates,
  getProjectLockStatus,
  validateProjectPlan,
  downloadSeededExcelTemplate,
  type ProgressPayload,
  type RecalculateValidationError,
} from '../services/projectApi';
import ExcelImportWizard from '../components/ProjectBuilder/ExcelImportWizard';
import { ApprovalRollbackOverlay } from '../components/Planning/ApprovalRollbackOverlay';
import {
  isRejectTargetAllowed,
  sourceUsedByAnotherApprove,
} from '../utils/approvalEntanglementUi';
import { getHolidaysForDateRange, Holiday } from '../services/holidayApi';
import { getNextBusinessDayDateString, getDefaultProjectStartDateString } from '../utils/dateUtils';
import DocumentAssociationPanel from '../components/Actions/DocumentAssociationPanel';
import { apiService } from '../services/api';
import {
  getPhases,
  createPhase,
  updatePhase,
  deletePhase,
  getTasks,
  createTask,
  updateTask,
  deleteTask,
  getActions,
  createAction,
  updateAction,
  deleteAction,
  getTaskAssignments,
  assignUserToTask,
  removeTaskAssignment,
  getActionAssignments,
  assignUserToAction,
  assignUsersToAction,
  removeActionAssignment,
  PlanningExecutionApiError,
} from '../services/planningExecutionApi';
import ProjectUserSelector from '../components/Project/ProjectUserSelector';
import ProjectUserManagement from '../components/Project/ProjectUserManagement';
import { User } from '../services/userApi';
import { getProjectUsers, ProjectUser } from '../services/projectUserApi';
import { useAuth } from '../contexts/AuthContext';
import { useModalOpen, useModalOpenRegistration } from '../contexts/ModalOpenContext';
import { hasFullAdminAccess, isFraportUser } from '../services/authApi';
import { createAuthHeaders } from '../utils/apiHelpers';

/** Coerce unknown to YYYY-MM-DD string for date fields that may be Date or string. */
function toDateOnly(v: unknown): string | null {
  if (v == null) return null;
  if ((v as object) instanceof Date) return (v as Date).toISOString().split('T')[0];
  if (typeof v === 'string') return v.split('T')[0];
  return null;
}

// Mock user list for assignment dropdowns (currently unused)
// @ts-ignore - intentionally unused
const _mockUsers = [
  'john.doe@fraport.com',
  'jane.smith@fraport.com',
  'bob.johnson@fraport.com',
  'alice.williams@fraport.com',
  'charlie.brown@fraport.com',
  'diana.davis@fraport.com',
  'edward.miller@fraport.com',
  'fiona.wilson@fraport.com',
];

// Helper type for grid rows
interface GridRow {
  id: string; // String ID for React keys (e.g., "phase-1", "task-2")
  type: RowType | 'PROJECT';
  level: number; // -1 = Project, 0 = Phase, 1 = Task, 2 = Action
  phaseId?: number; // Numeric phase ID
  taskId?: number; // Numeric task ID
  data: PlanningPhase | PlanningTask | PlanningAction | ProjectPlan;
  expanded?: boolean;
}

// Enhanced duration input component
interface EnhancedDurationInputProps {
  value: string;
  isEditing: boolean;
  validationError: string | null;
  isDurationCalculated: boolean;
  rowType: RowType | 'PROJECT';
  onCellEdit: () => void;
  onCellSave: (value: string) => void;
  setEditingCell: React.Dispatch<React.SetStateAction<{ rowId: string; field: string; value: string | User[] } | null>>;
}

// Description input component with local state to prevent re-render issues
const DescriptionInput = ({
  initialValue,
  onSave,
  onCancel,
}: {
  initialValue: string;
  onSave: (value: string) => void;
  onCancel: () => void;
}) => {
  const [localValue, setLocalValue] = useState(initialValue);

  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  return (
    <TextField
      size="small"
      fullWidth
      value={localValue}
      onChange={(e) => {
        e.stopPropagation();
        setLocalValue(e.target.value);
      }}
      onBlur={(e) => {
        e.stopPropagation();
        onSave(e.target.value || '');
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          onSave((e.target as HTMLInputElement).value || '');
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
      onClick={(e) => e.stopPropagation()}
      autoFocus
    />
  );
};

// Required Document Count input component with local state
const RequiredDocumentCountInput = ({
  initialValue,
  onSave,
  onCancel,
  disabled,
}: {
  initialValue: string | number | null;
  onSave: (value: number | null) => void;
  onCancel: () => void;
  disabled?: boolean;
}) => {
  const [localValue, setLocalValue] = useState<string>(initialValue != null ? String(initialValue) : '');

  useEffect(() => {
    setLocalValue(initialValue != null ? String(initialValue) : '');
  }, [initialValue]);

  const handleSave = () => {
    if (localValue === '' || localValue === null) {
      onSave(null);
    } else {
      const numValue = parseInt(localValue, 10);
      if (!isNaN(numValue) && numValue >= 0) {
        onSave(numValue);
      } else {
        // Invalid value, revert to original
        onSave(initialValue != null ? (typeof initialValue === 'number' ? initialValue : parseInt(String(initialValue), 10)) : null);
      }
    }
  };

  return (
    <TextField
      type="number"
      size="small"
      value={localValue}
      onChange={(e) => {
        e.stopPropagation();
        const inputValue = e.target.value;
        // Allow empty string or valid numbers
        if (inputValue === '' || /^\d+$/.test(inputValue)) {
          setLocalValue(inputValue);
        }
      }}
      onBlur={(e) => {
        e.stopPropagation();
        handleSave();
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          handleSave();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onFocus={(e) => {
        e.stopPropagation();
        e.target.select();
      }}
      autoFocus
      inputProps={{ min: 0, step: 1 }}
      sx={{ width: 80 }}
      disabled={disabled}
    />
  );
};

const EnhancedDurationInput = ({
  value,
  isEditing,
  validationError,
  isDurationCalculated,
  rowType,
  onCellEdit,
  onCellSave,
  setEditingCell,
}: EnhancedDurationInputProps) => {
  const [durationValue, setDurationValue] = useState<string>('');

  useEffect(() => {
    if (isEditing && value) {
      // Parse existing value - value is in hours, convert to days
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 0) {
        // Convert hours to days (1 day = 8 working hours)
        const daysValue = Math.round(numValue / 8);
        setDurationValue(daysValue.toString());
      } else {
        setDurationValue('');
      }
    } else if (!isEditing) {
      // Reset when not editing
      setDurationValue('');
    }
  }, [isEditing, value]);

  const handleSave = () => {
    const numValue = parseInt(durationValue, 10);
    // Allow 0 as valid, only reject negative or NaN
    if (isNaN(numValue) || numValue < 0) {
      return;
    }
    // Convert days to hours for storage (1 day = 8 working hours)
    const hoursValue = numValue * 8;
    onCellSave(hoursValue.toString());
  };

  if (isEditing) {
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          type="number"
          value={durationValue}
          onChange={(e) => {
            // Only allow integer values
            const inputValue = e.target.value;
            if (inputValue === '' || /^\d+$/.test(inputValue)) {
              setDurationValue(inputValue);
            }
          }}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSave();
            } else if (e.key === 'Escape') {
              setEditingCell(null);
            }
          }}
          autoFocus
          placeholder="Enter days"
          error={!!validationError}
          helperText={validationError || ''}
          sx={{ width: 100 }}
          inputProps={{ min: 0, step: 1 }}
        />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {rowType === 'PROJECT' ? 'cal. days' : 'wrk days'}
        </Typography>
      </Box>
    );
  }

  // Format display value - always show as integer days
  const displayValue = (() => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue <= 0) return value;
    
    // Convert hours to days (1 day = 8 working hours) and round to integer
    const daysValue = Math.round(numValue / 8);
    // Add "cal" for PROJECT, "wrk" for other row types
    const dayLabel = rowType === 'PROJECT' 
      ? `${daysValue} ${daysValue === 1 ? 'cal. day' : 'cal. days'}`
      : `${daysValue} ${daysValue === 1 ? 'wrk day' : 'wrk days'}`;
    return dayLabel;
  })();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            minHeight: '20px', 
            fontFamily: 'monospace', 
            cursor: isDurationCalculated ? 'default' : 'text',
            ...(validationError && { color: '#d32f2f' }),
            ...(isDurationCalculated && !validationError && {
              fontStyle: 'italic',
              color: '#666',
            }),
          }}
          title={
            validationError 
              ? validationError || ''
              : isDurationCalculated
                ? 'Calculated duration (sum of action durations)' 
                : ''
          }
          onClick={() => {
            if (!isDurationCalculated) {
              onCellEdit();
            }
          }}
        >
          {displayValue}
        </Typography>
        {isDurationCalculated && !validationError && (
          <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }} title="Calculated">
            ⚡
          </Typography>
        )}
      </Box>
      {validationError && (
        <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: '0.7rem' }}>
          {validationError}
        </Typography>
      )}
    </Box>
  );
};

/** Sort by plan-id prefix descending (e.g. 4.5.6, 2.1.1, 1.1, 1) for prerequisite/source dropdowns */
function comparePlanIdDesc(labelA: string, labelB: string): number {
  const prefixA = (labelA.split(' - ')[0] ?? '').trim();
  const prefixB = (labelB.split(' - ')[0] ?? '').trim();
  const segA = prefixA.split('.').map(s => parseInt(s, 10) || 0);
  const segB = prefixB.split('.').map(s => parseInt(s, 10) || 0);
  for (let i = 0; i < Math.max(segA.length, segB.length); i++) {
    const va = segA[i] ?? 0;
    const vb = segB[i] ?? 0;
    if (va !== vb) return vb - va; // descending: higher level first
  }
  return 0;
}

// Option for prerequisites dropdown (can be phase or task)
interface PrerequisiteOption {
  id: number;
  name: string;
  displayName: string; // e.g., "1 - Phase Name" or "1.1 - Task Name"
  type: 'PHASE' | 'TASK';
}

// Droppable prerequisites cell component
interface DroppablePrerequisitesCellProps {
  rowId: string;
  rowType: RowType | 'PROJECT';
  isEditing: boolean;
  editingValue: string;
  displayValue: string;
  validationError: string | null;
  /** When set, enables per-prerequisite remove (e.g. to fix circular dependency) even when not editing */
  prerequisiteIds?: number[];
  onCellEdit: () => void;
  onCellSave: (value: string) => void;
  setEditingCell: React.Dispatch<React.SetStateAction<{ rowId: string; field: string; value: string | User[] } | null>>;
  getValidationError: (rowId: string, field: string) => string | null;
  projectPlan: ProjectPlan;
  currentTaskId?: number; // Exclude current task from options
  currentPhaseId?: number; // Exclude current phase from options
  isSaving?: boolean; // Loading state for save operation
}

const DroppablePrerequisitesCell = ({
  rowId,
  rowType,
  isEditing,
  editingValue,
  displayValue,
  validationError,
  prerequisiteIds = [],
  onCellEdit,
  onCellSave,
  setEditingCell,
  getValidationError,
  projectPlan,
  currentTaskId,
  currentPhaseId,
  isSaving = false,
}: DroppablePrerequisitesCellProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `prereq-${rowId}`,
  });

  // Get all available prerequisites (phases and tasks) for dropdown
  // For phases: show other phases as options
  // For tasks: show other tasks as options
  const availableOptions = useMemo((): PrerequisiteOption[] => {
    const options: PrerequisiteOption[] = [];
    
    if (rowType === 'PHASE') {
      // For phases, show other phases as prerequisites
      projectPlan.phases.forEach((phase) => {
        if (phase.phaseId !== currentPhaseId) {
          options.push({
            id: phase.phaseId,
            name: phase.name,
            displayName: `${phase.displayOrder} - ${phase.name}`,
            type: 'PHASE',
          });
        }
      });
    } else if (rowType === 'TASK') {
      // For tasks, show other tasks as prerequisites
      projectPlan.phases.forEach((phase) => {
        phase.tasks.forEach((task) => {
          if (task.taskId !== currentTaskId) {
            const phaseOrder = phase.displayOrder;
            const taskOrder = task.displayOrder;
            options.push({
              id: task.taskId,
              name: task.name,
              displayName: `${phaseOrder}.${taskOrder} - ${task.name}`,
              type: 'TASK',
            });
          }
        });
      });
    }
    
    return options.sort((a, b) => comparePlanIdDesc(a.displayName, b.displayName));
  }, [projectPlan, currentTaskId, currentPhaseId, rowType]);

  // When there's a validation error and we have prerequisite IDs, show removable chips so user can fix circular ref
  const showRemovableChips = !isEditing && validationError && prerequisiteIds.length > 0;

  // Convert prerequisite IDs to options for Autocomplete
  // Use a more robust matching that handles both string and number IDs
  const selectedOptions = useMemo(() => {
    if (!isEditing || !editingValue) return [];
    const idStrings = editingValue.split(',').map(s => s.trim()).filter(s => s);
    // Convert to numbers and find matching options
    const selected = availableOptions.filter(option => {
      return idStrings.some(idStr => {
        const idNum = parseInt(idStr, 10);
        return !isNaN(idNum) && option.id === idNum;
      });
    });
    // Ensure we return options in the same order as the IDs in editingValue
    return idStrings
      .map(idStr => {
        const idNum = parseInt(idStr, 10);
        if (isNaN(idNum)) return null;
        return selected.find(option => option.id === idNum);
      })
      .filter((option): option is PrerequisiteOption => option !== null);
  }, [isEditing, editingValue, availableOptions]);

  // Use a ref to track the current selected options for reliable access in onBlur
  const selectedOptionsRef = useRef<PrerequisiteOption[]>([]);
  
  // Convert option IDs back to comma-separated string
  const handleOptionChange = useCallback((newOptions: PrerequisiteOption[]) => {
    selectedOptionsRef.current = newOptions;
    const ids = newOptions.map(option => option.id.toString()).join(', ');
    // Update editingCell immediately to ensure UI reflects the change
    setEditingCell(prev => {
      if (prev && prev.rowId === rowId && prev.field === 'prerequisites') {
        return { ...prev, value: ids };
      }
      return { rowId, field: 'prerequisites', value: ids };
    });
  }, [rowId, setEditingCell]);
  
  // Update ref when selectedOptions changes
  useEffect(() => {
    selectedOptionsRef.current = selectedOptions;
  }, [selectedOptions]);

  // Show prerequisites for both PHASE and TASK rows
  const canHavePrerequisites = rowType === 'PHASE' || rowType === 'TASK';
  
  return (
    <TableCell
      ref={canHavePrerequisites ? setNodeRef : undefined}
      sx={{
        border: '1px solid #e0e0e0',
        padding: '4px 8px',
        backgroundColor: canHavePrerequisites ? 'transparent' : '#f5f5f5',
        '&:hover': { 
          backgroundColor: canHavePrerequisites ? '#f5f5f5' : '#f5f5f5',
        },
        ...(validationError && {
          border: '2px solid #d32f2f',
        }),
        ...(isOver && canHavePrerequisites && !isEditing && {
          backgroundColor: '#e3f2fd',
          border: '2px dashed #1976d2',
        }),
        ...(!canHavePrerequisites && {
          cursor: 'not-allowed',
          opacity: 0.6,
        }),
        // Ensure droppable area is always active when not editing (same as source document)
        ...(canHavePrerequisites && !isEditing && {
          pointerEvents: 'auto',
        }),
      }}
      onClick={() => {
        if (canHavePrerequisites && !isEditing) {
          onCellEdit();
        }
      }}
      title={
        canHavePrerequisites 
          ? (validationError || (isOver && !isEditing ? 'Drop here to add as prerequisite' : 'Click to edit prerequisites'))
          : 'Prerequisites are only available for phases and tasks'
      }
    >
      {canHavePrerequisites && isEditing ? (
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {isSaving && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255, 255, 255, 0.8)', zIndex: 10 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          <Autocomplete
            multiple
            size="small"
            options={availableOptions}
            getOptionLabel={(option) => option.displayName}
            value={selectedOptions}
            onChange={(_, newValue) => {
              handleOptionChange(newValue);
            }}
            onBlur={(e) => {
              // Only save if blur is not caused by clicking on the dropdown
              // Check if the related target is still within the Autocomplete
              if (e.relatedTarget && (e.currentTarget.contains(e.relatedTarget as Node) || 
                  (e.relatedTarget as HTMLElement).closest('.MuiAutocomplete-root'))) {
                return;
              }
              // Get current value from ref to ensure we have the latest selected options
              const currentIds = selectedOptionsRef.current.map(option => option.id.toString()).join(', ');
              // Store locally only - no API call
              onCellSave(currentIds);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                // Get current value from ref to ensure we have the latest
                const ids = selectedOptionsRef.current.map(option => option.id.toString()).join(', ');
                // Store locally only - no API call
                onCellSave(ids);
              } else if (e.key === 'Escape') {
                setEditingCell(null);
              }
            }}
            disabled={isSaving}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                placeholder={rowType === 'PHASE' ? 'Select prerequisite phases' : 'Select prerequisite tasks'}
                error={!!getValidationError(rowId, 'prerequisites')}
                helperText={isSaving ? 'Saving...' : (getValidationError(rowId, 'prerequisites') || '')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: isSaving ? <CircularProgress size={16} /> : params.InputProps.endAdornment,
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option.displayName}
                  size="small"
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
            sx={{ width: '100%' }}
          />
        </Box>
      ) : (
        <Box>
          {canHavePrerequisites ? (
            <>
              {showRemovableChips ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                  {prerequisiteIds.map((prereqId) => {
                    const option = availableOptions.find(o => o.id === prereqId);
                    const label = option?.displayName ?? String(prereqId);
                    return (
                      <Chip
                        key={prereqId}
                        size="small"
                        variant="outlined"
                        label={label}
                        onDelete={() => {
                          const remaining = prerequisiteIds.filter(id => id !== prereqId);
                          onCellSave(remaining.map(String).join(', '));
                        }}
                        sx={{ ...(validationError && { borderColor: 'error.main', color: 'error.main' }) }}
                      />
                    );
                  })}
                  <Chip
                    size="small"
                    icon={<Add />}
                    label="Add"
                    onClick={onCellEdit}
                    variant="outlined"
                    sx={{ flexShrink: 0 }}
                  />
                </Box>
              ) : (
                <>
              <Typography 
                variant="body2" 
                sx={{ 
                  cursor: 'text', 
                  minHeight: '20px',
                  ...(validationError && { color: '#d32f2f' }),
                }}
                title={validationError || undefined}
              >
                {displayValue || '(none)'}
              </Typography>
              {validationError && (
                <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: '0.7rem' }}>
                  {validationError}
                </Typography>
              )}
              {isOver && !isEditing && (
                <Typography variant="caption" sx={{ color: '#1976d2', fontSize: '0.7rem', fontStyle: 'italic' }}>
                  Drop to add prerequisite
                </Typography>
              )}
                </>
              )}
            </>
          ) : (
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#999',
                fontStyle: 'italic',
                minHeight: '20px',
              }}
            >
              N/A
            </Typography>
          )}
        </Box>
      )}
    </TableCell>
  );
};

// Droppable source document cell component (for REVISE, APPROVE, and REVIEW actions)
interface DroppableSourceDocumentCellProps {
  rowId: string;
  rowType: RowType;
  isEditing: boolean;
  editingValue: string;
  displayValue: string;
  validationError: string | null;
  onCellEdit: () => void;
  onCellSave: (value: string) => void;
  setEditingCell: React.Dispatch<React.SetStateAction<{ rowId: string; field: string; value: string | User[] } | null>>;
  getValidationError: (rowId: string, field: string) => string | null;
  projectPlan: ProjectPlan;
  action: PlanningAction;
}

const DroppableSourceDocumentCell = ({
  rowId,
  rowType,
  isEditing,
  editingValue,
  displayValue,
  validationError,
  onCellEdit,
  onCellSave,
  setEditingCell,
  getValidationError,
  projectPlan,
  action,
}: DroppableSourceDocumentCellProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `source-${rowId}`,
  });

  // Track the current selected value to ensure we save the correct value on blur
  const currentSelectedValueRef = useRef<string>(editingValue || '');

  // Show for REVISE, APPROVE, and REVIEW actions
  const isReviseAction = rowType === 'ACTION' && (action.actionType === 'REVISE' || action.actionType === 'APPROVE' || action.actionType === 'REVIEW');
  
  // Get all available source actions (actions that can be source documents)
  const availableSourceActions = useMemo(() => {
    if (!isReviseAction) return [];
    
    // Find the current action's task and phase
    let currentTask: PlanningTask | null = null;
    let currentPhase: PlanningPhase | null = null;
    
    for (const phase of projectPlan.phases) {
      for (const task of phase.tasks) {
        if (task.actions.some(a => a.actionId === action.actionId)) {
          currentTask = task;
          currentPhase = phase;
          break;
        }
      }
      if (currentTask) break;
    }
    
    if (!currentTask || !currentPhase) {
      return []; // Current action not found in project plan
    }
    
    const actions: Array<{ id: number; label: string; action: PlanningAction; task: PlanningTask; phase: PlanningPhase }> = [];
    
    // Get all prerequisite task IDs for the current task
    const prerequisiteTaskIds = new Set(currentTask.prerequisites || []);
    
    // Get all prerequisite phase IDs for the current phase
    const prerequisitePhaseIds = new Set((currentPhase as any).prerequisites || []);
    
    // Get all phases that contain prerequisite tasks (for grandchild relationship)
    const prerequisitePhases = projectPlan.phases.filter(phase =>
      phase.tasks.some(task => prerequisiteTaskIds.has(task.taskId))
    );
    const prerequisitePhaseIdsFromTasks = new Set(prerequisitePhases.map(p => p.phaseId));
    
    projectPlan.phases.forEach(phase => {
      phase.tasks.forEach(task => {
        task.actions.forEach(sourceAction => {
          // Rule 1: Cannot be itself
          if (sourceAction.actionId === action.actionId) {
            return; // Skip the current action
          }
          
          // Rule 2: Same task - only include if source has lower displayOrder (earlier)
          if (task.taskId === currentTask!.taskId) {
            const sourceOrder = sourceAction.displayOrder || 0;
            const currentOrder = action.displayOrder || 0;
            if (sourceOrder < currentOrder) {
              // Valid: source comes before current action in same task
              const taskName = task.name;
              const phaseOrder = phase.displayOrder;
              const taskOrder = task.displayOrder;
              const actionOrder = sourceAction.displayOrder;
              actions.push({
                id: sourceAction.actionId,
                label: `${phaseOrder}.${taskOrder}.${actionOrder} - ${sourceAction.actionType} (${taskName})`,
                action: sourceAction,
                task,
                phase,
              });
            }
            return; // Don't check other rules for same task
          }
          
          // Rule 3: Source task is a prerequisite of current task
          if (prerequisiteTaskIds.has(task.taskId)) {
            const taskName = task.name;
            const phaseOrder = phase.displayOrder;
            const taskOrder = task.displayOrder;
            const actionOrder = sourceAction.displayOrder;
            actions.push({
              id: sourceAction.actionId,
              label: `${phaseOrder}.${taskOrder}.${actionOrder} - ${sourceAction.actionType} (${taskName})`,
              action: sourceAction,
              task,
              phase,
            });
            return; // Don't check other rules if already a prerequisite task
          }
          
          // Rule 4: Source phase is a prerequisite of current phase
          if (prerequisitePhaseIds.has(phase.phaseId)) {
            const taskName = task.name;
            const phaseOrder = phase.displayOrder;
            const taskOrder = task.displayOrder;
            const actionOrder = sourceAction.displayOrder;
            actions.push({
              id: sourceAction.actionId,
              label: `${phaseOrder}.${taskOrder}.${actionOrder} - ${sourceAction.actionType} (${taskName})`,
              action: sourceAction,
              task,
              phase,
            });
            return; // Don't check other rules if already a prerequisite phase
          }
          
          // Rule 5: Source phase comes before current phase (earlier phase)
          const sourcePhaseOrder = phase.displayOrder || 0;
          const currentPhaseOrder = currentPhase!.displayOrder || 0;
          if (sourcePhaseOrder < currentPhaseOrder) {
            // Valid: source is in an earlier phase, guaranteed to complete before current phase
            const taskName = task.name;
            const phaseOrder = phase.displayOrder;
            const taskOrder = task.displayOrder;
            const actionOrder = sourceAction.displayOrder;
            actions.push({
              id: sourceAction.actionId,
              label: `${phaseOrder}.${taskOrder}.${actionOrder} - ${sourceAction.actionType} (${taskName})`,
              action: sourceAction,
              task,
              phase,
            });
            return; // Don't check other rules if already in earlier phase
          }
          
          // Rule 6: Same phase, earlier task (task with lower displayOrder in same phase)
          if (phase.phaseId === currentPhase!.phaseId) {
            const sourceTaskOrder = task.displayOrder || 0;
            const currentTaskOrder = currentTask!.displayOrder || 0;
            if (sourceTaskOrder < currentTaskOrder) {
              // Valid: source is in an earlier task in the same phase
              const taskName = task.name;
              const phaseOrder = phase.displayOrder;
              const taskOrder = task.displayOrder;
              const actionOrder = sourceAction.displayOrder;
              actions.push({
                id: sourceAction.actionId,
                label: `${phaseOrder}.${taskOrder}.${actionOrder} - ${sourceAction.actionType} (${taskName})`,
                action: sourceAction,
                task,
                phase,
              });
            }
            return; // Don't check other rules for same phase
          }
          
          // Rule 7: Source task is in a phase that contains prerequisite tasks (grandchild)
          if (prerequisitePhaseIdsFromTasks.has(phase.phaseId)) {
            const taskName = task.name;
            const phaseOrder = phase.displayOrder;
            const taskOrder = task.displayOrder;
            const actionOrder = sourceAction.displayOrder;
            actions.push({
              id: sourceAction.actionId,
              label: `${phaseOrder}.${taskOrder}.${actionOrder} - ${sourceAction.actionType} (${taskName})`,
              action: sourceAction,
              task,
              phase,
            });
          }
        });
      });
    });
    
    let sorted = actions.sort((a, b) => comparePlanIdDesc(a.label, b.label));
    if (action.actionType === 'APPROVE') {
      sorted = sorted.filter(
        (item) => !sourceUsedByAnotherApprove(projectPlan, action.actionId, item.id)
      );
    }
    return sorted;
  }, [projectPlan, isReviseAction, action]);
  
  // Get selected source action for Autocomplete
  const selectedSourceAction = useMemo(() => {
    if (!isEditing || !editingValue) return null;
    const actionId = parseInt(editingValue, 10);
    if (isNaN(actionId)) return null;
    return availableSourceActions.find(a => a.id === actionId) || null;
  }, [isEditing, editingValue, availableSourceActions]);

  // Update ref when editingValue changes
  useEffect(() => {
    if (isEditing) {
      currentSelectedValueRef.current = editingValue || '';
    }
  }, [isEditing, editingValue]);
  
  return (
    <TableCell
      ref={isReviseAction ? setNodeRef : undefined}
      sx={{
        border: '1px solid #e0e0e0',
        padding: '4px 8px',
        backgroundColor: isReviseAction ? 'transparent' : '#f5f5f5',
        '&:hover': { 
          backgroundColor: isReviseAction ? '#f5f5f5' : '#f5f5f5',
        },
        ...(validationError && {
          border: '2px solid #d32f2f',
        }),
        ...(isOver && isReviseAction && !isEditing && {
          backgroundColor: '#e3f2fd',
          border: '2px dashed #1976d2',
        }),
        ...(!isReviseAction && {
          cursor: 'not-allowed',
          opacity: 0.6,
        }),
        // Ensure droppable area is always active when not editing
        ...(isReviseAction && !isEditing && {
          pointerEvents: 'auto',
        }),
      }}
      onClick={() => {
        if (isReviseAction && !isEditing) {
          onCellEdit();
        }
      }}
      title={
        isReviseAction 
          ? (validationError || (isOver && !isEditing ? 'Drop action here to set source document' : 'Click to edit source document'))
          : 'Source document is only available for REVISE, APPROVE, and REVIEW actions'
      }
    >
      {isReviseAction ? (
        isEditing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Autocomplete
              size="small"
              options={availableSourceActions}
              getOptionLabel={(option) => option.label}
              value={selectedSourceAction}
              onChange={(_, newValue) => {
                const value = newValue ? newValue.id.toString() : '';
                // Update ref immediately to track current selection
                currentSelectedValueRef.current = value;
                setEditingCell(prev => {
                  if (prev && prev.rowId === rowId && prev.field === 'sourceDocument') {
                    return { ...prev, value };
                  }
                  return { rowId, field: 'sourceDocument', value };
                });
              }}
              onBlur={() => {
                // Use ref to get the most current value, ensuring we save what was just selected
                const currentValue = currentSelectedValueRef.current;
                onCellSave(currentValue);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Use ref to get the most current value
                  const currentValue = currentSelectedValueRef.current;
                  onCellSave(currentValue);
                } else if (e.key === 'Escape') {
                  setEditingCell(null);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  placeholder="Select source action or leave empty to clear"
                  error={!!getValidationError(rowId, 'sourceDocument')}
                  helperText={getValidationError(rowId, 'sourceDocument') || ''}
                />
              )}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              onClick={() => {
                // Update ref to track cleared value
                currentSelectedValueRef.current = '';
                setEditingCell(prev => {
                  if (prev && prev.rowId === rowId && prev.field === 'sourceDocument') {
                    return { ...prev, value: '' };
                  }
                  return { rowId, field: 'sourceDocument', value: '' };
                });
                onCellSave('');
              }}
              title="Clear source document"
              sx={{ p: 0.5 }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                cursor: 'text', 
                minHeight: '20px',
                ...(validationError && { color: '#d32f2f' }),
              }}
              title={validationError || undefined}
            >
              {displayValue || '(none)'}
            </Typography>
            {validationError && (
              <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: '0.7rem' }}>
                {validationError}
              </Typography>
            )}
            {isOver && (
              <Typography variant="caption" sx={{ color: '#1976d2', fontSize: '0.7rem', fontStyle: 'italic' }}>
                Drop to set source document
              </Typography>
            )}
          </Box>
        )
      ) : (
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#999',
            fontStyle: 'italic',
            minHeight: '20px',
          }}
        >
          N/A
        </Typography>
      )}
    </TableCell>
  );
};

// Sortable row component
interface SortableRowProps {
  row: GridRow;
  projectPlan: ProjectPlan;
  editingCell: { rowId: string; field: string; value: string | User[] } | null;
  validationErrors: Map<string, string>;
  onCellEdit: (rowId: string, field: string) => void;
  onCellSave: (rowId: string, field: string, newValue: string | User[]) => void;
  getCellValue: (row: GridRow, field: string) => string;
  getValidationError: (rowId: string, field: string) => string | null;
  getRowValidationError: (row: GridRow) => string | null;
  isDateCalculated: (row: GridRow) => boolean;
  isDurationCalculated: (row: GridRow) => boolean;
  setEditingCell: React.Dispatch<React.SetStateAction<{ rowId: string; field: string; value: string | User[] } | null>>;
  onToggleExpand: (rowId: string) => void;
  expandedRows: Set<string> | null;
  currentUsersRef: React.MutableRefObject<User[]>;
  userJustSelectedRef: React.MutableRefObject<boolean>;
  saveInProgressRef: React.MutableRefObject<{ rowId: string; timestamp: number } | null>;
  visibleColumns: Set<PlanningColumnKey>;
  projectLocked: boolean;
  projectUserIds: Set<number>;
  projectUsers: ProjectUser[];
  isRecalculating: boolean;
  setSelectedActionForDocuments: React.Dispatch<React.SetStateAction<{
    actionId: number;
    actionType: ActionType;
    requiredDocumentCount?: number | null;
  } | null>>;
  setDocumentAssociationDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setRejectActionMenuAnchor: React.Dispatch<React.SetStateAction<{ el: HTMLElement; rowId: string } | null>>;
  handleInsertRow: (rowType: RowType, afterRow?: GridRow) => Promise<void>;
  handleDeleteRow: (row: GridRow) => Promise<void>;
}

const SortableRow = ({
  row,
  projectPlan,
  editingCell,
  validationErrors: _validationErrors,
  onCellEdit,
  onCellSave,
  getCellValue,
  getValidationError,
  getRowValidationError,
  isDateCalculated,
  isDurationCalculated,
  setEditingCell,
  onToggleExpand,
  expandedRows,
  visibleColumns,
  currentUsersRef,
  userJustSelectedRef,
  saveInProgressRef,
  projectLocked,
  projectUserIds,
  projectUsers,
  isRecalculating,
  setSelectedActionForDocuments,
  setDocumentAssociationDialogOpen,
  setRejectActionMenuAnchor,
  handleInsertRow,
  handleDeleteRow,
}: SortableRowProps) => {
  // PROJECT rows should not be draggable
  // Also disable drag when project is locked or recalculating
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id, disabled: row.type === 'PROJECT' || projectLocked || isRecalculating });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isEditing = editingCell?.rowId === row.id;
  const indent = row.level >= 0 ? row.level * 30 : 0;
  const rowColor = row.type === 'PROJECT' ? '#bbdefb' : row.type === 'PHASE' ? '#e3f2fd' : row.type === 'TASK' ? '#f3e5f5' : '#fff';
  const rowError = getRowValidationError(row);
  const hasRowError = rowError !== null;
  // null = all expanded, Set = check if row is in the set
  const isExpanded = expandedRows === null || expandedRows.has(row.id);
  const canExpand = row.type === 'PHASE' || row.type === 'TASK';

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      key={row.id}
      data-planning-row-id={row.id}
      sx={{
        backgroundColor: rowColor,
        '&:hover': { backgroundColor: rowColor, opacity: 0.9 },
        ...(hasRowError && {
          borderLeft: '4px solid #d32f2f',
        }),
      }}
    >
      {visibleColumns.has('number') && (
        <TableCell
          sx={{
            border: '1px solid #e0e0e0',
            padding: '4px 8px',
            backgroundColor: '#fafafa',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', pl: `${indent}px` }}>
            {canExpand && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand(row.id);
                }}
                sx={{ p: 0.5, mr: 0.5 }}
              >
                {isExpanded ? (
                  <ExpandMore sx={{ fontSize: 18 }} />
                ) : (
                  <ChevronRight sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            )}
            {!canExpand && <Box sx={{ width: 24 }} />}
            {row.type !== 'PROJECT' && (
              <Box
                {...(projectLocked ? {} : attributes)}
                {...(projectLocked ? {} : listeners)}
                sx={{ 
                  cursor: projectLocked ? 'not-allowed' : (isDragging ? 'grabbing' : 'grab'), 
                  mr: 1, 
                  display: 'flex', 
                  alignItems: 'center',
                  opacity: projectLocked ? 0.5 : 1,
                }}
              >
                <DragIndicator sx={{ color: 'text.secondary' }} />
              </Box>
            )}
            {row.type === 'PROJECT' && <Box sx={{ width: 24 }} />}
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
              {row.type === 'PROJECT' && 'PROJECT'}
              {row.type === 'PHASE' && (row.data as PlanningPhase).displayOrder}
              {row.type === 'TASK' && (() => {
                const phase = projectPlan.phases.find(p => p.phaseId === row.phaseId);
                const task = row.data as PlanningTask;
                return phase ? `${phase.displayOrder}.${task.displayOrder}` : task.displayOrder;
              })()}
              {row.type === 'ACTION' && (() => {
                const phase = projectPlan.phases.find(p => p.phaseId === row.phaseId);
                const task = phase?.tasks.find(t => t.taskId === row.taskId);
                const action = row.data as PlanningAction;
                return phase && task ? `${phase.displayOrder}.${task.displayOrder}.${action.displayOrder}` : action.displayOrder;
              })()}
            </Typography>
          </Box>
        </TableCell>
      )}
      {visibleColumns.has('name') && (
        <TableCell
          sx={{
            border: '1px solid #e0e0e0',
            padding: '4px 8px',
            '&:hover': { backgroundColor: '#f5f5f5' },
            ...(hasRowError && {
              borderLeft: '4px solid #d32f2f',
            }),
          }}
          onClick={() => {
            if (row.type !== 'ACTION' && row.type !== 'PROJECT') {
              onCellEdit(row.id, 'name');
            }
          }}
          title={rowError || undefined}
        >
          <Box sx={{ pl: `${indent}px` }}>
            {row.type === 'ACTION' ? (
            isEditing && editingCell?.field === 'actionType' ? (
              <Select
                size="small"
                value={editingCell.value}
                onChange={(e) => {
                  onCellSave(row.id, 'actionType', e.target.value);
                }}
                onBlur={() => setEditingCell(null)}
                autoFocus
                sx={{ width: '100%' }}
              >
                <MenuItem value="CREATE">CREATE</MenuItem>
                <MenuItem value="REVIEW">REVIEW</MenuItem>
                <MenuItem value="REVISE">REVISE</MenuItem>
                <MenuItem value="APPROVE">
                  APPROVE
                </MenuItem>
              </Select>
            ) : (
              <Typography
                variant="body2"
                onClick={() => onCellEdit(row.id, 'actionType')}
                sx={{ cursor: 'text', minHeight: '20px' }}
              >
                {(row.data as PlanningAction).name ?? (row.data as PlanningAction).actionType}
              </Typography>
            )
          ) : (
            isEditing && editingCell?.field === 'name' ? (
              <DescriptionInput
                key={`name-${row.id}`}
                initialValue={editingCell.value != null ? String(editingCell.value) : ''}
                onSave={(value) => onCellSave(row.id, 'name', value)}
                onCancel={() => setEditingCell(null)}
              />
            ) : (
              <Typography
                variant="body2"
                sx={{ cursor: 'text', minHeight: '20px' }}
              >
                {row.type === 'PHASE' && (row.data as PlanningPhase).name}
                {row.type === 'TASK' && (row.data as PlanningTask).name}
              </Typography>
            )
          )}
          </Box>
        </TableCell>
      )}
      {visibleColumns.has('description') && (
        <TableCell
          sx={{
            border: '1px solid #e0e0e0',
            padding: '4px 8px',
            '&:hover': { backgroundColor: '#f5f5f5' },
          }}
          onClick={() => {
            if (row.type !== 'PROJECT') {
              onCellEdit(row.id, 'description');
            }
          }}
        >
          {isEditing && editingCell?.field === 'description' ? (
            <DescriptionInput
              key={`description-${row.id}`}
              initialValue={editingCell.value != null ? String(editingCell.value) : ''}
              onSave={(value) => onCellSave(row.id, 'description', value)}
              onCancel={() => setEditingCell(null)}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{ cursor: 'text', minHeight: '20px' }}
            >
              {getCellValue(row, 'description')}
            </Typography>
          )}
        </TableCell>
      )}
      {visibleColumns.has('assigned') && (
        <TableCell
          sx={{
            border: '1px solid #e0e0e0',
            padding: '4px 8px',
            '&:hover': { backgroundColor: '#f5f5f5' },
            ...(getValidationError(row.id, row.type === 'TASK' ? 'assignedUsers' : 'assignedTo') && {
              border: '2px solid #d32f2f',
            }),
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (row.type === 'ACTION') {
              onCellEdit(row.id, 'assignedTo');
            }
          }}
          title={getValidationError(row.id, row.type === 'TASK' ? 'assignedUsers' : 'assignedTo') || undefined}
        >
        {isEditing && editingCell?.field === 'assignedTo' && row.type === 'ACTION' ? (
            <ProjectUserSelector
              key={`project-user-selector-${row.id}`} // Force remount when row changes
              multiple={(row.data as PlanningAction).actionType !== 'APPROVE'}
              value={editingCell.value ? (typeof editingCell.value === 'string' ? [] : editingCell.value as User[]) : []}
              allowedUserIds={projectUserIds}
              projectUsers={projectUsers
                .filter(pu => pu.userId && (pu.user?.email || pu.userEmail)) // Only include users with valid data
                .map(pu => {
                  const email = pu.user?.email || pu.userEmail || '';
                  const displayName = pu.user?.displayName || pu.userName || email;
                  const u = pu.user as Partial<User> | undefined;
                  return {
                    userId: pu.userId,
                    email,
                    displayName,
                    roles: u?.roles ?? [],
                    role: pu.user?.role ?? null,
                    isActive: pu.user?.isActive ?? true,
                    emailConfirmed: u?.emailConfirmed ?? false,
                    lockoutEnabled: u?.lockoutEnabled ?? false,
                    lockoutEnd: u?.lockoutEnd ?? null,
                    accessFailedCount: u?.accessFailedCount ?? 0,
                    createdAt: u?.createdAt ?? new Date().toISOString(),
                    company: pu.user?.company ?? pu.company ?? null,
                    userType: pu.user?.userType ?? pu.userType ?? null,
                  } as User;
                })}
              onChange={(users: User[]) => {
              // Only update if we have users, or if we're explicitly clearing (users is empty array)
              // Don't update if users is null or undefined
              if (!users || !Array.isArray(users)) {
                console.warn('UserSelector onChange - invalid users value:', users);
                return;
              }
              const actionRow = row.data as PlanningAction;
              const usersNorm =
                actionRow.actionType === 'APPROVE' && users.length > 1
                  ? [users[0]]
                  : users;

              // CRITICAL: Update ref FIRST (synchronously) - this is the source of truth for saving
              // Create a deep copy to avoid reference issues
              currentUsersRef.current = usersNorm.map(u => ({ ...u }));
              
              // Update state using a function to ensure we're updating the latest editingCell state
              // This is for UI display only - we'll save from the ref
              setEditingCell((prev: { rowId: string; field: string; value: string | User[] } | null) => {
                if (!prev || prev.rowId !== row.id) {
                  console.warn('UserSelector onChange - prev is null or rowId mismatch:', { prev, rowId: row.id });
                  return prev;
                }
                
                return { ...prev, value: usersNorm.map(u => ({ ...u })) }; // Create a deep copy
              });
              
              // If we have users, save immediately after a short delay
              // This is the PRIMARY save mechanism - onBlur is just a backup
              if (usersNorm.length > 0) {
                userJustSelectedRef.current = true;
                
                // Clear any existing timeouts to prevent duplicate saves
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if ((window as any).__userSelectorTimeout) {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  clearTimeout((window as any).__userSelectorTimeout);
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if ((window as any).__userSelectorSaveTimeout) {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  clearTimeout((window as any).__userSelectorSaveTimeout);
                }
                
                // Save after a short delay to allow the Autocomplete to finish updating
                // Capture users directly in the closure to avoid ref issues
                const usersToSave = usersNorm.map(u => ({ ...u })); // Capture users immediately
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).__userSelectorSaveTimeout = setTimeout(async () => {
                  // Check if save is already in progress
                  const now = Date.now();
                  const lastSave = saveInProgressRef.current;
                  if (lastSave && lastSave.rowId === row.id && (now - lastSave.timestamp) < 1000) {
                    return;
                  }
                  
                  // Verify we're still editing this row
                  setEditingCell((currentEditingCell: { rowId: string; field: string; value: string | User[] } | null) => {
                    if (currentEditingCell && currentEditingCell.rowId === row.id && usersToSave.length > 0) {
                      // Mark save as in progress
                      saveInProgressRef.current = { rowId: row.id, timestamp: now };
                      
                      // Perform the save using the captured users value
                      (async () => {
                        try {
                          await onCellSave(row.id, row.type === 'TASK' ? 'assignedUsers' : 'assignedTo', usersToSave);
                          // Clear save in progress after a delay
                          setTimeout(() => {
                            if (saveInProgressRef.current?.rowId === row.id) {
                              saveInProgressRef.current = null;
                            }
                          }, 1000);
                        } catch (error) {
                          console.error('Error in auto-save:', error);
                          saveInProgressRef.current = null;
                        }
                      })();
                    }
                    return currentEditingCell; // Return unchanged state
                  });
                }, 400); // 400ms delay to allow Autocomplete to finish updating
                
                // Reset the flag after a delay
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as any).__userSelectorTimeout = setTimeout(() => {
                  userJustSelectedRef.current = false;
                }, 2000);
              } else {
                // If users array is empty, clear the flag immediately
                userJustSelectedRef.current = false;
              }
            }}
            onBlur={() => {
              // Only process blur if we're still editing this row
              // This prevents processing blur events from other rows or after editing has ended
              if (!editingCell || editingCell.rowId !== row.id) {
                return;
              }
              
              // Capture everything immediately - don't wait for setTimeout
              const userJustSelectedAtBlur = userJustSelectedRef.current;
              // Capture the ref value immediately - create a deep copy to avoid reference issues
              const usersAtBlur = currentUsersRef.current.length > 0 
                ? currentUsersRef.current.map(u => ({ ...u })) 
                : [];
              // Capture editingCell.value immediately - this is the most reliable source
              const editingCellValueAtBlur = editingCell?.value && Array.isArray(editingCell.value) && editingCell.value.length > 0
                ? (editingCell.value as User[]).map(u => ({ ...u }))
                : [];
              
              // Use setTimeout to check if focus is moving within the autocomplete
              // This prevents saving when clicking on dropdown options
              setTimeout(async () => {
                // Double-check we're still editing this row (might have changed during delay)
                const currentEditingCell = editingCell;
                if (!currentEditingCell || currentEditingCell.rowId !== row.id) {
                  return;
                }
                
                const activeElement = document.activeElement;
                
                // Determine if we should save
                // Save if: we have users to save AND (user was just selected OR focus has left the autocomplete)
                const focusStillInAutocomplete = activeElement && (
                  activeElement.closest('.MuiAutocomplete-root') ||
                  activeElement.closest('.MuiPopper-root') ||
                  activeElement.closest('[role="listbox"]') ||
                  activeElement.closest('.MuiAutocomplete-popper')
                );
                
                // Use editingCellValueAtBlur as primary source since it's captured immediately when onBlur fires
                // Fall back to ref values if editingCellValueAtBlur is not available
                let users: User[] = [];
                if (editingCellValueAtBlur.length > 0) {
                  users = editingCellValueAtBlur;
                } else if (usersAtBlur.length > 0) {
                  users = usersAtBlur;
                } else if (currentUsersRef.current.length > 0) {
                  users = currentUsersRef.current.map(u => ({ ...u }));
                } else if (currentEditingCell?.value && Array.isArray(currentEditingCell.value) && currentEditingCell.value.length > 0) {
                  users = (currentEditingCell.value as User[]).map(u => ({ ...u }));
                } else {
                  console.warn('UserSelector onBlur - no users found in any source');
                }
                
                // If we have users, always save (regardless of focus state)
                // This ensures users are saved even if focus detection fails
                const shouldSave = (users.length > 0) || (userJustSelectedAtBlur || !focusStillInAutocomplete);
                
                if (!shouldSave) {
                  return;
                }
                
                if (users && Array.isArray(users) && users.length > 0) {
                  try {
                    // Clear the fallback timeout since blur is firing and will handle the save
                    if ((window as any).__userSelectorSaveTimeout) {
                      clearTimeout((window as any).__userSelectorSaveTimeout);
                    }
                    // Mark save as in progress to prevent fallback from also saving
                    saveInProgressRef.current = { rowId: row.id, timestamp: Date.now() };
                    await onCellSave(row.id, (editingCell?.field === 'assignedUsers' ? 'assignedUsers' : 'assignedTo'), users);
                    // Clear save in progress after a delay
                    setTimeout(() => {
                      if (saveInProgressRef.current?.rowId === row.id) {
                        saveInProgressRef.current = null;
                      }
                    }, 1000);
                    // Note: setEditingCell(null) is called inside onCellSave for user assignments
                    // Don't clear the ref here - let onCellSave handle cleanup
                  } catch (error) {
                    console.error('Error saving user assignments:', error);
                    saveInProgressRef.current = null;
                    // Keep editing cell open on error so user can retry
                  }
                } else {
                  setEditingCell(null);
                  currentUsersRef.current = [];
                }
              }, 300); // Delay to allow focus to settle
            }}
                error={!!getValidationError(row.id, 'assignedTo')}
                helperText={getValidationError(row.id, 'assignedTo') || ''}
            placeholder="Search project users..."
            autoFocus
          />
        ) : (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  cursor: row.type === 'ACTION' ? 'pointer' : 'default', 
                  minHeight: '20px',
                  ...(getValidationError(row.id, row.type === 'TASK' ? 'assignedUsers' : 'assignedTo') && { color: '#d32f2f' }),
                }}
                title={getValidationError(row.id, row.type === 'TASK' ? 'assignedUsers' : 'assignedTo') || undefined}
              >
                {getCellValue(row, 'assignedUsers') || getCellValue(row, 'assignedTo')}
              </Typography>
              {/* Execution behavior indicator for actions */}
              {row.type === 'ACTION' && (() => {
                const action = row.data as PlanningAction;
                const assignedCount = action.assignedTo?.length || 0;
                if (assignedCount > 1) {
                  if (action.actionType === 'CREATE' || action.actionType === 'REVISE') {
                    return (
                      <Tooltip title="First user to complete wins. Other users will see completion details.">
                        <Chip 
                          icon={<FirstPage sx={{ fontSize: 14 }} />}
                          label="First Complete"
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 18 }}
                        />
                      </Tooltip>
                    );
                  } else if (action.actionType === 'REVIEW') {
                    return (
                      <Tooltip title="Each user gets their own action. All run in parallel.">
                        <Chip 
                          icon={<People sx={{ fontSize: 14 }} />}
                          label="Parallel"
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 18 }}
                        />
                      </Tooltip>
                    );
                  }
                }
                return null;
              })()}
            </Box>
            {getValidationError(row.id, row.type === 'TASK' ? 'assignedUsers' : 'assignedTo') && (
              <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: '0.7rem' }}>
                {getValidationError(row.id, row.type === 'TASK' ? 'assignedUsers' : 'assignedTo')}
              </Typography>
            )}
          </Box>
        )}
        </TableCell>
      )}
      {visibleColumns.has('startDate') && (
        <TableCell
          sx={{
            border: '1px solid #e0e0e0',
            padding: '4px 8px',
            backgroundColor: '#fafafa',
            cursor: 'default',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                cursor: 'default', 
                minHeight: '20px',
                ...(isDateCalculated(row) && {
                  fontStyle: 'italic',
                  color: '#666',
                }),
              }}
              title={isDateCalculated(row) ? 'Calculated date (based on actions and prerequisites)' : 'Date is calculated from actions and durations'}
            >
              {getCellValue(row, 'startDate')}
            </Typography>
            {isDateCalculated(row) && (
              <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }} title="Calculated">
                ⚡
              </Typography>
            )}
          </Box>
        </TableCell>
      )}
      {visibleColumns.has('endDate') && (
        <TableCell
          sx={{
            border: '1px solid #e0e0e0',
            padding: '4px 8px',
            backgroundColor: '#fafafa',
            cursor: 'default',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                cursor: 'default', 
                minHeight: '20px',
                ...(isDateCalculated(row) && {
                  fontStyle: 'italic',
                  color: '#666',
                }),
              }}
              title={isDateCalculated(row) ? 'Calculated date (based on actions and prerequisites)' : 'Date is calculated from actions and durations'}
            >
              {getCellValue(row, 'endDate')}
            </Typography>
            {isDateCalculated(row) && (
              <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }} title="Calculated">
                ⚡
              </Typography>
            )}
          </Box>
        </TableCell>
      )}
      {visibleColumns.has('duration') && (
        <TableCell
          sx={{
            border: '1px solid #e0e0e0',
            padding: '4px 8px',
            backgroundColor: '#fafafa',
            '&:hover': { backgroundColor: '#f5f5f5' },
            ...(getValidationError(row.id, 'duration') && {
              border: '2px solid #d32f2f',
            }),
          }}
          onClick={() => {
            if (!isDurationCalculated(row) && row.type !== 'PROJECT') {
              onCellEdit(row.id, 'duration');
            }
          }}
          title={getValidationError(row.id, 'duration') || undefined}
        >
        <EnhancedDurationInput
          value={isEditing && editingCell?.field === 'duration' ? (typeof editingCell.value === 'string' ? editingCell.value : '') : getCellValue(row, 'durationRaw')}
          isEditing={isEditing && editingCell?.field === 'duration'}
          validationError={getValidationError(row.id, 'duration')}
          isDurationCalculated={isDurationCalculated(row)}
          rowType={row.type}
          onCellEdit={() => onCellEdit(row.id, 'duration')}
          onCellSave={(value) => onCellSave(row.id, 'duration', value)}
          setEditingCell={setEditingCell}
        />
      </TableCell>
      )}
      {visibleColumns.has('prerequisites') && (
        <DroppablePrerequisitesCell
          rowId={row.id}
          rowType={row.type}
          isEditing={isEditing && editingCell?.field === 'prerequisites'}
          editingValue={editingCell?.field === 'prerequisites' ? (typeof editingCell.value === 'string' ? editingCell.value : '') : ''}
          displayValue={getCellValue(row, 'prerequisites')}
          validationError={getValidationError(row.id, 'prerequisites')}
          prerequisiteIds={row.type === 'PHASE' ? (row.data as PlanningPhase & { prerequisites?: number[] }).prerequisites : row.type === 'TASK' ? (row.data as PlanningTask).prerequisites : undefined}
          onCellEdit={() => onCellEdit(row.id, 'prerequisites')}
          onCellSave={(value) => onCellSave(row.id, 'prerequisites', value)}
          setEditingCell={setEditingCell}
          getValidationError={getValidationError}
          projectPlan={projectPlan}
          currentTaskId={row.type === 'TASK' ? (row.data as PlanningTask).taskId : undefined}
          currentPhaseId={row.type === 'PHASE' ? (row.data as PlanningPhase).phaseId : undefined}
        />
      )}
      {visibleColumns.has('sourceDocument') && (
        <DroppableSourceDocumentCell
          rowId={row.id}
          rowType={row.type}
          isEditing={isEditing && editingCell?.field === 'sourceDocument'}
          editingValue={editingCell?.field === 'sourceDocument' ? (typeof editingCell.value === 'string' ? editingCell.value : '') : ''}
          displayValue={getCellValue(row, 'sourceDocument')}
          validationError={getValidationError(row.id, 'sourceDocument')}
          onCellEdit={() => onCellEdit(row.id, 'sourceDocument')}
          onCellSave={(value) => onCellSave(row.id, 'sourceDocument', value)}
          setEditingCell={setEditingCell}
          getValidationError={getValidationError}
          projectPlan={projectPlan}
          action={row.type === 'ACTION' ? (row.data as PlanningAction) : {} as PlanningAction}
        />
      )}
      {visibleColumns.has('rejectAction') && (
        <TableCell
          sx={{
            border: '1px solid #e0e0e0',
            padding: '4px 8px',
            '&:hover': { backgroundColor: (row.type === 'ACTION' && (row.data as PlanningAction).actionType === 'APPROVE' && !projectLocked) ? '#f5f5f5' : undefined },
          }}
        >
          {row.type === 'ACTION' && (row.data as PlanningAction).actionType === 'APPROVE' ? (
            <Box
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                if (!projectLocked) setRejectActionMenuAnchor({ el: e.currentTarget as HTMLElement, rowId: row.id });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!projectLocked) setRejectActionMenuAnchor({ el: e.currentTarget as HTMLElement, rowId: row.id });
                }
              }}
              sx={{
                cursor: projectLocked ? 'default' : 'pointer',
                minHeight: 28,
                display: 'flex',
                alignItems: 'center',
                outline: 'none',
              }}
            >
              <Typography variant="body2" sx={{ minHeight: '20px' }}>
                {getCellValue(row, 'rejectAction') || '—'}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              —
            </Typography>
          )}
        </TableCell>
      )}
      {visibleColumns.has('requiredDocumentCount') && (
        <TableCell
          sx={{
            border: '1px solid #e0e0e0',
            padding: '4px 8px',
            '&:hover': { backgroundColor: '#f5f5f5' },
          }}
          onClick={(e) => {
            // Only trigger edit if clicking on the Typography, not if already editing
            if (row.type === 'ACTION' && (row.data as PlanningAction).actionType === 'CREATE' && 
                !isEditing && editingCell?.field !== 'requiredDocumentCount') {
              e.stopPropagation();
              onCellEdit(row.id, 'requiredDocumentCount');
            }
          }}
        >
          {row.type === 'ACTION' && (row.data as PlanningAction).actionType === 'CREATE' ? (
            <Box 
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              onClick={(e) => e.stopPropagation()}
            >
              {isEditing && editingCell?.field === 'requiredDocumentCount' && editingCell?.rowId === row.id ? (
                <RequiredDocumentCountInput
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  initialValue={editingCell.value != null ? editingCell.value : ((row.data as PlanningAction) as any).requiredDocumentCount || null}
                  onSave={(value) => {
                    onCellSave(row.id, 'requiredDocumentCount', value != null ? String(value) : '');
                  }}
                  onCancel={() => setEditingCell(null)}
                  disabled={projectLocked}
                />
              ) : (
                <>
                  <Typography
                    variant="body2"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCellEdit(row.id, 'requiredDocumentCount');
                    }}
                    sx={{ cursor: 'text', minHeight: '20px', flex: 1 }}
                  >
                    {getCellValue(row, 'requiredDocumentCount') || '0'}
                  </Typography>
                  <Tooltip title="Manage Document Associations">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        const action = row.data as PlanningAction;
                        setSelectedActionForDocuments({
                          actionId: action.actionId,
                          actionType: action.actionType,
                          requiredDocumentCount: (action as any).requiredDocumentCount || null,
                        });
                        setDocumentAssociationDialogOpen(true);
                      }}
                    >
                      <Description fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              -
            </Typography>
          )}
        </TableCell>
      )}
      {visibleColumns.has('actions') && (
        <TableCell
          sx={{
            border: '1px solid #e0e0e0',
            padding: '4px 8px',
            backgroundColor: '#fafafa',
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {/* Add button - color coded by row type */}
            {row.type === 'PHASE' && (
              <>
                <Tooltip title="Insert Phase After">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInsertRow('PHASE', row);
                    }}
                    disabled={projectLocked}
                    sx={{
                      color: '#1976d2', // Dark blue for visibility on light blue background
                      backgroundColor: '#e3f2fd', // PHASE color
                      border: '1px solid #90caf9',
                      '&:hover': { 
                        backgroundColor: '#bbdefb', 
                        borderColor: '#64b5f6',
                      },
                      '&:disabled': { backgroundColor: '#f5f5f5', color: '#bdbdbd', borderColor: '#e0e0e0' },
                      padding: '4px',
                      minWidth: '28px',
                      minHeight: '28px',
                    }}
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Insert Task">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInsertRow('TASK', row);
                    }}
                    disabled={projectLocked}
                    sx={{
                      color: '#7b1fa2', // Dark purple for visibility on light purple background
                      backgroundColor: '#f3e5f5', // TASK color
                      border: '1px solid #ce93d8',
                      '&:hover': { 
                        backgroundColor: '#e1bee7', 
                        borderColor: '#ba68c8',
                      },
                      '&:disabled': { backgroundColor: '#f5f5f5', color: '#bdbdbd', borderColor: '#e0e0e0' },
                      padding: '4px',
                      minWidth: '28px',
                      minHeight: '28px',
                    }}
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {row.type === 'TASK' && (
              <Tooltip title="Insert Action">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleInsertRow('ACTION', row);
                  }}
                  disabled={projectLocked}
                  sx={{
                    color: '#424242', // Dark gray for visibility on white background
                    backgroundColor: '#fff', // ACTION color (white background)
                    border: '1px solid #e0e0e0',
                    '&:hover': { 
                      backgroundColor: '#f5f5f5', 
                      borderColor: '#bdbdbd',
                    },
                    '&:disabled': { backgroundColor: '#f5f5f5', color: '#bdbdbd', borderColor: '#e0e0e0' },
                    padding: '4px',
                    minWidth: '28px',
                    minHeight: '28px',
                  }}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {/* Delete button - color coded by row type */}
            {row.type !== 'PROJECT' && (
              <Tooltip title={`Delete ${row.type}`}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteRow(row);
                  }}
                  disabled={projectLocked}
                  sx={{
                    color: '#d32f2f', // Red for delete action
                    backgroundColor: rowColor,
                    border: `1px solid ${rowColor}`,
                    '&:hover': { 
                      backgroundColor: rowColor, 
                      opacity: 0.8,
                      borderColor: '#d32f2f',
                    },
                    '&:disabled': { backgroundColor: '#f5f5f5', color: '#bdbdbd', borderColor: '#e0e0e0' },
                    padding: '4px',
                    minWidth: '28px',
                    minHeight: '28px',
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      )}
    </TableRow>
  );
};

// Memoize SortableRow to prevent unnecessary re-renders during drag operations
// This significantly improves drag-and-drop performance
const MemoizedSortableRow = memo(SortableRow, (prevProps, nextProps) => {
  // Custom comparison function for better performance
  // Return true if props are equal (skip re-render), false if different (re-render)
  if (prevProps.row.id !== nextProps.row.id) return false;
  if (prevProps.row.type !== nextProps.row.type) return false;
  if (prevProps.editingCell?.rowId !== nextProps.editingCell?.rowId) return false;
  if (prevProps.editingCell?.field !== nextProps.editingCell?.field) return false;
  if (prevProps.validationErrors.size !== nextProps.validationErrors.size) return false;
  // Check if validation errors for this specific row changed
  const prevRowError = prevProps.validationErrors.get(prevProps.row.id);
  const nextRowError = nextProps.validationErrors.get(nextProps.row.id);
  if (prevRowError !== nextRowError) return false;
  if (prevProps.expandedRows !== nextProps.expandedRows) {
    // Check if expansion state for this row changed
    const prevExpanded = prevProps.expandedRows === null || prevProps.expandedRows.has(prevProps.row.id);
    const nextExpanded = nextProps.expandedRows === null || nextProps.expandedRows.has(nextProps.row.id);
    if (prevExpanded !== nextExpanded) return false;
  }
  if (prevProps.visibleColumns !== nextProps.visibleColumns) return false;
  if (prevProps.projectLocked !== nextProps.projectLocked) return false;
  if (prevProps.isRecalculating !== nextProps.isRecalculating) return false;
  // Check projectUsers array reference - if it changed, we need to re-render
  if (prevProps.projectUsers !== nextProps.projectUsers) return false;
  // Only check projectPlan reference - deep comparison would be too expensive
  if (prevProps.projectPlan !== nextProps.projectPlan) return false;
  // Props are equal, skip re-render
  return true;
});

// Column definitions for Planning View
type PlanningColumnKey = 
  | 'number' 
  | 'name' 
  | 'description' 
  | 'assigned' 
  | 'startDate' 
  | 'endDate' 
  | 'duration' 
  | 'prerequisites' 
  | 'sourceDocument'
  | 'rejectAction'
  | 'requiredDocumentCount'
  | 'actions';

interface PlanningColumnConfig {
  key: PlanningColumnKey;
  label: string;
  group?: string;
  priority: 'high' | 'medium' | 'low';
}

const PLANNING_COLUMNS: PlanningColumnConfig[] = [
  { key: 'number', label: '#', priority: 'high' },
  { key: 'name', label: 'Name/Type', priority: 'high' },
  { key: 'description', label: 'Description', priority: 'medium' },
  { key: 'assigned', label: 'Assigned', priority: 'high' },
  { key: 'startDate', label: 'Start Date', group: 'dates', priority: 'high' },
  { key: 'endDate', label: 'End Date', group: 'dates', priority: 'high' },
  { key: 'duration', label: 'Duration', priority: 'high' },
  { key: 'prerequisites', label: 'Prerequisites', priority: 'medium' },
  { key: 'sourceDocument', label: 'Source Document', priority: 'medium' },
  { key: 'rejectAction', label: 'Reject Action', priority: 'medium' },
  { key: 'requiredDocumentCount', label: 'Req. Docs', priority: 'medium' },
  { key: 'actions', label: '', priority: 'high' },
];

const PLANNING_QUICK_VIEW_PRESETS: Record<string, Set<PlanningColumnKey>> = {
  overview: new Set(['number', 'name', 'assigned', 'startDate', 'endDate', 'duration', 'actions']),
  details: new Set(['number', 'name', 'description', 'assigned', 'startDate', 'endDate', 'duration', 'prerequisites', 'sourceDocument', 'rejectAction', 'requiredDocumentCount', 'actions']),
  dates: new Set(['number', 'name', 'startDate', 'endDate', 'duration', 'actions']),
};

export const ProjectPlanningPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  const urlProjectId = searchParams.get('projectId'); // Get projectId from query string
  const { user } = useAuth();
  const { isModalOpen } = useModalOpen();
  const isSystemAdmin = user ? hasFullAdminAccess(user) : false;
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  /** Phase 7: progress payload during backend recalc (message/counts); null when not recalculating or no progress yet. */
  const [recalcProgress, setRecalcProgress] = useState<ProgressPayload | null>(null);
  const [_savingPrerequisites, setSavingPrerequisites] = useState<string | null>(null); // rowId of cell being saved
  const [apiError, setApiError] = useState<string | null>(null);
  
  // Project status and lock state
  const [projectStatus, setProjectStatus] = useState<string>('PLANNING');
  const [projectLocked, setProjectLocked] = useState(false);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [excelReplaceWizardOpen, setExcelReplaceWizardOpen] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  
  // Document association dialog state
  const [rejectActionMenuAnchor, setRejectActionMenuAnchor] = useState<{ el: HTMLElement; rowId: string } | null>(null);
  const [showApprovalRollbackOverlay, setShowApprovalRollbackOverlay] = useState(false);
  const planningTableScrollRef = useRef<HTMLDivElement>(null);
  const [documentAssociationDialogOpen, setDocumentAssociationDialogOpen] = useState(false);
  const [selectedActionForDocuments, setSelectedActionForDocuments] = useState<{
    actionId: number;
    actionType: ActionType;
    requiredDocumentCount?: number | null;
  } | null>(null);
  
  // Store document associations for each action (for saving titles/descriptions)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [actionDocumentAssociations, setActionDocumentAssociations] = useState<Map<number, any[]>>(new Map());
  
  // Cache document associations for source document display (key: actionId, value: DocumentAssociation[])
  const [sourceDocumentAssociationsCache, setSourceDocumentAssociationsCache] = useState<Map<number, DocumentAssociation[]>>(new Map());
  
  // Cache documents from CREATE actions to look up source document titles (key: actionId, value: DocumentAssociation[])
  const [createActionDocumentsCache, setCreateActionDocumentsCache] = useState<Map<number, DocumentAssociation[]>>(new Map());
  
  // Document selection dialog for drag-and-drop (CREATE actions with multiple documents)
  const [documentSelectionDialogOpen, setDocumentSelectionDialogOpen] = useState(false);
  const [pendingDragOperation, setPendingDragOperation] = useState<{
    sourceAction: PlanningAction;
    targetAction: PlanningAction;
    targetRowId: string;
  } | null>(null);
  const [availableSourceDocuments, setAvailableSourceDocuments] = useState<DocumentAssociation[]>([]);
  
  const [assignmentValidationError, setAssignmentValidationError] = useState<{
    open: boolean;
    actions: Array<{ phaseName: string; taskName: string; actionName: string; actionId: number }>;
  }>({ open: false, actions: [] });

  const hasPlanningModalOpen = lockDialogOpen || documentAssociationDialogOpen || documentSelectionDialogOpen || assignmentValidationError.open || excelReplaceWizardOpen;
  useModalOpenRegistration(hasPlanningModalOpen);
  
  // Editing cell state - must be declared before useEffect that uses it
  const [editingCell, setEditingCell] = useState<{ rowId: string; field: string; value: string | User[] } | null>(null);
  
  // Project users for filtering assignments
  const [projectUsers, setProjectUsers] = useState<ProjectUser[]>([]);
  const [projectUserIds, setProjectUserIds] = useState<Set<number>>(new Set());
  
  // Holidays for date calculations
  const [_holidays, setHolidays] = useState<Holiday[]>([]);
  const [holidayDates, setHolidayDates] = useState<Set<string>>(new Set()); // Set of YYYY-MM-DD dates
  
  // Temporary ID counter for new items (negative numbers)
  const tempIdCounterRef = useRef(-1);
  // @ts-ignore - intentionally unused
  const _generateTempId = () => tempIdCounterRef.current--;
  
  
  // Column visibility state - load from localStorage or use default
  const [visibleColumns, setVisibleColumns] = useState<Set<PlanningColumnKey>>(() => {
    const saved = localStorage.getItem('planningViewColumns');
    if (saved) {
      try {
        const savedCols = new Set<PlanningColumnKey>(JSON.parse(saved));
        // Ensure new columns are added to existing saved preferences
        // Add requiredDocumentCount if it's missing (for users who saved preferences before this feature)
        if (!savedCols.has('requiredDocumentCount') && !isMobile) {
          savedCols.add('requiredDocumentCount');
        }
        if (!savedCols.has('rejectAction') && !isMobile) {
          savedCols.add('rejectAction');
        }
        return savedCols;
      } catch {
        // Fall through to default
      }
    }
    // Default: show high priority columns, hide low priority on mobile
    const defaultCols = new Set<PlanningColumnKey>(['number', 'name', 'assigned', 'startDate', 'endDate', 'duration', 'actions']);
    if (!isMobile) {
      defaultCols.add('description');
      defaultCols.add('prerequisites');
      defaultCols.add('sourceDocument'); // Show source document column by default on desktop
      defaultCols.add('rejectAction');
      defaultCols.add('requiredDocumentCount'); // Show required document count column by default on desktop
    }
    return defaultCols;
  });

  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

  // Ensure requiredDocumentCount column is visible (add it if missing from saved preferences)
  useEffect(() => {
    if (!isMobile && !visibleColumns.has('requiredDocumentCount')) {
      setVisibleColumns(prev => {
        const updated = new Set(prev);
        updated.add('requiredDocumentCount');
        return updated;
      });
    }
  }, [isMobile, visibleColumns]);

  // Save column preferences to localStorage
  useEffect(() => {
    localStorage.setItem('planningViewColumns', JSON.stringify(Array.from(visibleColumns)));
  }, [visibleColumns]);

  // Adjust visible columns based on screen size
  useEffect(() => {
    if (isMobile) {
      // On mobile, hide less critical columns
      setVisibleColumns(prev => {
        const next = new Set(prev);
        if (!next.has('description')) {
          // Keep description if it was already visible
        }
        return next;
      });
    }
  }, [isMobile]);
  // Helper function to convert dates in project plan
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parseDatesInPlan = (plan: any): ProjectPlan => {
    const parsed = { ...plan };
    // Keep projectStartDate as ISO string (YYYY-MM-DD) for API compatibility
    if (typeof plan.projectStartDate === 'string') {
      // Extract date part (YYYY-MM-DD) and use it directly - no Date object conversion
      // This avoids timezone shifts that occur when converting through Date objects
      const dateStr = plan.projectStartDate.split('T')[0];
      // Validate it's in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        parsed.projectStartDate = dateStr; // Use directly - no conversion needed
      } else {
        // If format is invalid, try to parse and reformat
        const dateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dateMatch) {
          parsed.projectStartDate = dateMatch[0]; // Use the matched YYYY-MM-DD part
        } else {
          parsed.projectStartDate = getNextBusinessDayDateString();
        }
      }
    } else if (plan.projectStartDate instanceof Date) {
      // If it's a Date object, convert to YYYY-MM-DD string in local time
      parsed.projectStartDate = dateToLocalDateString(plan.projectStartDate);
    } else {
      parsed.projectStartDate = getNextBusinessDayDateString();
    }
    
    // Convert phase/task dates to Date objects for UI display (dates are stored as strings in types)
    // Use local time parsing to avoid timezone issues
    const parseDateString = (dateStr: string | null): Date | null => {
      if (!dateStr) return null;
      const dateOnly = dateStr.split('T')[0];
      const [year, month, day] = dateOnly.split('-').map(Number);
      return new Date(year, month - 1, day, 0, 0, 0, 0);
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parsed.phases = (plan.phases || []).map((phase: any) => {
      const parsedStartDate = parseDateString(phase.startDate);
      const parsedEndDate = parseDateString(phase.endDate);

      return {
        ...phase,
        startDate: parsedStartDate,
        endDate: parsedEndDate,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tasks: (phase.tasks || []).map((task: any) => ({
          ...task,
          startDate: parseDateString(task.startDate),
          endDate: parseDateString(task.endDate),
        })),
      };
    });
    return parsed;
  };

  // Helper function to deep clone project plan
  // @ts-expect-error - intentionally unused
  const _cloneProjectPlan = (plan: ProjectPlan): ProjectPlan => {
    const cloned = JSON.parse(JSON.stringify(plan));
    // Ensure dates are properly parsed after cloning
    return parseDatesInPlan(cloned);
  };

  /**
   * Map GET /plan response to ProjectPlan shape (with startDate/endDate strings).
   * Call parseDatesInPlan(result) and optionally calculateDurationsOnly() after.
   */
  const mapGetPlanResponseToProjectPlan = useCallback((response: ProjectPlanResponse): ProjectPlan => {
    const toPrerequisiteIds = (prereqs: Array<{ phaseId?: number; taskId?: number }>): number[] => {
      if (!Array.isArray(prereqs)) return [];
      return prereqs
        .map((p) => p.phaseId ?? p.taskId)
        .filter((id): id is number => id != null && !isNaN(id));
    };

    const phases: PlanningPhase[] = response.phases.map((phase) => ({
      phaseId: phase.phaseId,
      name: phase.name,
      description: phase.description ?? '',
      startDate: phase.contractedStartDate ?? phase.plannedStartDate,
      endDate: phase.contractedEndDate ?? phase.plannedEndDate,
      duration: phase.plannedDurationHours ?? 0,
      tasks: phase.tasks.map((task) => ({
        taskId: task.taskId,
        name: task.name,
        description: task.description ?? '',
        assignedUsers: task.assignedUsers ?? [],
        startDate: task.contractedStartDate ?? task.plannedStartDate,
        endDate: task.contractedEndDate ?? task.plannedEndDate,
        duration: task.plannedDurationHours ?? 0,
        prerequisites: toPrerequisiteIds(task.prerequisites),
        parallelExecution: false,
        actions: task.actions.map((action) => {
          const a = action as { name?: string | null; description?: string | null };
          const actionExt = action as {
            rejectActionId?: number | null;
            sourceSpItemId?: number | null;
            requiredDocumentCount?: number | null;
          };
          return {
          actionId: action.actionId,
          actionType: action.actionType as ActionType,
          name: a.name ?? null,
          description: a.description ?? null,
          duration: action.durationHours ?? 0,
          assignedTo: (action.assignedTo ?? []).map((a) => (typeof a === 'string' ? a : a?.email ?? '')).filter((e): e is string => e.length > 0),
          notes: action.notes ?? '',
          displayOrder: action.displayOrder,
          plannedStartDate: action.contractedStartDate ?? action.plannedStartDate ?? undefined,
          plannedEndDate: action.contractedEndDate ?? action.plannedEndDate ?? undefined,
          sourceActionId: action.sourceActionId ?? undefined,
          rejectActionId: actionExt.rejectActionId ?? null,
          sourceSpItemId: actionExt.sourceSpItemId ?? null,
          requiredDocumentCount: actionExt.requiredDocumentCount ?? null,
        };
        }),
        displayOrder: task.displayOrder,
      })),
      displayOrder: phase.displayOrder,
      prerequisites: toPrerequisiteIds(phase.prerequisites ?? []),
    }));

    const rawPlan = {
      projectId: response.project.projectId,
      projectName: response.project.name,
      projectStartDate: response.project.projectStartDate,
      phases,
    };
    return parseDatesInPlan(rawPlan);
  }, [parseDatesInPlan]);

  /** After PATCH updates ProjectStartDate, merge engine-computed dates from GET /plan into local plan (keep unsaved structure). */
  const mergeScheduleDatesFromServer = useCallback((local: ProjectPlan, server: ProjectPlan): ProjectPlan => {
    const serverPhases = new Map(server.phases.map((p) => [p.phaseId, p]));
    return {
      ...local,
      projectStartDate: server.projectStartDate,
      phases: local.phases.map((phase) => {
        const sp = serverPhases.get(phase.phaseId);
        if (!sp) {
          return phase;
        }
        const serverTasks = new Map(sp.tasks.map((t) => [t.taskId, t]));
        return {
          ...phase,
          startDate: sp.startDate,
          endDate: sp.endDate,
          duration: sp.duration,
          tasks: phase.tasks.map((task) => {
            const st = serverTasks.get(task.taskId);
            if (!st) {
              return task;
            }
            const serverActions = new Map(st.actions.map((a) => [a.actionId, a]));
            return {
              ...task,
              startDate: st.startDate,
              endDate: st.endDate,
              duration: st.duration,
              actions: task.actions.map((action) => {
                const sa = serverActions.get(action.actionId);
                if (!sa) {
                  return action;
                }
                return {
                  ...action,
                  plannedStartDate: sa.plannedStartDate,
                  plannedEndDate: sa.plannedEndDate,
                  duration: sa.duration,
                };
              }),
            };
          }),
        };
      }),
    };
  }, []);

  // Load project from API or localStorage
  const loadProjectPlan = async (projectId?: number): Promise<ProjectPlan> => {
    if (projectId) {
      try {
        setIsLoading(true);
        setApiError(null);
        
        // Load project metadata
        const project = await getProject(projectId);
        
        // Update project status and locked state
        setProjectStatus(project.status || 'PLANNING');
        setProjectLocked(project.status === 'EXECUTION' || project.status === 'COMPLETE');
        
        // Load phases with nested tasks and actions
        const phasesData = await getPhases(projectId);

        // Convert API phases to PlanningPhase format
        const phases: PlanningPhase[] = await Promise.all(
          phasesData.map(async (phase) => {
            // Load tasks for this phase
            const tasksData = await getTasks(phase.phaseId);

            // Convert tasks to PlanningTask format
            const tasks: PlanningTask[] = await Promise.all(
              tasksData.map(async (task) => {
                // Load actions for this task
                const actionsData = await getActions(task.taskId);

                // Load user assignments for this task
                let assignedUsers: string[] = [];
                try {
                  const assignments = await getTaskAssignments(task.taskId);
                  assignedUsers = assignments.map(a => a.user.email);
                } catch (err) {
                  console.warn(`loadProjectPlan: Failed to load task assignments for task ${task.taskId}:`, err);
                }
                
                // Convert actions to PlanningAction format
                const actions: PlanningAction[] = await Promise.all(actionsData.map(async (action) => {
                  // Load user assignments for this action
                  let assignedUsers: string[] = [];
                  try {
                    const actionAssignments = await getActionAssignments(action.actionId);
                    assignedUsers = actionAssignments.map(a => a.user.email);
                  } catch (err) {
                    console.warn('Failed to load action assignments:', err);
                  }
                  
                  const loadedAction = {
                    actionId: action.actionId,
                    actionType: action.actionType,
                    name: (action as { name?: string | null }).name ?? null,
                    description: (action as { description?: string | null }).description ?? null,
                    // Backend returns durationHours, check that first, then fall back to other fields
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    duration: (action as any).durationHours ?? action.plannedDurationHours ?? action.duration ?? 0,
                    assignedTo: assignedUsers,
                    notes: action.notes || '',
                    displayOrder: action.displayOrder,
                    plannedStartDate: action.plannedStartDate,
                    plannedEndDate: action.plannedEndDate,
                    // Source document fields (for REVISE, APPROVE, and REVIEW actions)
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    sourceActionId: (action as any).sourceActionId ?? null,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    rejectActionId: (action as any).rejectActionId ?? null,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    sourceSpItemId: (action as any).sourceSpItemId ?? null,
                    // Multi-document support
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    requiredDocumentCount: (action as any).requiredDocumentCount ?? null,
                  };

                  return loadedAction;
                }));
                
                // Load document associations for REVISE/REVIEW/APPROVE actions to display source document titles
                const associationsToCache = new Map<number, DocumentAssociation[]>();
                const createDocumentsToCache = new Map<number, DocumentAssociation[]>();
                
                await Promise.all(actions.map(async (action) => {
                  // Load CREATE action documents for source document lookup
                  if (action.actionType === 'CREATE' && action.actionId) {
                    try {
                      const documents = await apiService.getActionDocuments(action.actionId);
                      createDocumentsToCache.set(action.actionId, documents);
                    } catch (err) {
                      console.warn(`Failed to load CREATE action documents for action ${action.actionId}:`, err);
                    }
                  }
                  
                  // Load document associations for REVISE/REVIEW/APPROVE actions
                  if ((action.actionType === 'REVISE' || action.actionType === 'APPROVE' || action.actionType === 'REVIEW') && action.actionId) {
                    try {
                      const associations = await apiService.getActionDocuments(action.actionId);
                      associationsToCache.set(action.actionId, associations);
                    } catch (err) {
                      console.warn(`Failed to load document associations for action ${action.actionId}:`, err);
                    }
                  }
                }));
                
                // Update cache with all associations at once
                if (associationsToCache.size > 0) {
                  setSourceDocumentAssociationsCache(prev => {
                    const newMap = new Map(prev);
                    associationsToCache.forEach((associations, actionId) => {
                      newMap.set(actionId, associations);
                    });
                    return newMap;
                  });
                }
                
                // Update cache with CREATE action documents
                if (createDocumentsToCache.size > 0) {
                  setCreateActionDocumentsCache(prev => {
                    const newMap = new Map(prev);
                    createDocumentsToCache.forEach((documents, actionId) => {
                      newMap.set(actionId, documents);
                    });
                    return newMap;
                  });
                }
                
            return {
              taskId: task.taskId,
              name: task.name,
              description: task.description || '',
              assignedUsers,
              startDate: task.plannedStartDate,
              endDate: task.plannedEndDate,
              duration: task.plannedDurationHours || 0,
              // Ensure prerequisites are always an array of numbers
              prerequisites: Array.isArray(task.prerequisites) 
                ? task.prerequisites.map(id => typeof id === 'number' ? id : parseInt(String(id), 10)).filter(id => !isNaN(id))
                : [],
              parallelExecution: task.parallelExecution,
              actions: actions.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)), // Sort actions by displayOrder
              displayOrder: task.displayOrder,
            };
              })
            );
            
            // Sort tasks by displayOrder to ensure correct order regardless of backend ordering
            tasks.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            
            const phaseResult = {
              phaseId: phase.phaseId,
              name: phase.name,
              description: phase.description || '',
              startDate: phase.plannedStartDate,
              endDate: phase.plannedEndDate,
              duration: phase.plannedDurationHours || 0,
              tasks,
              displayOrder: phase.displayOrder,
              prerequisites: Array.isArray((phase as unknown as { prerequisites?: number[] }).prerequisites)
                ? (phase as unknown as { prerequisites: number[] }).prerequisites
                : [],
            };

            return phaseResult;
          })
        );
        
        // Sort phases by displayOrder to ensure correct order regardless of backend ordering
        phases.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
        
        // Ensure projectStartDate is in YYYY-MM-DD format
        // CRITICAL: Extract the date part BEFORE any Date object conversion to avoid timezone shifts
        let projectStartDateStr: string;
        if (typeof project.projectStartDate === 'string') {
          // Extract just the date part (YYYY-MM-DD) to avoid timezone issues
          // The API might return dates like "2025-12-09T00:00:00Z" or "2025-12-09T00:00:00.000Z"
          // We want ONLY the date part: "2025-12-09"
          const dateMatch = project.projectStartDate.match(/^(\d{4}-\d{2}-\d{2})/);
          if (dateMatch) {
            projectStartDateStr = dateMatch[1]; // Use the matched YYYY-MM-DD part
          } else {
            // Fallback: try splitting on 'T' if regex doesn't match
            projectStartDateStr = project.projectStartDate.split('T')[0];
            console.warn('loadProjectPlan: Could not extract date with regex, using split result:', {
              raw: project.projectStartDate,
              extracted: projectStartDateStr,
            });
          }
          // Validate format
          if (!/^\d{4}-\d{2}-\d{2}$/.test(projectStartDateStr)) {
            console.error('loadProjectPlan: Invalid date format after extraction:', {
              raw: project.projectStartDate,
              extracted: projectStartDateStr,
            });
            // Fallback to next business day if invalid (do not use "today")
            projectStartDateStr = getNextBusinessDayDateString();
          }
        } else {
          projectStartDateStr = getNextBusinessDayDateString();
        }

        const plan: ProjectPlan = {
          projectId: project.projectId,
          projectName: project.name,
          projectStartDate: projectStartDateStr, // YYYY-MM-DD format string (no time, no timezone)
          phases,
        };

        const parsedPlan = parseDatesInPlan(plan);
        const planWithDurations = calculateDurationsOnly(parsedPlan);
        return planWithDurations;
      } catch (error) {
        const apiError = error as ProjectApiError | PlanningExecutionApiError;
        setApiError(apiError.message || 'Failed to load project');
        console.error('Error loading project from API:', error);
        // Don't fall back to seed data - show error instead
        // Return empty plan structure so UI can show error message
        throw error; // Re-throw so caller can handle it
      } finally {
        setIsLoading(false);
      }
    }
    
    // No projectId provided - return empty plan
    // This should not happen in normal flow, but handle gracefully
    return {
      projectId: -1,
      projectName: 'New Project',
      projectStartDate: getDefaultProjectStartDateString(),
      phases: [],
    };
  };

  // NEW: Load project summary (fast initial load using new endpoint)
  const loadProjectSummary = async (projectId: number): Promise<ProjectPlan> => {
    try {
      setApiError(null);
      
      // Load project metadata and summary in parallel
      const [project, summary] = await Promise.all([
        getProject(projectId),
        getPlanningSummary(projectId),
      ]);
      
      // Update project status and locked state
      setProjectStatus(project.status || 'PLANNING');
      setProjectLocked(project.status === 'EXECUTION' || project.status === 'COMPLETE');
      
      // Convert summary to ProjectPlan format with empty tasks/actions (will be loaded on demand)
      const projectStartDateStr = typeof project.projectStartDate === 'string'
        ? project.projectStartDate.split('T')[0]
        : getNextBusinessDayDateString();
      
      const phases: PlanningPhase[] = summary.phases.map(phaseSummary => ({
        phaseId: phaseSummary.phaseId,
        name: phaseSummary.name,
        description: phaseSummary.description || '', // May be included in summary
        startDate: phaseSummary.plannedStartDate,
        endDate: phaseSummary.plannedEndDate,
        duration: phaseSummary.plannedDurationHours || 0, // May be included in summary
        tasks: [], // Will be loaded in parallel after summary loads
        displayOrder: phaseSummary.displayOrder,
      }));
      
      const plan: ProjectPlan = {
        projectId: summary.projectId,
        projectName: summary.projectName,
        projectStartDate: projectStartDateStr,
        phases,
      };
      
      return parseDatesInPlan(plan);
    } catch (error) {
      const apiError = error as ProjectApiError;
      setApiError(apiError.message || 'Failed to load project summary');
      console.error('Error loading project summary:', error);
      throw error;
    }
    // Note: Loading state is now managed by the caller (useEffect) to ensure it's cleared
    // only after all phase details are loaded, preventing the browser tab spinner from hanging
  };

  // NEW: Load phase details on demand (progressive loading)
  // Use refs to track loading state to avoid stale closures
  const loadingPhasesRef = useRef<Set<number>>(new Set());
  const phaseDetailsCacheRef = useRef<Map<number, PhaseDetailsResponse>>(new Map());

  // Track which phases have dates that should be preserved (from backend calculation or full reload)
  // Key: phaseId, Value: { startDate, endDate } - the dates that should be preserved
  const preservedPhaseDatesRef = useRef<Map<number, { startDate: string | Date | null; endDate: string | Date | null }>>(new Map());
  
  const loadPhaseDetails = useCallback(async (projectId: number, phaseId: number): Promise<void> => {
    const hasPreservedDatesForPhase = preservedPhaseDatesRef.current.has(phaseId);
    if (hasPreservedDatesForPhase) {
      return;
    }
    if (phaseDetailsCacheRef.current.has(phaseId) || loadingPhasesRef.current.has(phaseId)) {
      return;
    }

    try {
      // Update both state and ref
      loadingPhasesRef.current.add(phaseId);
      setLoadingPhases(prev => new Set(prev).add(phaseId));
      
      const phaseDetails = await getPhaseDetails(projectId, phaseId);

      // Cache the phase details in both state and ref
      phaseDetailsCacheRef.current.set(phaseId, phaseDetails);
      setPhaseDetailsCache(prev => new Map(prev).set(phaseId, phaseDetails));
      
      // Load user assignments for tasks and actions
      const tasks: PlanningTask[] = await Promise.all(
        phaseDetails.tasks.map(async (taskData) => {
          let assignedUsers: string[] = [];
          try {
            const assignments = await getTaskAssignments(taskData.taskId);
            assignedUsers = assignments.map(a => a.user.email);
          } catch (err) {
            console.warn(`Failed to load task assignments for task ${taskData.taskId}:`, err);
          }
          
          const actions: PlanningAction[] = await Promise.all(
            taskData.actions.map(async (actionData) => {
              let assignedUsers: string[] = [];
              try {
                const actionAssignments = await getActionAssignments(actionData.actionId);
                assignedUsers = actionAssignments.map(a => a.user.email);
              } catch (err) {
                console.warn('Failed to load action assignments:', err);
              }

              // Load CREATE action documents for source document lookup
              if (actionData.actionType === 'CREATE' && actionData.actionId) {
                try {
                  const documents = await apiService.getActionDocuments(actionData.actionId);
                  // Cache CREATE action documents for source document title lookup
                  setCreateActionDocumentsCache(prev => {
                    const newMap = new Map(prev);
                    newMap.set(actionData.actionId, documents);
                    return newMap;
                  });
                } catch (err) {
                  console.warn(`Failed to load CREATE action documents for action ${actionData.actionId}:`, err);
                }
              }
              
              // Load document associations for REVISE/REVIEW/APPROVE actions to display source document titles
              if ((actionData.actionType === 'REVISE' || actionData.actionType === 'APPROVE' || actionData.actionType === 'REVIEW') && actionData.actionId) {
                try {
                  const associations = await apiService.getActionDocuments(actionData.actionId);
                  // Cache associations for source document display
                  setSourceDocumentAssociationsCache(prev => {
                    const newMap = new Map(prev);
                    newMap.set(actionData.actionId, associations);
                    return newMap;
                  });
                } catch (err) {
                  console.warn(`Failed to load document associations for action ${actionData.actionId}:`, err);
                }
              }
              
              const action: PlanningAction = {
                actionId: actionData.actionId,
                actionType: actionData.actionType as ActionType,
                name: actionData.name ?? null,
                description: actionData.description ?? null,
                duration: actionData.durationHours || 0,
                assignedTo: assignedUsers,
                notes: actionData.notes || '',
                displayOrder: actionData.displayOrder,
                plannedStartDate: actionData.plannedStartDate ?? null,
                plannedEndDate: actionData.plannedEndDate ?? null,
                sourceActionId: actionData.sourceActionId ?? null, // Ensure null if undefined
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                rejectActionId: (actionData as any).rejectActionId ?? null,
                sourceSpItemId: null,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                requiredDocumentCount: (actionData as any).requiredDocumentCount ?? null,
              };

              return action;
            })
          );
          
          return {
            taskId: taskData.taskId,
            name: taskData.name,
            description: taskData.description || '',
            assignedUsers,
            startDate: taskData.plannedStartDate,
            endDate: taskData.plannedEndDate,
            duration: taskData.durationHours || 0,
            prerequisites: taskData.prerequisites || [],
            parallelExecution: taskData.parallelExecution,
            actions: actions.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)), // Sort actions by displayOrder
            displayOrder: taskData.displayOrder,
          };
        })
      );
      
      // Sort tasks by displayOrder to ensure correct order regardless of backend ordering
      // This is a defensive measure - backend should persist displayOrder, but we sort here as fallback
      tasks.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
      
          // Update the phase with loaded tasks
      setProjectPlan(prevPlan => {
        const updatedPlan = { ...prevPlan };
        const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === phaseId);
        
        if (phaseIndex >= 0) {
          const currentPhase = updatedPlan.phases[phaseIndex];

          // Determine if we should update dates
          // Rules:
          // 1. If skipProgressiveLoading is true, preserve existing dates (backend recalculation just happened)
          // 2. If phase has preserved dates, only update if dates match (no change) or phase has no dates yet
          // 3. Otherwise, allow update (normal progressive loading scenario)
          const preservedDates = preservedPhaseDatesRef.current.get(phaseId);
          const currentEndDateStr = toDateOnly(currentPhase.endDate) ?? currentPhase.endDate;
          
          // Check if we're overwriting fresh data from a recent reload
          const isOverwritingFreshData = currentPhase && currentPhase.endDate && 
            phaseDetails.plannedEndDate && 
            currentEndDateStr !== phaseDetails.plannedEndDate;
          
          if (isOverwritingFreshData) {
            console.warn(`loadPhaseDetails: WARNING - Overwriting phase ${phaseId} dates!`, {
              currentEndDate: currentEndDateStr,
              newEndDate: phaseDetails.plannedEndDate,
              currentStartDate: toDateOnly(currentPhase.startDate) ?? currentPhase.startDate,
              newStartDate: phaseDetails.plannedStartDate,
            });
          }
          const preservedEndDateStr = toDateOnly(preservedDates?.endDate) ?? preservedDates?.endDate ?? null;
          const preservedStartDateStr = toDateOnly(preservedDates?.startDate) ?? preservedDates?.startDate ?? null;
          const apiEndDateStr = phaseDetails.plannedEndDate;
          
          // Check if we have preserved dates (from backend calculation)
          const hasPreservedDates = preservedDates && (preservedEndDateStr || preservedStartDateStr);
          
          // Determine if we should update dates
          // Priority: if we have preserved dates (from backend calculation), use them; otherwise normal progressive loading.
          let shouldUpdateDates = false;
          
          if (hasPreservedDates) {
            shouldUpdateDates = false;
          } else {
            // Normal progressive loading - update if phase has no dates or dates match
            shouldUpdateDates = !currentPhase.endDate || currentEndDateStr === apiEndDateStr;
          }
          
          // Determine which dates to use
          let finalStartDate: string | Date | null;
          let finalEndDate: string | Date | null;

          if (shouldUpdateDates) {
            finalStartDate = phaseDetails.plannedStartDate;
            finalEndDate = phaseDetails.plannedEndDate;
          } else if (preservedDates && (preservedDates.startDate || preservedDates.endDate)) {
            finalStartDate = preservedDates.startDate;
            finalEndDate = preservedDates.endDate;
          } else if (currentPhase.startDate || currentPhase.endDate) {
            finalStartDate = currentPhase.startDate;
            finalEndDate = currentPhase.endDate;
          } else {
            // Last resort: use API dates (shouldn't happen, but be safe)
            finalStartDate = phaseDetails.plannedStartDate;
            finalEndDate = phaseDetails.plannedEndDate;
            console.warn(`loadPhaseDetails: WARNING - No preserved or current dates for phase ${phaseId}, using API dates as fallback`);
          }
          
          const phaseDetailsWithPrereqs = phaseDetails as unknown as { prerequisites?: number[] };
          const phasePrereqs = Array.isArray(phaseDetailsWithPrereqs.prerequisites)
            ? phaseDetailsWithPrereqs.prerequisites
            : [];
          updatedPlan.phases[phaseIndex] = {
            phaseId: phaseDetails.phaseId,
            name: phaseDetails.name,
            description: phaseDetails.description || '',
            // Use the determined dates (preserved, API, or current), normalized to string | null
            startDate: toDateOnly(finalStartDate) ?? (typeof finalStartDate === 'string' ? finalStartDate : null),
            endDate: toDateOnly(finalEndDate) ?? (typeof finalEndDate === 'string' ? finalEndDate : null),
            duration: phaseDetails.plannedDurationHours || 0,
            tasks,
            displayOrder: phaseDetails.displayOrder,
            prerequisites: phasePrereqs,
          } as PlanningPhase;
        }

        return updatedPlan;
      });
    } catch (error) {
      console.error(`Error loading phase details for phase ${phaseId}:`, error);
      setApiError(`Failed to load phase details: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      // Update both state and ref
      loadingPhasesRef.current.delete(phaseId);
      setLoadingPhases(prev => {
        const next = new Set(prev);
        next.delete(phaseId);
        return next;
      });
    }
  }, []);

  // Initialize with empty plan, will load via useEffect
  const [projectPlan, setProjectPlanRaw] = useState<ProjectPlan>(() => {
    return {
      projectId: -1,
      projectName: '',
      projectStartDate: getDefaultProjectStartDateString(),
      phases: [],
    };
  });
  
  const setProjectPlan = useCallback((updater: ProjectPlan | ((prev: ProjectPlan) => ProjectPlan)) => {
    setProjectPlanRaw((prevPlan) => {
      return typeof updater === 'function' ? updater(prevPlan) : updater;
    });
  }, []);

  // Progressive loading state - cache for loaded phase details
  const [phaseDetailsCache, setPhaseDetailsCache] = useState<Map<number, PhaseDetailsResponse>>(new Map());
  const [loadingPhases, setLoadingPhases] = useState<Set<number>>(new Set()); // Track which phases are currently loading

  // Ensure sourceDocument column is visible if there are REVISE, APPROVE, or REVIEW actions
  useEffect(() => {
    const hasReviseOrApproveOrReviewActions = projectPlan.phases.some(phase =>
      phase.tasks.some(task =>
        task.actions.some(action => action.actionType === 'REVISE' || action.actionType === 'APPROVE' || action.actionType === 'REVIEW')
      )
    );
    
    if (hasReviseOrApproveOrReviewActions && !visibleColumns.has('sourceDocument')) {
      setVisibleColumns(prev => {
        const next = new Set(prev);
        next.add('sourceDocument');
        return next;
      });
    }
    if (hasReviseOrApproveOrReviewActions && !visibleColumns.has('rejectAction')) {
      setVisibleColumns(prev => {
        const next = new Set(prev);
        next.add('rejectAction');
        return next;
      });
    }
  }, [projectPlan, visibleColumns]);
  
  // Helper function to format date as YYYY-MM-DD
  const formatDateAsYYYYMMDD = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load holidays for a date range
  const loadHolidaysForDateRange = async (startDate: Date, endDate: Date) => {
    try {
      const startDateStr = formatDateAsYYYYMMDD(startDate);
      const endDateStr = formatDateAsYYYYMMDD(endDate);
      const holidaysData = await getHolidaysForDateRange(startDateStr, endDateStr);
      setHolidays(holidaysData);
      // Create a Set of holiday dates for quick lookup
      const datesSet = new Set<string>(holidaysData.map(h => h.holidayDate));
      setHolidayDates(datesSet);
    } catch (err) {
      // Silently handle holiday loading errors - holidays are optional for date calculations
      // Only log if it's not a 404/403 (expected if endpoint doesn't exist or user doesn't have access)
      const error = err as any;
      if (error?.code !== 'NETWORK_ERROR' || (!error?.message?.includes('404') && !error?.message?.includes('403'))) {
        console.warn('Failed to load holidays:', err);
      }
      // Don't fail the whole page if holidays can't be loaded
      setHolidays([]);
      setHolidayDates(new Set());
    }
  };

  // Optimized: Cache date string conversions for isBusinessDay to avoid repeated formatDateAsYYYYMMDD calls
  const dateStrCache = useRef<Map<number, string>>(new Map()); // Cache: timestamp -> YYYY-MM-DD string
  
  // Helper function to check if a date is a business day (not weekend and not holiday)
  // Memoized to avoid recreation on every render
  const isBusinessDay = useCallback((date: Date): boolean => {
    // Check if weekend (fast check first - no string conversion needed)
    if (isWeekend(date)) {
      return false;
    }
    // Check if holiday using cached date string
    const timestamp = date.getTime();
    let dateStr = dateStrCache.current.get(timestamp);
    if (!dateStr) {
      dateStr = formatDateAsYYYYMMDD(date);
      dateStrCache.current.set(timestamp, dateStr);
    }
    return !holidayDates.has(dateStr);
  }, [holidayDates]);

  // Helper to convert Date to YYYY-MM-DD string using local time (not UTC)
  // This avoids timezone shifts when converting dates back to strings
  // CRITICAL: Use getFullYear(), getMonth(), getDate() which return LOCAL time components
  // Never use toISOString() or similar UTC methods
  const dateToLocalDateString = (date: Date): string => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      console.warn('dateToLocalDateString: Invalid date provided:', date);
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    }
    // Use local time components - these are timezone-independent
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // getMonth() returns 0-11, we need 1-12
    const day = date.getDate();
    
    const yearStr = String(year);
    const monthStr = String(month).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    
    const result = `${yearStr}-${monthStr}-${dayStr}`;
    
    const utcYear = date.getUTCFullYear();
    const utcMonth = date.getUTCMonth() + 1;
    const utcDay = date.getUTCDate();
    const hasMismatch = year !== utcYear || month !== utcMonth || day !== utcDay;
    
    if (hasMismatch) {
      console.warn('dateToLocalDateString: UTC/Local mismatch detected:', {
        inputDate: date.toString(),
        inputDateISO: date.toISOString(),
        localComponents: { year, month, day },
        utcComponents: { year: utcYear, month: utcMonth, day: utcDay },
        result,
        timezoneOffset: date.getTimezoneOffset(),
      });
    }
    
    return result;
  };

  // Optimized: Shared function to add working hours (reused across calculations)
  // Note: isBusinessDay is defined in the same scope, so we can reference it directly
  const addWorkingHours = useCallback((startDate: Date, hours: number): Date => {
    let currentDate = new Date(startDate);
    let remainingHours = hours;
    
    // Optimize: Skip multiple days at once for weekends
    while (remainingHours > 0) {
      // Skip weekends and holidays
      while (!isBusinessDay(currentDate)) {
        currentDate = addDays(currentDate, 1);
      }
      
      const hoursToday = Math.min(remainingHours, 8);
      remainingHours -= hoursToday;
      
      if (remainingHours > 0) {
        currentDate = addDays(currentDate, 1);
      }
    }
    
    return currentDate;
  }, [isBusinessDay]); // Depend on isBusinessDay which is now memoized
  
  // Helper function to find next business day (optimized)
  const getNextBusinessDay = useCallback((date: Date): Date => {
    let nextDate = new Date(date);
    while (!isBusinessDay(nextDate)) {
      nextDate = addDays(nextDate, 1);
    }
    return nextDate;
  }, [isBusinessDay]); // Depend on isBusinessDay which is now memoized

  // Count business days between start and end (inclusive). Used for duration = end - start in business days
  // so parallel actions are not double-counted (e.g. REVIEW and APPROVE both ending 2/5).
  const businessDaysBetween = useCallback((start: Date | string | null, end: Date | string | null): number => {
    if (!start || !end) return 0;
    const toDate = (v: Date | string): Date => {
      if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
      const s = typeof v === 'string' ? v.split('T')[0] : '';
      const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return new Date(NaN);
      return new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10));
    };
    const startDate = toDate(start);
    const endDate = toDate(end);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || endDate < startDate) return 0;
    let count = 0;
    let current = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    while (current <= endOnly) {
      if (isBusinessDay(current)) count++;
      current.setDate(current.getDate() + 1);
    }
    return count;
  }, [isBusinessDay]);

  // Helper function to calculate task dates (needed by calculateDurations)
  const calculateTaskDates = (
    task: PlanningTask,
    _phase: PlanningPhase,
    plan: ProjectPlan,
    taskDurations: Map<number, number>,
    taskEndDates: Map<number, Date>,
    phaseEarliestStart?: Date | null
  ): { startDate: Date; endDate: Date } => {
    const projectStartDateAsDate = getProjectStartDateAsDate(plan.projectStartDate);
    const taskDuration = taskDurations.get(task.taskId) || 0;
    
    // Calculate task start date based on prerequisites
    let taskStartDate: Date;
    if (task.prerequisites && task.prerequisites.length > 0) {
      // Find the latest end date of prerequisite tasks
      let latestPrereqEndDate: Date | null = null;
      for (const prereqId of task.prerequisites) {
        const prereqEndDate = taskEndDates.get(prereqId);
        if (prereqEndDate && (!latestPrereqEndDate || prereqEndDate > latestPrereqEndDate)) {
          latestPrereqEndDate = prereqEndDate;
        }
      }
      
      if (latestPrereqEndDate) {
        // Start the day after the latest prerequisite ends
        taskStartDate = addDays(latestPrereqEndDate, 1);
        taskStartDate.setHours(8, 0, 0, 0);
        // Skip weekends and holidays (use optimized function)
        taskStartDate = getNextBusinessDay(taskStartDate);
      } else {
        // No valid prerequisite dates, start from project start date
        // Create a new date from the project start date components to avoid timezone issues
        const projectYear = projectStartDateAsDate.getFullYear();
        const projectMonth = projectStartDateAsDate.getMonth();
        const projectDay = projectStartDateAsDate.getDate();
        taskStartDate = new Date(projectYear, projectMonth, projectDay, 8, 0, 0, 0);
        taskStartDate = getNextBusinessDay(taskStartDate);
      }
    } else {
      // No prerequisites, start from project start date
      // Create a new date from the project start date components to avoid timezone issues
      const projectYear = projectStartDateAsDate.getFullYear();
      const projectMonth = projectStartDateAsDate.getMonth();
      const projectDay = projectStartDateAsDate.getDate();
      taskStartDate = new Date(projectYear, projectMonth, projectDay, 8, 0, 0, 0);
      taskStartDate = getNextBusinessDay(taskStartDate);
    }

    // Enforce phase prerequisite: phase cannot start before its prerequisite phases end
    if (phaseEarliestStart != null && taskStartDate < phaseEarliestStart) {
      taskStartDate = new Date(phaseEarliestStart);
      taskStartDate = getNextBusinessDay(taskStartDate);
    }

    const taskEndDate = addWorkingHours(taskStartDate, taskDuration);

    return { startDate: taskStartDate, endDate: taskEndDate };
  };

  // Calculate durations and dates with prerequisite support
  // Optimized: Clear date string cache before calculation to prevent memory leaks
  const calculateDurations = useCallback((plan: ProjectPlan): ProjectPlan => {
    // Clear cache at start of calculation to prevent memory growth
    dateStrCache.current.clear();
    const updatedPlan = { ...plan };
    
    // First pass: calculate durations for all tasks
    const taskDurations = new Map<number, number>();
    const taskEndDates = new Map<number, Date>();
    
    updatedPlan.phases.forEach((phase) => {
      phase.tasks.forEach((task) => {
        let taskDuration = 0;
        task.actions.forEach((action) => {
          taskDuration += action.duration;
        });
        taskDurations.set(task.taskId, taskDuration);
      });
    });

    // Topological order of phases by phase prerequisites (so a phase is after all its prerequisite phases)
    const phaseIds = new Set(updatedPlan.phases.map((p) => p.phaseId));
    const phaseById = new Map(updatedPlan.phases.map((p) => [p.phaseId, p]));
    const inDegree = new Map<number, number>();
    const reverseEdges = new Map<number, number[]>();
    updatedPlan.phases.forEach((phase) => {
      inDegree.set(phase.phaseId, 0);
      reverseEdges.set(phase.phaseId, []);
    });
    updatedPlan.phases.forEach((phase) => {
      const prereqs = ((phase as unknown as { prerequisites?: number[] }).prerequisites ?? []).filter((id) => phaseIds.has(id));
      prereqs.forEach((prereqId) => {
        inDegree.set(phase.phaseId, (inDegree.get(phase.phaseId) ?? 0) + 1);
        reverseEdges.get(prereqId)!.push(phase.phaseId);
      });
    });
    const phaseQueue = updatedPlan.phases.filter((p) => (inDegree.get(p.phaseId) ?? 0) === 0);
    const phaseOrder: typeof updatedPlan.phases = [];
    while (phaseQueue.length > 0) {
      const phase = phaseQueue.shift()!;
      phaseOrder.push(phase);
      (reverseEdges.get(phase.phaseId) ?? []).forEach((dependentId) => {
        const d = (inDegree.get(dependentId) ?? 1) - 1;
        inDegree.set(dependentId, d);
        if (d === 0) phaseQueue.push(phaseById.get(dependentId)!);
      });
    }
    // If cycle or missing refs, use original order so all phases are still processed
    const orderToUse = phaseOrder.length === updatedPlan.phases.length ? phaseOrder : updatedPlan.phases;

    const projectStartDateAsDate = getProjectStartDateAsDate(updatedPlan.projectStartDate);
    const phaseEndDates = new Map<number, Date>();

    // Second pass: calculate dates in phase dependency order; enforce phase prerequisites (phase cannot start before prereq phase ends)
    const newPhases = orderToUse.map((phase) => {
      let phaseEarliestStart: Date | null = null;
      const phasePrereqs = ((phase as unknown as { prerequisites?: number[] }).prerequisites ?? []).filter((id) => phaseIds.has(id));
      if (phasePrereqs.length > 0) {
        phasePrereqs.forEach((prereqId) => {
          const prereqEnd = phaseEndDates.get(prereqId);
          if (prereqEnd) {
            const nextBiz = addDays(prereqEnd, 1);
            nextBiz.setHours(8, 0, 0, 0);
            const nextBizDay = getNextBusinessDay(nextBiz);
            if (!phaseEarliestStart || nextBizDay > phaseEarliestStart) phaseEarliestStart = nextBizDay;
          }
        });
      }
      if (phaseEarliestStart == null) {
        phaseEarliestStart = new Date(projectStartDateAsDate);
        phaseEarliestStart = getNextBusinessDay(phaseEarliestStart);
      }

      let phaseDuration = 0;
      let phaseStartDate: Date | null = null;
      let phaseEndDate: Date | null = null;

      const updatedTasks = phase.tasks.map((task) => {
        const taskDuration = taskDurations.get(task.taskId) || 0;
        const dates = calculateTaskDates(task, phase, updatedPlan, taskDurations, taskEndDates, phaseEarliestStart);
        const taskStartDate = dates.startDate;
        const taskEndDate = dates.endDate;

        taskEndDates.set(task.taskId, taskEndDate);

        phaseDuration += taskDuration;
        if (!phaseStartDate || taskStartDate < phaseStartDate) phaseStartDate = taskStartDate;
        if (!phaseEndDate || taskEndDate > phaseEndDate) phaseEndDate = taskEndDate;

        let currentActionStart: Date | null = taskStartDate;
        const actionsWithDates = task.actions.map(action => {
          let plannedStartDate: string | null = null;
          let plannedEndDate: string | null = null;
          if (currentActionStart) {
            const actionStartDate = getNextBusinessDay(new Date(currentActionStart));
            const actionEndDate = addWorkingHours(actionStartDate, action.duration);
            plannedStartDate = dateToLocalDateString(actionStartDate);
            plannedEndDate = dateToLocalDateString(actionEndDate);
            currentActionStart = addDays(actionEndDate, 1);
            currentActionStart = getNextBusinessDay(currentActionStart);
          }
          return {
            ...action,
            assignedTo: action.assignedTo || [],
            plannedStartDate,
            plannedEndDate,
          };
        });

        return {
          ...task,
          duration: taskDuration,
          startDate: dateToLocalDateString(taskStartDate),
          endDate: dateToLocalDateString(taskEndDate),
          assignedUsers: task.assignedUsers || [],
          actions: actionsWithDates,
        };
      });

      if (phaseEndDate) phaseEndDates.set(phase.phaseId, phaseEndDate);

      const phaseStartDateStr = phaseStartDate ? dateToLocalDateString(phaseStartDate) : null;
      const phaseEndDateStr = phaseEndDate ? dateToLocalDateString(phaseEndDate) : null;

      return {
        ...phase,
        tasks: updatedTasks,
        duration: phaseDuration,
        startDate: phaseStartDateStr,
        endDate: phaseEndDateStr,
      };
    });

    // Restore display order so UI and persistence match original phase order
    updatedPlan.phases = [...newPhases].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    return updatedPlan;
  }, [addWorkingHours, getNextBusinessDay, isBusinessDay]); // calculateTaskDates/getProjectStartDateAsDate used but defined in same component

  // Calculate task and phase durations from end - start (business days) when dates exist from API;
  // otherwise fall back to sum of action/task durations. This avoids "2 extra days" when parallel
  // actions (e.g. REVIEW and APPROVE) are not double-counted.
  const calculateDurationsOnly = useCallback((plan: ProjectPlan): ProjectPlan => {
    const updatedPlan = { ...plan };
    
    updatedPlan.phases = updatedPlan.phases.map((phase) => {
      const updatedTasks = phase.tasks.map((task) => {
        const hasTaskDates = task.startDate != null && task.endDate != null;
        const taskDurationHours = hasTaskDates
          ? businessDaysBetween(task.startDate, task.endDate) * 8
          : task.actions.reduce((sum, action) => sum + action.duration, 0);
        
        return {
          ...task,
          duration: taskDurationHours,
        };
      });
      
      const hasPhaseDates = phase.startDate != null && phase.endDate != null;
      const phaseDurationHours = hasPhaseDates
        ? businessDaysBetween(phase.startDate, phase.endDate) * 8
        : updatedTasks.reduce((sum, task) => sum + task.duration, 0);
      
      return {
        ...phase,
        tasks: updatedTasks,
        duration: phaseDurationHours,
      };
    });
    
    return updatedPlan;
  }, [businessDaysBetween]);

  // Function to save calculated dates to database
  const saveCalculatedDates = useCallback(async (plan: ProjectPlan) => {
    try {
      // Save phase dates
      for (const phase of plan.phases) {
        if (phase.phaseId > 0) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const updateData: any = {};
          
          // Use plannedStartDate/plannedEndDate if available (from calculateDurations), otherwise fallback to startDate/endDate
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const phaseStartDateStr = (phase as any).plannedStartDate || phase.startDate;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const phaseEndDateStr = (phase as any).plannedEndDate || phase.endDate;
          
          // Only include startDate if it's a valid date
          if (phaseStartDateStr) {
            let startDateStr: string | null = null;
            if (typeof phaseStartDateStr === 'string') {
              startDateStr = phaseStartDateStr.trim() ? phaseStartDateStr.split('T')[0] : null;
            } else if (phaseStartDateStr instanceof Date) {
              startDateStr = dateToLocalDateString(phaseStartDateStr);
            }
            if (startDateStr) {
              updateData.plannedStartDate = startDateStr;
            }
          }
          
          // Only include endDate if it's a valid date
          if (phaseEndDateStr) {
            let endDateStr: string | null = null;
            if (typeof phaseEndDateStr === 'string') {
              endDateStr = phaseEndDateStr.trim() ? phaseEndDateStr.split('T')[0] : null;
            } else if (phaseEndDateStr instanceof Date) {
              endDateStr = dateToLocalDateString(phaseEndDateStr);
            }
            if (endDateStr) {
              updateData.plannedEndDate = endDateStr;
            }
          }
          
          // Only include duration if it's a valid number > 0
          if (phase.duration !== undefined && phase.duration !== null && !isNaN(phase.duration) && phase.duration > 0) {
            updateData.plannedDurationHours = phase.duration;
          }
          
          if (Object.keys(updateData).length > 0) {
            await updatePhase(phase.phaseId, updateData);
          }
        }
        
        // Save task dates and prerequisites
        for (const task of phase.tasks) {
          if (task.taskId > 0) {
            const updateData: any = {};
            
            // Use plannedStartDate/plannedEndDate if available (from calculateDurations), otherwise fallback to startDate/endDate
            const taskStartDateStr = (task as any).plannedStartDate || task.startDate;
            const taskEndDateStr = (task as any).plannedEndDate || task.endDate;
            
            // Only include startDate if it's a valid date
            if (taskStartDateStr) {
              let startDateStr: string | null = null;
              if (typeof taskStartDateStr === 'string') {
                startDateStr = taskStartDateStr.trim() ? taskStartDateStr.split('T')[0] : null;
              } else if (taskStartDateStr instanceof Date) {
                startDateStr = dateToLocalDateString(taskStartDateStr);
              }
              if (startDateStr) {
                updateData.plannedStartDate = startDateStr;
              }
            }
            
            // Only include endDate if it's a valid date
            if (taskEndDateStr) {
              let endDateStr: string | null = null;
              if (typeof taskEndDateStr === 'string') {
                endDateStr = taskEndDateStr.trim() ? taskEndDateStr.split('T')[0] : null;
              } else if (taskEndDateStr instanceof Date) {
                endDateStr = dateToLocalDateString(taskEndDateStr);
              }
              if (endDateStr) {
                updateData.plannedEndDate = endDateStr;
              }
            }
            
            // Only include duration if it's a valid number > 0
            if (task.duration !== undefined && task.duration !== null && !isNaN(task.duration) && task.duration > 0) {
              updateData.plannedDurationHours = task.duration;
            }
            
            // Always include prerequisites to ensure they're saved
            // Backend expects prerequisiteTaskIds (not prerequisites)
            // Empty array [] clears all prerequisites, array with IDs sets prerequisites
            // We always include prerequisites in bulk save to ensure they're persisted
            const prereqIds = (task.prerequisites && Array.isArray(task.prerequisites))
              ? task.prerequisites
                  .map(id => typeof id === 'number' ? id : parseInt(String(id), 10))
                  .filter(id => !isNaN(id))
              : [];
            updateData.prerequisiteTaskIds = prereqIds;

            try {
              await updateTask(task.taskId, updateData);
            } catch (error) {
              console.error('Error saving task prerequisites:', {
                taskId: task.taskId,
                taskName: task.name,
                updateData,
                error
              });
              // Don't throw - continue with other tasks
            }
          }
          
          // Save action dates
          // NOTE: Actions now have plannedStartDate and plannedEndDate set by calculateDurations
          for (const action of task.actions) {
            if (action.actionId > 0) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const updateData: any = {};
              
              // Include plannedStartDate if it's set (from calculateDurations)
              if (action.plannedStartDate) {
                const startDateStr = typeof action.plannedStartDate === 'string' && action.plannedStartDate.trim()
                  ? action.plannedStartDate.split('T')[0] // Ensure we only use the date part
                  : null;
                if (startDateStr) {
                  updateData.plannedStartDate = startDateStr;
                }
              }
              
              // Include plannedEndDate if it's set (from calculateDurations)
              if (action.plannedEndDate) {
                const endDateStr = typeof action.plannedEndDate === 'string' && action.plannedEndDate.trim()
                  ? action.plannedEndDate.split('T')[0] // Ensure we only use the date part
                  : null;
                if (endDateStr) {
                  updateData.plannedEndDate = endDateStr;
                }
              }
              
              // Only include duration if it's a valid number >= 0 (actions can have 0 duration)
              if (action.duration !== undefined && action.duration !== null && !isNaN(action.duration) && action.duration >= 0) {
                updateData.plannedDurationHours = action.duration;
              }
              
              if (Object.keys(updateData).length > 0) {
                try {
                  await updateAction(action.actionId, updateData);
                } catch (error) {
                  console.error('Error saving action dates:', {
                    actionId: action.actionId,
                    actionType: action.actionType,
                    updateData,
                    error
                  });
                  // Don't throw - continue with other actions
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error saving calculated dates:', error);
      // Don't throw - allow the save to continue even if date saving fails
      // The dates will be recalculated on next load anyway
    }
  }, []);
  
  // Handle project locking
  const handleLockProject = async () => {
    const projectId = urlProjectId ? parseInt(urlProjectId, 10) : undefined;
    if (!projectId || isNaN(projectId)) {
      setSnackbar({ 
        open: true, 
        message: 'Cannot lock project: No project ID found', 
        severity: 'error' 
      });
      return;
    }
    
    // Check if project has phases
    if (!projectPlan.phases || projectPlan.phases.length === 0) {
      setSnackbar({ 
        open: true, 
        message: 'Cannot lock project: Project must have at least one phase', 
        severity: 'error' 
      });
      return;
    }
    
    try {
      setIsLocking(true);
      setApiError(null);
      
      // Verify ALL actions have assigned users before locking
      const actionsWithoutAssignments: Array<{ phaseName: string; taskName: string; actionName: string; actionId: number }> = [];
      let totalActions = 0;
      let actionsWithAssignments = 0;
      
      for (const phase of projectPlan.phases) {
        for (const task of phase.tasks) {
          for (const action of task.actions) {
            totalActions++;
            if (action.actionId > 0) {
              try {
                const assignments = await getActionAssignments(action.actionId);
                if (assignments.length > 0) {
                  actionsWithAssignments++;
                } else {
                  const actionName = action.notes || `${action.actionType} action`;
                  actionsWithoutAssignments.push({
                    phaseName: phase.name,
                    taskName: task.name,
                    actionName: actionName,
                    actionId: action.actionId
                  });
                }
              } catch (error) {
                console.warn(`handleLockProject: Failed to check assignments for action ${action.actionId}:`, error);
                const actionName = action.notes || `${action.actionType} action`;
                actionsWithoutAssignments.push({
                  phaseName: phase.name,
                  taskName: task.name,
                  actionName: actionName,
                  actionId: action.actionId
                });
              }
            } else {
              // New action that hasn't been saved yet - also needs assignment
              const actionName = action.notes || `${action.actionType} action`;
              actionsWithoutAssignments.push({
                phaseName: phase.name,
                taskName: task.name,
                actionName: actionName,
                actionId: action.actionId
              });
            }
          }
        }
      }

      if (actionsWithoutAssignments.length > 0) {
        console.error('handleLockProject: BLOCKING lock - actions missing assignments:', actionsWithoutAssignments);
        setAssignmentValidationError({
          open: true,
          actions: actionsWithoutAssignments
        });
        setIsLocking(false);
        setLockDialogOpen(false); // Close the lock confirmation dialog
        return;
      }

      const calculatedPlan = calculateDurations(projectPlan);
      await saveCalculatedDates(calculatedPlan);

      const result = await lockProject(projectId);

      // Update local state
      setProjectStatus(result.status);
      setProjectLocked(true);
      setLockDialogOpen(false);
      
      // Build success message with action duplication info if available
      let successMessage = result.message || 'Project locked successfully!';
      if (result.actionDuplication && result.actionDuplication.duplicated > 0) {
        successMessage += ` ${result.actionDuplication.duplicated} REVIEW/APPROVE action(s) duplicated for parallel execution.`;
      }
      
      // Show success message
      setSnackbar({ 
        open: true, 
        message: successMessage, 
        severity: 'success' 
      });
      
      // Navigate to Execution View
      setTimeout(() => {
        navigate(`/execution?projectId=${projectId}`);
      }, 1000);
    } catch (error) {
      const apiError = error as ProjectApiError;
      console.error('handleLockProject: Failed to lock project:', error);
      setApiError(apiError.message || 'Failed to lock project');
      setSnackbar({ 
        open: true, 
        message: apiError.message || 'Failed to lock project', 
        severity: 'error' 
      });
    } finally {
      setIsLocking(false);
    }
  };
  
  // Load project users when projectId changes
  const loadProjectUsers = useCallback(async (projectId: number) => {
    try {
      const response = await getProjectUsers(projectId);
      setProjectUsers(response.users);
      setProjectUserIds(new Set(response.users.map(pu => pu.userId)));
    } catch (error) {
      console.error('Error loading project users:', error);
      // Don't block the UI if project users fail to load
      setProjectUsers([]);
      setProjectUserIds(new Set());
    }
  }, []);

  // Track which project we've loaded to prevent re-loading
  const loadedProjectIdRef = useRef<number | null>(null);
  
  // Load project on mount or when projectId changes
  useEffect(() => {
    const projectId = urlProjectId ? parseInt(urlProjectId, 10) : undefined;
    
    // Check if any dialog/form is currently open - if so, prevent reload to avoid losing user input
    const hasActiveDialogOrForm = 
      documentAssociationDialogOpen ||
      documentSelectionDialogOpen ||
      lockDialogOpen ||
      assignmentValidationError.open ||
      editingCell !== null;
    
    if (hasActiveDialogOrForm) {
      return;
    }
    
    // Prevent re-loading the same project
    if (projectId && !isNaN(projectId) && loadedProjectIdRef.current === projectId) {
      return;
    }
    
    // Reset state when loading a new project
    if (projectId && !isNaN(projectId)) {
      // Projects in EXECUTION or COMPLETE must not open in Planning; redirect to Execution
      getProject(projectId)
        .then((project) => {
          if (project.status === 'EXECUTION' || project.status === 'COMPLETE') {
            navigate(`/execution?projectId=${projectId}`);
            loadedProjectIdRef.current = null;
            return;
          }
          // Project is in PLANNING (or other) - proceed with load
          loadedProjectIdRef.current = projectId;
          hasRecalculatedAfterLoadRef.current = false;
          phaseDetailsCacheRef.current.clear();
          loadingPhasesRef.current.clear();
          preservedPhaseDatesRef.current.clear();
          setPhaseDetailsCache(new Map());
          setLoadingPhases(new Set());
          setIsLoading(true);
          return loadProjectSummary(projectId);
        })
        .then((loadedPlanOrUndefined) => {
          if (loadedPlanOrUndefined === undefined) return; // redirect happened
          const loadedPlan = loadedPlanOrUndefined;
          // Summary loads quickly with phase metadata only (200-500ms)
          setProjectPlan(loadedPlan);
          
          // Immediately start loading ALL phase details in parallel
          // React will automatically re-render each phase component as its data arrives
          const phaseIds = loadedPlan.phases.map(p => p.phaseId);
          
          // Track all phase detail loads to ensure loading state is cleared only after all complete
          const phaseDetailPromises: Promise<void>[] = [];
          
          phaseIds.forEach(phaseId => {
            // Load phase details in parallel - React will render as each completes
            // Check refs to prevent duplicate loads
            const shouldLoad = !phaseDetailsCacheRef.current.has(phaseId) && !loadingPhasesRef.current.has(phaseId);
            if (shouldLoad) {
              const phasePromise = loadPhaseDetails(projectId, phaseId).catch(error => {
                console.error(`Failed to load phase details for phase ${phaseId}:`, error);
                // Return void to ensure Promise.allSettled doesn't fail
                return Promise.resolve();
              });
              phaseDetailPromises.push(phasePromise);
            }
          });
          
          // Wait for all phase details to complete (or fail) before clearing loading state
          // This ensures the browser tab loading indicator stops spinning
          Promise.allSettled(phaseDetailPromises)
            .then(() => {
              setIsLoading(false);
            })
            .catch((error) => {
              console.error('Error waiting for phase details to complete:', error);
              setIsLoading(false);
            });
          
          // If no phases to load, clear loading state immediately
          if (phaseDetailPromises.length === 0) {
            setIsLoading(false);
          }
        })
        .catch((error) => {
          console.error('Failed to load project summary:', error);
          setIsLoading(false);
          // Fallback to full load if summary endpoint fails
          loadProjectPlan(projectId)
            .then((loadedPlan) => {
              setProjectPlan(loadedPlan);
            })
            .catch((fallbackError) => {
              console.error('Failed to load project plan (fallback):', fallbackError);
            });
        });
      // Load project users
      loadProjectUsers(projectId);
    } else {
      // No projectId - show error
      loadedProjectIdRef.current = null;
      setApiError('No project ID provided. Please select a project.');
      setProjectUsers([]);
      setProjectUserIds(new Set());
    }
  }, [
    urlProjectId,
    navigate,
    calculateDurations,
    saveCalculatedDates,
    loadProjectUsers,
    loadPhaseDetails,
    // Include dialog/form state to detect when they're open and prevent reloads
    documentAssociationDialogOpen,
    documentSelectionDialogOpen,
    lockDialogOpen,
    assignmentValidationError.open,
    editingCell,
  ]);

  // Check lock state on page load and when projectId changes
  useEffect(() => {
    const checkLockState = async () => {
      const projectId = urlProjectId ? parseInt(urlProjectId, 10) : undefined;
      if (!projectId || isNaN(projectId)) {
        return;
      }

      try {
        const lockState = await getProjectLockStatus(projectId);
        setProjectLocked(lockState.isLocked);
        if (lockState.isLocked) {
          // Disable drag operations, prerequisite editing, and reordering when locked
          // This is handled by the projectLocked state in the UI components
        }
      } catch (error) {
        console.error('Failed to check lock state:', error);
        // Don't block the UI if lock status check fails - use existing project status
      }
    };

    checkLockState();
  }, [urlProjectId]);
  // Ref to track current users value for assigned fields to avoid stale closures
  const currentUsersRef = useRef<User[]>([]);
  // Ref to track if a user was just selected (to allow save even if focus is still in autocomplete)
  const userJustSelectedRef = useRef(false);
  // Ref to track if a save is in progress or has completed to prevent duplicate saves
  const saveInProgressRef = useRef<{ rowId: string; timestamp: number } | null>(null);
  
  // Helper to convert projectStartDate to Date object
  // Parses date string in local time to avoid timezone issues
  const getProjectStartDateAsDate = (dateValue: Date | string | undefined): Date => {
    if (dateValue instanceof Date) {
      // If it's already a Date, create a new one in local time to avoid timezone issues
      // Use getFullYear, getMonth, getDate to get local time components
      return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate(), 0, 0, 0, 0);
    } else if (typeof dateValue === 'string') {
      // Parse date string (YYYY-MM-DD) in local time to avoid timezone shifts
      // Handle both ISO format (with time) and plain date format
      // IMPORTANT: Always extract just the date part and create in local timezone
      // Never use new Date(dateValue) directly as it may interpret as UTC
      const dateStr = dateValue.split('T')[0]; // Get just the date part (YYYY-MM-DD)
      const dateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const year = parseInt(dateMatch[1], 10);
        const month = parseInt(dateMatch[2], 10);
        const day = parseInt(dateMatch[3], 10);
        // Validate the parsed values
        if (!isNaN(year) && !isNaN(month) && !isNaN(day) && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          // Create date in local timezone (month is 0-indexed in Date constructor)
          // Use noon (12:00) instead of midnight to avoid DST edge cases
          // This ensures the date is exactly what the user entered, regardless of timezone
          const localDate = new Date(year, month - 1, day, 12, 0, 0, 0);
          
          // Verify the date components match what we put in
          const verifyYear = localDate.getFullYear();
          const verifyMonth = localDate.getMonth() + 1;
          const verifyDay = localDate.getDate();
          
          if (verifyYear !== year || verifyMonth !== month || verifyDay !== day) {
            console.error('Date component mismatch in getProjectStartDateAsDate:', {
              input: { year, month, day },
              output: { year: verifyYear, month: verifyMonth, day: verifyDay },
              dateString: localDate.toString(),
              dateISO: localDate.toISOString(),
            });
          }
          
          return localDate;
        }
      }
      console.warn('Invalid date string:', dateValue, 'using today as fallback');
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    } else {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    }
  };
  
  // Store projectStartDate as both a Date object (for calculations) and string (for display)
  // This avoids timezone conversion issues when displaying in the date input
  const getDateStringFromPlan = (): string => {
    if (typeof projectPlan.projectStartDate === 'string') {
      const dateMatch = projectPlan.projectStartDate.match(/^(\d{4}-\d{2}-\d{2})/);
      return dateMatch ? dateMatch[1] : projectPlan.projectStartDate.split('T')[0];
    }
    return getNextBusinessDayDateString();
  };
  
  // Initialize state with a Date object
  const [projectStartDate, setProjectStartDate] = useState<Date>(() => {
    return getProjectStartDateAsDate(projectPlan.projectStartDate);
  });
  
  // Also store the date string directly to avoid conversion issues
  const [projectStartDateString, setProjectStartDateString] = useState<string>(() => {
    return getDateStringFromPlan();
  });
  
  // Update projectStartDate state when projectPlan.projectStartDate changes (e.g., after API load)
  // CRITICAL: Use the raw date string directly - NO Date object conversion to avoid timezone shifts
  useEffect(() => {
    // projectPlan.projectStartDate should already be in YYYY-MM-DD format from parseDatesInPlan
    // Extract just the date part (YYYY-MM-DD) if it has a time component
    let rawDateStr: string;
    if (typeof projectPlan.projectStartDate === 'string') {
      // Extract just the date part (YYYY-MM-DD) - handle both cases:
      // 1. Already in YYYY-MM-DD format (from parseDatesInPlan)
      // 2. Still has time component (direct from API before parsing)
      const dateMatch = projectPlan.projectStartDate.match(/^(\d{4}-\d{2}-\d{2})/);
      rawDateStr = dateMatch ? dateMatch[1] : projectPlan.projectStartDate.split('T')[0];

      // Use the raw date string directly - NO conversion through Date objects
      setProjectStartDateString(rawDateStr);
      
      // Also create a Date object for calculations (but don't use it for display)
      const [year, month, day] = rawDateStr.split('-').map(Number);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        const newDate = new Date(year, month - 1, day, 12, 0, 0, 0);
        setProjectStartDate(newDate);
      }
    } else {
      // If not a string, skip update
      console.warn('Planning page: projectStartDate is not a string:', projectPlan.projectStartDate);
      return;
    }
  }, [projectPlan.projectStartDate]); // Only depend on projectPlan.projectStartDate
  
  // Update projectStartDate in projectPlan when user changes it in the UI
  // NOTE: We no longer sync projectPlan.projectStartDate from projectStartDate state here
  // because we update it directly in the TextField onChange handler to avoid timezone conversion issues.
  // The projectStartDate Date object is only used for calculations, not for storing the date string.
  // This prevents the -1 day issue that was occurring when converting Date objects back to strings.
  const [validationErrors, setValidationErrors] = useState<Map<string, string>>(new Map());
  const [validationErrorDialog, setValidationErrorDialog] = useState<{
    open: boolean;
    errors: Array<{ type: string; id: number; error: string }>;
  }>({ open: false, errors: [] });
  const [backendValidationError, setBackendValidationError] = useState<{
    message: string;
    code?: string;
    details?: {
      entityType?: string;
      entityName?: string;
      entityId?: number;
      reason?: string;
      cyclePath?: string[];
    };
  } | null>(null);
  /** Validation errors from last recalculate-dates (lock-style validation). When set, dates may be inaccurate. */
  const [recalcValidationErrors, setRecalcValidationErrors] = useState<RecalculateValidationError[] | null>(null);
  // Track newly created actions to suppress validation errors until user has a chance to edit them
  const [newlyCreatedActions, setNewlyCreatedActions] = useState<Set<number>>(new Set());
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  // Expanded state for phases and tasks
  const [expandedRows, setExpandedRows] = useState<Set<string> | null>(null); // null = all expanded, Set = explicit state

  // Drag and drop sensors - optimized for performance
  // Reduced distance for faster activation, but still prevents accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Reduced from 8 for faster activation
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validateEmails = (emails: string[]): string | null => {
    for (const email of emails) {
      if (!validateEmail(email)) {
        return `Invalid email: ${email}`;
      }
    }
    return null;
  };

  const validateDuration = (duration: number): string | null => {
    if (isNaN(duration) || duration <= 0) {
      return 'Duration must be a positive number';
    }
    return null;
  };

  const checkCircularDependencies = (_taskId: number, prerequisites: number[], plan: ProjectPlan): string | null => {
    const visited = new Set<number>();
    const visiting = new Set<number>();

    const hasCycle = (currentTaskId: number): boolean => {
      if (visiting.has(currentTaskId)) {
        return true; // Circular dependency detected
      }
      if (visited.has(currentTaskId)) {
        return false; // Already checked, no cycle
      }

      visiting.add(currentTaskId);
      
      // Find the task
      for (const phase of plan.phases) {
        const task = phase.tasks.find(t => t.taskId === currentTaskId);
        if (task) {
          // Check all prerequisites
          for (const prereqId of task.prerequisites) {
            if (hasCycle(prereqId)) {
              return true;
            }
          }
        }
      }

      visiting.delete(currentTaskId);
      visited.add(currentTaskId);
      return false;
    };

    // Check if adding these prerequisites creates a cycle
    for (const prereqId of prerequisites) {
      if (hasCycle(prereqId)) {
        return `Circular dependency detected: Task ${prereqId} would create a cycle`;
      }
    }

    return null;
  };

  const validatePlan = useCallback((plan: ProjectPlan): Map<string, string> => {
    const errors = new Map<string, string>();

    plan.phases.forEach((phase) => {
      // Validate phase has at least one task
      if (phase.tasks.length === 0) {
        errors.set(`phase-${phase.phaseId}`, 'Phase must have at least one Task');
      }

      phase.tasks.forEach((task) => {
        // Validate task has at least one action
        if (task.actions.length === 0) {
          errors.set(`task-${task.taskId}`, 'Task must have at least one Action');
        }

        // Validate duration
        if (task.duration !== undefined) {
          const durationError = validateDuration(task.duration);
          if (durationError) {
            errors.set(`task-${task.taskId}-duration`, durationError);
          }
        }

        // Validate assigned users (emails)
        if (task.assignedUsers && task.assignedUsers.length > 0) {
          const emailError = validateEmails(task.assignedUsers);
          if (emailError) {
            errors.set(`task-${task.taskId}-assignedUsers`, emailError);
          }
        }

        // Validate prerequisites (no circular dependencies)
        if (task.prerequisites && task.prerequisites.length > 0) {
          const circularError = checkCircularDependencies(task.taskId, task.prerequisites, plan);
          if (circularError) {
            errors.set(`task-${task.taskId}-prerequisites`, circularError);
          }
        }

        task.actions.forEach((action) => {
          // Validate action duration
          if (action.duration !== undefined) {
            const durationError = validateDuration(action.duration);
            if (durationError) {
              errors.set(`action-${action.actionId}-duration`, durationError);
            }
          }

          // Validate assigned users (emails)
          if (action.assignedTo && action.assignedTo.length > 0) {
            const emailError = validateEmails(action.assignedTo);
            if (emailError) {
              errors.set(`action-${action.actionId}-assignedTo`, emailError);
            }
          }
        });
      });
    });

    return errors;
  }, []);

  // NOTE: calculateTaskDates, calculateDurations, and saveCalculatedDates are now defined earlier in the file (before useEffect)
  // The duplicate addWorkingHours definition has been removed - using the optimized memoized version at line 2286

  // Track if this is the initial mount to avoid double calculation
  const isInitialMountRef = useRef(true);
  
  // Track if we've recalculated after loading to avoid infinite loops in validation
  const hasRecalculatedAfterLoadRef = useRef(false);
  // Single post-recalc guard: skip one run of the effect that runs calculateDurations when plan comes from backend recalc (response or GET /plan).
  const justDidBackendRecalculationRef = useRef(false);
  // Track if we just did a save to skip frontend recalculation after reload
  const justDidSaveRef = useRef(false);

    // Initialize - but DON'T recalculate dates on mount
    // Dates should come from the API when the project loads
    // Only recalculate if explicitly requested by the user
  useEffect(() => {
    // Don't recalculate dates on initial mount - wait for project to load from API
    // The projectPlan will be set by loadProjectPlan, which already has dates from the API
    if (projectPlan.phases.length > 0) {
      // Project has been loaded - just validate, don't recalculate
      const errors = validatePlan(projectPlan);
      setValidationErrors(errors);
    }
    setProjectStartDate(getProjectStartDateAsDate(projectPlan.projectStartDate));
    isInitialMountRef.current = false;
  }, []);

  // Validate plan whenever it changes, but suppress errors during loading
  // After loading completes, recalculate durations before validating
  useEffect(() => {
    // Suppress validation errors if project is still loading
    // Check if we're loading the summary or any phase details
    const isStillLoading = isLoading || loadingPhases.size > 0;
    
    // Also check if phases have empty tasks (indicating they're still loading from summary)
    const hasIncompletePhases = projectPlan.phases.some(phase => phase.tasks.length === 0);
    
    if (isStillLoading || hasIncompletePhases) {
      // Clear validation errors during loading to prevent false positives
      setValidationErrors(new Map());
      setRecalcValidationErrors(null);
      hasRecalculatedAfterLoadRef.current = false;
      return;
    }

    // Skip frontend recalculation if we just did a backend recalculation OR a save
    // Backend already calculated/saved the correct dates, so we don't want to overwrite them
    if (justDidBackendRecalculationRef.current || justDidSaveRef.current) {
      const errors = validatePlan(projectPlan);
      setValidationErrors(errors);
      return;
    }
    
    // Loading is complete - check if we should recalculate dates
    // CRITICAL: Only recalculate if dates are NOT already set from the API
    // If the API returned dates, we should use those, not recalculate them
    const hasDatesFromApi = projectPlan.phases.some(phase => 
      phase.startDate !== null && phase.startDate !== undefined &&
      phase.endDate !== null && phase.endDate !== undefined
    );
    
    // Only recalculate if:
    // 1. We haven't recalculated after load yet
    // 2. AND dates are NOT already set from the API (phases have no dates)
    // This prevents overwriting dates that were loaded from the backend
    if (!hasRecalculatedAfterLoadRef.current && !hasDatesFromApi) {
      const recalculatedPlan = calculateDurations(projectPlan);
      hasRecalculatedAfterLoadRef.current = true;
      setProjectPlan(recalculatedPlan);
      // Validation will run again after setProjectPlan updates with recalculated plan
      return;
    } else if (!hasRecalculatedAfterLoadRef.current && hasDatesFromApi) {
      const planWithDurations = calculateDurationsOnly(projectPlan);
      hasRecalculatedAfterLoadRef.current = true;
      setProjectPlan(planWithDurations);
      // Validate the plan after updating durations
      const errors = validatePlan(planWithDurations);
      setValidationErrors(errors);
      return;
    }

    const errors = validatePlan(projectPlan);
    setValidationErrors(errors);
  }, [projectPlan, validatePlan, isLoading, loadingPhases, calculateDurations]);

  // Load holidays when project plan or project start date changes
  useEffect(() => {
    if (projectPlan.projectId > 0 && projectPlan.projectStartDate) {
      const projectStartDate = getProjectStartDateAsDate(projectPlan.projectStartDate);
      
      // Calculate estimated end date (project start + 2 years as a safe range)
      const estimatedEndDate = new Date(projectStartDate);
      estimatedEndDate.setFullYear(estimatedEndDate.getFullYear() + 2);
      
      // Also check actual project end date from phases
      let actualEndDate = estimatedEndDate;
      projectPlan.phases.forEach(phase => {
        if (phase.endDate) {
          const phaseEndDate = new Date(phase.endDate);
          if (phaseEndDate > actualEndDate) {
            actualEndDate = phaseEndDate;
          }
        }
      });
      
      // Add some buffer (30 days) to ensure we have holidays for the full range
      const bufferDate = new Date(actualEndDate);
      bufferDate.setDate(bufferDate.getDate() + 30);
      
      loadHolidaysForDateRange(projectStartDate, bufferDate);
    }
  }, [projectPlan.projectId, projectPlan.projectStartDate, projectPlan.phases]);

  // Recalculate when project start date changes
  useEffect(() => {
    // Skip on initial mount (handled by the initialization effect above)
    if (isInitialMountRef.current) {
      return;
    }

    // Update the plan with the new project start date and recalculate all dates
    setProjectPlan(prevPlan => {
      let projectStartDateStr: string;
      if (projectStartDate instanceof Date) {
        projectStartDateStr = dateToLocalDateString(projectStartDate);
      } else if (typeof projectStartDate === 'string') {
        projectStartDateStr = (projectStartDate as string).split('T')[0];
      } else if (typeof prevPlan.projectStartDate === 'string') {
        projectStartDateStr = prevPlan.projectStartDate;
      } else {
        projectStartDateStr = getNextBusinessDayDateString();
      }
      const updatedPlan = { ...prevPlan, projectStartDate: projectStartDateStr };
      const calculatedPlan = calculateDurations(updatedPlan);
      // Ensure the calculated plan has the correct project start date
      calculatedPlan.projectStartDate = projectStartDateStr;
      return calculatedPlan;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectStartDate]);

  // Auto-save timer: Save project plan every 10 minutes (600000 ms)
  useEffect(() => {
    // Only auto-save if project is not locked and has a valid project ID
    if (projectLocked || !projectPlan.projectId || projectPlan.projectId <= 0) {
      return;
    }

    const autoSaveInterval = setInterval(async () => {
      // Sprint B3: Skip auto-save when any modal is open
      if (isModalOpen) {
        return;
      }
      // Skip auto-save if there are validation errors
      if (validationErrors.size > 0) {
        return;
      }

      // Skip auto-save if manual save is in progress
      if (isSaving) {
        return;
      }

      try {
        // Use the same save logic as the manual save button
        const planToSave = {
          ...projectPlan,
          projectStartDate: projectStartDateString || (typeof projectPlan.projectStartDate === 'string' 
            ? projectPlan.projectStartDate.split('T')[0]
            : getNextBusinessDayDateString()),
          phases: projectPlan.phases.map(phase => ({
            ...phase,
            startDate: typeof phase.startDate === 'string' ? phase.startDate.split('T')[0] : null,
            endDate: typeof phase.endDate === 'string' ? phase.endDate.split('T')[0] : null,
            tasks: phase.tasks.map(task => ({
              ...task,
              startDate: typeof task.startDate === 'string' ? task.startDate.split('T')[0] : null,
              endDate: typeof task.endDate === 'string' ? task.endDate.split('T')[0] : null,
              actions: task.actions.map(action => ({
                ...action,
              })),
            })),
          })),
        };

        // Save to API (silently, no user notification for auto-save)
        await updateProject(planToSave.projectId, {
          name: planToSave.projectName,
          projectStartDate: planToSave.projectStartDate,
        });

        let planForStorage = planToSave;
        try {
          const refreshed = await getProjectPlan(planToSave.projectId);
          const mapped = mapGetPlanResponseToProjectPlan(refreshed);
          const merged = mergeScheduleDatesFromServer(planToSave, mapped);
          planForStorage = calculateDurationsOnly(merged);
          justDidBackendRecalculationRef.current = true;
          setProjectPlan(planForStorage);
        } catch (refreshErr) {
          console.warn('Auto-save: could not refresh plan after project update:', refreshErr);
        }

        // Save to localStorage as backup
        const planJson = JSON.stringify(planForStorage);
        try {
          localStorage.setItem('projectPlan', planJson);
        } catch (storageError) {
          // Ignore storage errors for auto-save
          console.warn('Auto-save: Failed to save to localStorage:', storageError);
        }
      } catch (error) {
        // Silently fail auto-save - don't interrupt user workflow
        console.warn('Auto-save failed:', error);
      }
    }, 600000); // 10 minutes = 600000 milliseconds

    return () => {
      clearInterval(autoSaveInterval);
    };
  }, [
    projectPlan,
    projectLocked,
    validationErrors.size,
    isSaving,
    projectStartDateString,
    isModalOpen,
    mapGetPlanResponseToProjectPlan,
    mergeScheduleDatesFromServer,
    calculateDurationsOnly,
  ]);

  // Convert plan to flat grid rows
  const gridRows = useMemo((): GridRow[] => {
    const rows: GridRow[] = [];
    
    // Calculate project-level dates and duration
    let projectStartDate: Date | null = null;
    let projectEndDate: Date | null = null;
    let projectDuration = 0;
    
    projectPlan.phases.forEach((phase) => {
      projectDuration += phase.duration;
      if (phase.startDate) {
        const phaseStartDateObj = new Date(phase.startDate + 'T00:00:00');
        if (!projectStartDate || phaseStartDateObj < projectStartDate) {
          projectStartDate = phaseStartDateObj;
        }
      }
      if (phase.endDate) {
        const phaseEndDateObj = new Date(phase.endDate + 'T00:00:00');
        if (!projectEndDate || phaseEndDateObj > projectEndDate) {
          projectEndDate = phaseEndDateObj;
        }
      }
    });
    
    // Add PROJECT summary row at the top
    rows.push({
      id: 'project-summary',
      type: 'PROJECT',
      level: -1,
      data: {
        ...projectPlan,
        startDate: projectStartDate || projectPlan.projectStartDate,
        endDate: projectEndDate,
        duration: projectDuration,
      } as ProjectPlan,
      expanded: true,
    });
    
    projectPlan.phases.forEach((phase) => {
      const phaseId = `phase-${phase.phaseId}`;
      // null = all expanded, Set = check if phase is in the set
      const isPhaseExpanded = expandedRows === null || expandedRows.has(phaseId);
      
      // Phase row
      rows.push({
        id: phaseId,
        type: 'PHASE',
        level: 0,
        data: phase,
        expanded: isPhaseExpanded,
      });

      // Only add tasks if phase is expanded
      if (isPhaseExpanded) {
        phase.tasks.forEach((task) => {
          const taskId = `task-${task.taskId}`;
          // null = all expanded, Set = check if task is in the set
          const isTaskExpanded = expandedRows === null || expandedRows.has(taskId);
          
          rows.push({
            id: taskId,
            type: 'TASK',
            level: 1,
            phaseId: phase.phaseId,
            data: task,
            expanded: isTaskExpanded,
          });

          // Only add actions if task is expanded
          if (isTaskExpanded) {
            // Sort actions by displayOrder before displaying to ensure correct order
            // This is a defensive measure in case actions array is not already sorted
            const sortedActions = [...task.actions].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            sortedActions.forEach((action) => {
              rows.push({
                id: `action-${action.actionId}`,
                type: 'ACTION',
                level: 2,
                phaseId: phase.phaseId,
                taskId: task.taskId,
                data: action,
              });
            });
          }
        });
      }
    });

    return rows;
  }, [projectPlan, expandedRows]);


  // Toggle expand/collapse for a single row
  const handleToggleExpand = useCallback((rowId: string) => {
    setExpandedRows(prev => {
      // If prev is null (all expanded), create a Set with all row IDs except the one being toggled
      if (prev === null) {
        const allRowIds = new Set<string>();
        projectPlan.phases.forEach(phase => {
          allRowIds.add(`phase-${phase.phaseId}`);
          phase.tasks.forEach(task => {
            allRowIds.add(`task-${task.taskId}`);
          });
        });
        // Remove the toggled row ID to collapse it
        allRowIds.delete(rowId);
        return allRowIds;
      }
      
      // Otherwise, toggle the row in the existing Set
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, [projectPlan, phaseDetailsCache]);

  // Expand/collapse all
  const handleExpandCollapseAll = (expand: boolean) => {
    if (expand) {
      // Expand all phases and tasks - set to null to show all
      setExpandedRows(null);
    } else {
      // Collapse all - set to empty Set to hide all
      setExpandedRows(new Set());
    }
  };

  // Validation function to check if source action is valid
  // Source action can be:
  // 1. An earlier action in the same task (actions execute sequentially)
  // 2. An action from a prerequisite task
  // 3. An action from a task in a prerequisite phase (grandchild)
  const validateSourceAction = (
    sourceActionId: number,
    targetActionId: number,
    projectPlan: ProjectPlan
  ): { valid: boolean; error?: string } => {
    // Rule 1: Cannot be itself
    if (sourceActionId === targetActionId) {
      return { 
        valid: false, 
        error: 'An action cannot be its own source document' 
      };
    }
    
    // Find source action and its task
    let sourceTask: PlanningTask | null = null;
    let sourceAction: PlanningAction | null = null;
    
    for (const phase of projectPlan.phases) {
      for (const task of phase.tasks) {
        const action = task.actions.find(a => a.actionId === sourceActionId);
        if (action) {
          sourceTask = task;
          sourceAction = action;
          break;
        }
      }
      if (sourceTask) break;
    }
    
    if (!sourceTask || !sourceAction) {
      return { valid: false, error: 'Source action not found' };
    }
    
    // Find target action, its task, and its phase
    let targetTask: PlanningTask | null = null;
    let targetAction: PlanningAction | null = null;
    let targetPhase: PlanningPhase | null = null;
    
    for (const phase of projectPlan.phases) {
      for (const task of phase.tasks) {
        const action = task.actions.find(a => a.actionId === targetActionId);
        if (action) {
          targetTask = task;
          targetAction = action;
          targetPhase = phase;
          break;
        }
      }
      if (targetTask) break;
    }
    
    if (!targetTask || !targetAction || !targetPhase) {
      return { valid: false, error: 'Target action not found' };
    }
    
    // Find source phase
    let sourcePhase: PlanningPhase | null = null;
    for (const phase of projectPlan.phases) {
      if (phase.tasks.some(t => t.taskId === sourceTask!.taskId)) {
        sourcePhase = phase;
        break;
      }
    }
    
    if (!sourcePhase) {
      return { valid: false, error: 'Source phase not found' };
    }
    
    // Check if source and target are in the same task
    // If so, source must come before target (lower displayOrder)
    if (sourceTask.taskId === targetTask.taskId) {
      if (sourceAction.displayOrder < targetAction.displayOrder) {
        // Source action comes before target action in the same task - valid
        return { valid: true };
      } else {
        return { 
          valid: false, 
          error: 'Source action must come before the target action in the same task' 
        };
      }
    }
    
    // Rule 3: Source task is a prerequisite of target task
    if (targetTask.prerequisites.includes(sourceTask.taskId)) {
      return { valid: true };
    }
    
    // Rule 4: Source phase is a prerequisite of target phase
    const targetPrerequisitePhaseIds = (targetPhase as any).prerequisites || [];
    if (targetPrerequisitePhaseIds.includes(sourcePhase.phaseId)) {
      return { valid: true };
    }
    
    // Rule 5: Source phase comes before target phase (earlier phase)
    const sourcePhaseOrder = sourcePhase.displayOrder || 0;
    const targetPhaseOrder = targetPhase.displayOrder || 0;
    if (sourcePhaseOrder < targetPhaseOrder) {
      // Source is in an earlier phase, guaranteed to complete before target phase
      return { valid: true };
    }
    
    // Rule 6: Same phase, earlier task (task with lower displayOrder in same phase)
    if (sourcePhase.phaseId === targetPhase.phaseId) {
      const sourceTaskOrder = sourceTask.displayOrder || 0;
      const targetTaskOrder = targetTask.displayOrder || 0;
      if (sourceTaskOrder < targetTaskOrder) {
        // Source is in an earlier task in the same phase
        return { valid: true };
      } else {
        return {
          valid: false,
          error: 'Source action must be from an earlier task in the same phase, or from a prerequisite task/phase'
        };
      }
    }
    
    // Rule 7: Source task is in a phase that contains prerequisite tasks (grandchild)
    const targetPrerequisites = targetTask.prerequisites;
    const prerequisitePhases = projectPlan.phases.filter(phase =>
      phase.tasks.some(task => targetPrerequisites.includes(task.taskId))
    );
    const isGrandchild = prerequisitePhases.some(phase =>
      phase.tasks.some(task => task.taskId === sourceTask.taskId)
    );
    
    if (isGrandchild) {
      return { valid: true };
    }
    
    // If none of the rules match, it's invalid
    return { 
      valid: false, 
      error: 'Source action must be from a prerequisite task/phase, an earlier phase, an earlier task in the same phase, or an earlier action in the same task' 
    };
  };

  // Handle drag end to reorder rows or assign prerequisites
  // Memoize row lookups for performance
  const rowMapRef = useRef<Map<string, GridRow>>(new Map());
  
  useEffect(() => {
    // Update row map when gridRows changes
    const map = new Map<string, GridRow>();
    gridRows.forEach(row => {
      map.set(row.id, row);
    });
    rowMapRef.current = map;
  }, [gridRows]);

  // Helper function to build specific error messages from backend errors
  const buildErrorMessage = (error: any): string => {
    // Check for specific prerequisite violation
    if (error.details?.reason) {
      const reason = error.details.reason.toLowerCase();
      if (reason.includes('prerequisite') || reason.includes('before') || reason.includes('depend')) {
        const entityType = error.details.entityType || 'project step';
        const entityName = error.details.entityName ? ` "${error.details.entityName}"` : '';
        return `Cannot move ${entityType}${entityName} before its prerequisites. ${error.details.reason}`;
      }
    }
    
    // Check for entity-specific error details
    if (error.details?.entityName && error.details?.entityType) {
      const entityType = error.details.entityType;
      const entityName = error.details.entityName;
      if (error.details.reason) {
        return `${entityType} "${entityName}": ${error.details.reason}`;
      }
      return `Validation failed for ${entityType} "${entityName}"`;
    }
    
    // Use the error message if available
    if (error.message && error.message !== 'Validation failed for one or more reorder operations') {
      return error.message;
    }
    
    // Fallback to generic message
    return 'Cannot reorder: this would violate prerequisite requirements.';
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      return;
    }

    // Use memoized map for faster lookup
    const activeRow = rowMapRef.current.get(String(active.id));
    
    // Don't allow dragging PROJECT rows
    if (!activeRow || activeRow.type === 'PROJECT') {
      return;
    }

    // Check if dropping on a prerequisites cell FIRST (before source document check)
    // This ensures prerequisites are checked before source documents
    const overId = String(over.id);
    if (overId.startsWith('prereq-')) {
      // Extract the target row ID from the prerequisites cell ID
      const targetRowId = overId.replace('prereq-', '');
      const targetRow = rowMapRef.current.get(targetRowId);
      
      if (!targetRow || (targetRow.type !== 'TASK' && targetRow.type !== 'PHASE')) {
        // Only phases and tasks can have prerequisites
        return;
      }

      // Get the prerequisite ID to add
      let prereqId: number | null = null;
      if (activeRow.type === 'TASK' && targetRow.type === 'TASK') {
        // Task prerequisite for task
        prereqId = (activeRow.data as PlanningTask).taskId;
      } else if (activeRow.type === 'PHASE' && targetRow.type === 'PHASE') {
        // Phase prerequisite for phase
        prereqId = (activeRow.data as PlanningPhase).phaseId;
      } else if (activeRow.type === 'ACTION') {
        // Actions can't be prerequisites
        return;
      } else {
        // Can't mix phase and task prerequisites
        return;
      }

      if (!prereqId) {
        return;
      }

      // Add prerequisite
      const updatedPlan = { ...projectPlan };
      let shouldSave = false;
      let saveTargetId: number | null = null;
      let saveTargetType: 'TASK' | 'PHASE' | null = null;
      
      if (targetRow.type === 'TASK') {
        const targetTask = targetRow.data as PlanningTask;
        const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === targetRow.phaseId);
        
        if (phaseIndex >= 0) {
          const taskIndex = updatedPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === targetTask.taskId);
          if (taskIndex >= 0) {
            const task = updatedPlan.phases[phaseIndex].tasks[taskIndex];
            
            // Check if prerequisite already exists
            if (task.prerequisites.includes(prereqId!)) {
              setSnackbar({ open: true, message: 'Prerequisite already exists', severity: 'info' });
              return; // Already exists
            }

            // Add prerequisite - call backend API for validation
            const newPrerequisites = [...task.prerequisites, prereqId!];
            
            // Optimistically update UI
            updatedPlan.phases[phaseIndex].tasks[taskIndex].prerequisites = newPrerequisites;
            shouldSave = true;
            saveTargetId = targetTask.taskId;
            saveTargetType = 'TASK';
          }
        }
      } else if (targetRow.type === 'PHASE') {
        const targetPhase = targetRow.data as PlanningPhase;
        const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === targetPhase.phaseId);
        
        if (phaseIndex >= 0) {
          const phase = updatedPlan.phases[phaseIndex];
          const prereqs = (phase as any).prerequisites || [];
          
          // Check if prerequisite already exists
          if (prereqs.includes(prereqId!)) {
            setSnackbar({ open: true, message: 'Prerequisite already exists', severity: 'info' });
            return; // Already exists
          }

          // Add prerequisite
          (updatedPlan.phases[phaseIndex] as any).prerequisites = [...prereqs, prereqId!];
          shouldSave = true;
          saveTargetId = targetPhase.phaseId;
          saveTargetType = 'PHASE';
        }
      }
      
      if (shouldSave && saveTargetId && saveTargetType && projectPlan.projectId > 0) {
        // Call backend API to validate and persist prerequisite update
        // Use bulk endpoint for phases (individual endpoint doesn't support prerequisitePhaseIds)
        (async () => {
          try {
            const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
            const newPrerequisites = saveTargetType === 'TASK' 
              ? updatedPlan.phases.find(p => p.phaseId === targetRow.phaseId)?.tasks.find(t => t.taskId === saveTargetId)?.prerequisites || []
              : (updatedPlan.phases.find(p => p.phaseId === saveTargetId) as any)?.prerequisites || [];
            
            let response: Response;
            
            if (saveTargetType === 'PHASE') {
              // Use bulk endpoint for phases (individual endpoint doesn't support prerequisitePhaseIds)
              const requestBody = {
                updates: {
                  phases: [{
                    phaseId: saveTargetId,
                    prerequisitePhaseIds: newPrerequisites,
                  }]
                  }
              };
              
                            
              response = await fetch(`${API_BASE_URL}/api/projects/${projectPlan.projectId}/planning/bulk`, {
                method: 'PUT',
                headers: { 
                  'Content-Type': 'application/json',
                  ...createAuthHeaders(),
                },
                credentials: 'include',
                body: JSON.stringify(requestBody)
              });
            } else {
              // For tasks, use individual endpoint (it supports prerequisiteTaskIds)
              response = await fetch(`${API_BASE_URL}/api/tasks/${saveTargetId}`, {
                method: 'PUT',
                headers: { 
                  'Content-Type': 'application/json',
                  ...createAuthHeaders(),
                },
                credentials: 'include',
                body: JSON.stringify({
                  prerequisiteTaskIds: newPrerequisites,
                })
              });
            }
            
            if (!response.ok) {
              let error;
              try {
                error = await response.json();
              } catch (parseError) {
                // If response is not JSON, create a generic error
                const errorText = await response.text();
                console.error('Prerequisite update error (drag-and-drop, non-JSON response):', {
                  status: response.status,
                  statusText: response.statusText,
                  errorText
                });
                error = {
                  message: `Failed to update prerequisites: ${response.statusText}`,
                  code: 'VALIDATION_ERROR',
                };
              }
              
              console.error('Prerequisite update failed (drag-and-drop):', {
                status: response.status,
                error,
                errorMessage: error.message,
                errorCode: error.code,
                errorDetails: error.details,
                saveTargetType,
                saveTargetId,
                newPrerequisites
              });
              
              // Revert UI change
              setProjectPlan(prevPlan => {
                const newPlan = { ...prevPlan };
                if (saveTargetType === 'TASK') {
                  const phaseIndex = newPlan.phases.findIndex(p => p.phaseId === targetRow.phaseId);
                  if (phaseIndex >= 0) {
                    const taskIndex = newPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === saveTargetId);
                    if (taskIndex >= 0) {
                      // Revert to original prerequisites
                      const originalTask = projectPlan.phases.find(p => p.phaseId === targetRow.phaseId)?.tasks.find(t => t.taskId === saveTargetId);
                      if (originalTask) {
                        newPlan.phases[phaseIndex].tasks[taskIndex].prerequisites = originalTask.prerequisites;
                      }
                    }
                  }
                } else if (saveTargetType === 'PHASE') {
                  const phaseIndex = newPlan.phases.findIndex(p => p.phaseId === saveTargetId);
                  if (phaseIndex >= 0) {
                    const originalPhase = projectPlan.phases.find(p => p.phaseId === saveTargetId);
                    if (originalPhase) {
                      (newPlan.phases[phaseIndex] as any).prerequisites = (originalPhase as any).prerequisites || [];
                    }
                  }
                }
                return newPlan;
              });
              
              // Display backend error message
              if (error.code === 'PROJECT_LOCKED') {
                setBackendValidationError({
                  message: 'Project is locked. Prerequisite changes are disabled.',
                  code: 'PROJECT_LOCKED',
                });
                setSnackbar({ open: true, message: 'Project is locked. Prerequisite changes are disabled.', severity: 'error' });
                return;
              }
              
              if (error.code === 'CIRCULAR_DEPENDENCY') {
                const cyclePath = error.cyclePath || error.details?.cyclePath || [];
                setBackendValidationError({
                  message: `Circular dependency detected: ${cyclePath.join(' → ') || error.message}`,
                  code: 'CIRCULAR_DEPENDENCY',
                  details: {
                    cyclePath,
                    ...error.details,
                  },
                });
                setSnackbar({ 
                  open: true, 
                  message: `Circular dependency detected: ${cyclePath.join(' → ') || error.message}`, 
                  severity: 'error' 
                });
                return;
              }
              
              if (error.code === 'VALIDATION_ERROR') {
                const errorMessage = buildErrorMessage(error);
                setBackendValidationError({
                  message: errorMessage,
                  code: 'VALIDATION_ERROR',
                  details: error.details,
                });
                setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                return;
              }
              
              setBackendValidationError({
                message: error.message || 'Failed to update prerequisites',
                code: error.code,
              });
              setSnackbar({ open: true, message: error.message || 'Failed to update prerequisites', severity: 'error' });
              return;
            }
            
            // Check response body even on 200 OK - backend might return success=false
            if (saveTargetType === 'PHASE') {
              // For bulk endpoint, parse the response
              const result = await response.json();
              
                            
              // Check if the backend indicates failure
              // Note: result.updated.phases is a COUNT (number), not an array
              if (result.success === false || (result.updated && result.updated.phases === 0)) {
                // Check for specific error messages
                const errorMessage = result.message || 
                  (result.errors && result.errors.length > 0 
                    ? result.errors.map((e: any) => `${e.type} ${e.id}: ${e.message}`).join(', ')
                    : 'No updates were successful');
                
                console.error('Bulk update failed (drag-and-drop):', {
                  success: result.success,
                  updatedPhases: result.updated?.phases,
                  errors: result.errors,
                  errorMessage
                });
                
                setBackendValidationError({
                  message: errorMessage,
                  code: 'VALIDATION_ERROR',
                  details: result.errors?.[0] || result.details,
                });
                setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                return;
              }
              
              // Success - update local state
              // Note: result.updated.phases is a count, not an array, so we use what we sent
              setProjectPlan(prevPlan => {
                const newPlan = { ...prevPlan };
                const phaseIndex = newPlan.phases.findIndex(p => p.phaseId === saveTargetId);
                if (phaseIndex >= 0) {
                  // Use what we sent since backend doesn't return the updated phase data in the response
                  (newPlan.phases[phaseIndex] as any).prerequisites = newPrerequisites;
                }
                return newPlan;
              });
            } else {
              // For tasks, use individual endpoint response
              await response.json();
              setProjectPlan(prevPlan => {
                const newPlan = { ...prevPlan };
                const phaseIndex = newPlan.phases.findIndex(p => p.phaseId === targetRow.phaseId);
                if (phaseIndex >= 0) {
                  const taskIndex = newPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === saveTargetId);
                  if (taskIndex >= 0 && updatedPlan.phases[phaseIndex]) {
                    newPlan.phases[phaseIndex].tasks[taskIndex] = updatedPlan.phases[phaseIndex].tasks[taskIndex];
                  }
                }
                return newPlan;
              });
            }
            
            setBackendValidationError(null);
            setSnackbar({ 
              open: true, 
              message: 'Prerequisite added successfully', 
              severity: 'success' 
            });
            
          } catch (error) {
            console.error('Prerequisite update error:', error);
            // Revert UI change
            setProjectPlan(prevPlan => {
              const newPlan = { ...prevPlan };
              if (saveTargetType === 'TASK') {
                const phaseIndex = newPlan.phases.findIndex(p => p.phaseId === targetRow.phaseId);
                if (phaseIndex >= 0) {
                  const taskIndex = newPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === saveTargetId);
                  if (taskIndex >= 0) {
                    const originalTask = projectPlan.phases.find(p => p.phaseId === targetRow.phaseId)?.tasks.find(t => t.taskId === saveTargetId);
                    if (originalTask) {
                      newPlan.phases[phaseIndex].tasks[taskIndex].prerequisites = originalTask.prerequisites;
                    }
                  }
                }
              } else if (saveTargetType === 'PHASE') {
                const phaseIndex = newPlan.phases.findIndex(p => p.phaseId === saveTargetId);
                if (phaseIndex >= 0) {
                  const originalPhase = projectPlan.phases.find(p => p.phaseId === saveTargetId);
                  if (originalPhase) {
                    (newPlan.phases[phaseIndex] as any).prerequisites = (originalPhase as any).prerequisites || [];
                  }
                }
              }
              return newPlan;
            });
            setBackendValidationError({
              message: 'Failed to update prerequisites. Please try again.',
              code: 'NETWORK_ERROR',
            });
            setSnackbar({ open: true, message: 'Failed to update prerequisites. Please try again.', severity: 'error' });
          }
        })();
      }
      
      return;
    }

    // Check if dropping on a source document cell
    if (overId.startsWith('source-')) {
      // Extract the target row ID from the source cell ID
      const targetRowId = overId.replace('source-', '');
      const targetRow = rowMapRef.current.get(targetRowId);
      
      if (!targetRow || targetRow.type !== 'ACTION') {
        // Only REVISE, APPROVE, and REVIEW actions can have source documents
        return;
      }

      const targetAction = targetRow.data as PlanningAction;
      if (targetAction.actionType !== 'REVISE' && targetAction.actionType !== 'APPROVE' && targetAction.actionType !== 'REVIEW') {
        // Only REVISE, APPROVE, and REVIEW actions can have source documents
        return;
      }

      // Only ACTION rows can be dragged as source
      if (activeRow.type !== 'ACTION') {
        return;
      }

      const sourceAction = activeRow.data as PlanningAction;
      const sourceActionId = sourceAction.actionId;

      // Validate source action (F20.3)
      const validation = validateSourceAction(sourceActionId, targetAction.actionId, projectPlan);
      if (!validation.valid) {
        setSnackbar({ 
          open: true, 
          message: validation.error || 'Invalid source action', 
          severity: 'error' 
        });
        return;
      }

      // Check if source action is CREATE with multiple documents
      const sourceRequiredCount = (sourceAction as any).requiredDocumentCount;
      
      if (sourceAction.actionType === 'CREATE' && sourceRequiredCount && sourceRequiredCount > 1) {
        // CREATE action with multiple documents - show dialog to select specific document
        try {
          // Fetch documents from the source CREATE action
          const sourceDocuments = await apiService.getActionDocuments(sourceActionId);
          
          // Cache CREATE action documents for source document title lookup
          setCreateActionDocumentsCache(prev => {
            const newMap = new Map(prev);
            newMap.set(sourceActionId, sourceDocuments);
            return newMap;
          });
          
          // Show all documents (including placeholders) so user can select which one to reference
          // Even if they're placeholders, the user should be able to select them
          if (sourceDocuments.length === 0) {
            // No documents available yet - show message
            setSnackbar({
              open: true,
              message: 'Source CREATE action has no documents yet. Please create documents first.',
              severity: 'warning'
            });
            return;
          }
          
          // Store pending drag operation and show dialog
          setPendingDragOperation({
            sourceAction,
            targetAction,
            targetRowId,
          });
          setAvailableSourceDocuments(sourceDocuments);
          setDocumentSelectionDialogOpen(true);
        } catch (err: any) {
          console.error('Failed to fetch source documents:', err);
          setSnackbar({
            open: true,
            message: 'Failed to load source documents. Please try again.',
            severity: 'error'
          });
        }
        return;
        } else if (sourceAction.actionType === 'CREATE' && sourceRequiredCount === 1) {
        // Single document CREATE - auto-select the document
        try {
          const sourceDocuments = await apiService.getActionDocuments(sourceActionId);
          
          // Cache CREATE action documents for source document title lookup
          setCreateActionDocumentsCache(prev => {
            const newMap = new Map(prev);
            newMap.set(sourceActionId, sourceDocuments);
            return newMap;
          });
          
          if (sourceDocuments.length > 0) {
            const selectedDoc = sourceDocuments[0]; // Auto-select first (and only) document
            
            // Create document association
            const createdAssociations = await apiService.createActionDocuments(targetAction.actionId, [{
              documentType: 'SOURCE',
              sourceActionId: sourceActionId,
              sourceActionDocumentId: selectedDoc.actionDocumentId,
              documentNumber: selectedDoc.documentNumber || 1,
              isRequired: true,
            }]);
            
            // Update cache with new associations for source document display
            setSourceDocumentAssociationsCache(prev => {
              const newMap = new Map(prev);
              newMap.set(targetAction.actionId, createdAssociations);
              return newMap;
            });
            
            // Also update sourceActionId on action for backward compatibility
            setProjectPlan(prevPlan => {
              const updatedPlan = { ...prevPlan };
              const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === targetRow.phaseId);
              
              if (phaseIndex >= 0) {
                const taskIndex = updatedPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === targetRow.taskId);
                if (taskIndex >= 0) {
                  const actionIndex = updatedPlan.phases[phaseIndex].tasks[taskIndex].actions.findIndex(
                    a => a.actionId === targetAction.actionId
                  );
                  if (actionIndex >= 0) {
                    updatedPlan.phases[phaseIndex].tasks[taskIndex].actions[actionIndex].sourceActionId = sourceActionId;
                  }
                }
              }
              return updatedPlan;
            });
            
            setSnackbar({
              open: true,
              message: 'Source document set. Click Save to persist changes.',
              severity: 'success'
            });
            return;
          } else {
            setSnackbar({
              open: true,
              message: 'Source CREATE action has no documents yet. Please create documents first.',
              severity: 'warning'
            });
            return;
          }
        } catch (err: any) {
          console.error('Failed to create document association:', err);
          setSnackbar({
            open: true,
            message: err.message || 'Failed to set source document. Please try again.',
            severity: 'error'
          });
          return;
        }
      } else {
        // Non-CREATE action - use existing behavior (just set sourceActionId)
        setProjectPlan(prevPlan => {
          const updatedPlan = { ...prevPlan };
          const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === targetRow.phaseId);
          
          if (phaseIndex >= 0) {
            const taskIndex = updatedPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === targetRow.taskId);
            if (taskIndex >= 0) {
              const actionIndex = updatedPlan.phases[phaseIndex].tasks[taskIndex].actions.findIndex(
                a => a.actionId === targetAction.actionId
              );
              if (actionIndex >= 0) {
                updatedPlan.phases[phaseIndex].tasks[taskIndex].actions[actionIndex].sourceActionId = sourceActionId;
              }
            }
          }
          return updatedPlan;
        });

        // Show success message (optimistic update - changes stored locally)
        setSnackbar({ 
          open: true, 
          message: 'Source document set. Click Save to persist changes.', 
          severity: 'success' 
        });
        
        return;
      }
    }

    // Regular reordering logic - call backend API for validation
    const overRow = rowMapRef.current.get(String(over.id));
    
    if (!overRow) {
      return;
    }

    // Only allow reordering within the same level and parent
    if (activeRow.type !== overRow.type) {
      return;
    }

    // For tasks, ensure they're in the same phase
    if (activeRow.type === 'TASK' && activeRow.phaseId !== overRow.phaseId) {
      return;
    }

    // For actions, ensure they're in the same task
    if (activeRow.type === 'ACTION' && activeRow.taskId !== overRow.taskId) {
      return;
    }

    // Store current state for revert
    const currentPlan = JSON.parse(JSON.stringify(projectPlan));
    
    // Calculate new position and optimistically update UI
    let entityId: number | undefined;
    let entityType: 'phases' | 'tasks' | 'actions' | undefined;
    let newPosition: number | undefined;
    let affectedPhases: PlanningPhase[] | undefined;
    let affectedTasks: PlanningTask[] | undefined;
    let affectedTasksPhaseId: number | undefined;
    let affectedActions: PlanningAction[] | undefined;
    let affectedActionsTaskId: number | undefined;
    
    // For phases, calculate affected phases BEFORE state update
    if (activeRow.type === 'PHASE') {
      const activeIndex = projectPlan.phases.findIndex(p => p.phaseId === (activeRow.data as PlanningPhase).phaseId);
      const overIndex = projectPlan.phases.findIndex(p => p.phaseId === (overRow.data as PlanningPhase).phaseId);
      
      if (activeIndex >= 0 && overIndex >= 0) {
        // Calculate which phases will be affected (all phases between min and max index)
        const minIndex = Math.min(activeIndex, overIndex);
        const maxIndex = Math.max(activeIndex, overIndex);
        // Create a copy to calculate the new order
        const phasesCopy = [...projectPlan.phases];
        const reorderedPhases = arrayMove(phasesCopy, activeIndex, overIndex);
        // Store affected phases with their new display orders
        affectedPhases = reorderedPhases.slice(minIndex, maxIndex + 1).map((phase, idx) => ({
          ...phase,
          displayOrder: minIndex + idx + 1
        }));
      }
    } else if (activeRow.type === 'TASK') {
      // For tasks, calculate affected tasks BEFORE state update
      const phaseIndex = projectPlan.phases.findIndex(p => p.phaseId === activeRow.phaseId);
      if (phaseIndex >= 0) {
        const phase = projectPlan.phases[phaseIndex];
        const activeTaskIndex = phase.tasks.findIndex(t => t.taskId === (activeRow.data as PlanningTask).taskId);
        const overTaskIndex = phase.tasks.findIndex(t => t.taskId === (overRow.data as PlanningTask).taskId);
        
        if (activeTaskIndex >= 0 && overTaskIndex >= 0) {
          // Calculate which tasks will be affected (all tasks between min and max index)
          const minIndex = Math.min(activeTaskIndex, overTaskIndex);
          const maxIndex = Math.max(activeTaskIndex, overTaskIndex);
          // Create a copy to calculate the new order
          const tasksCopy = [...phase.tasks];
          const reorderedTasks = arrayMove(tasksCopy, activeTaskIndex, overTaskIndex);
          // Store affected tasks with their new display orders
          affectedTasks = reorderedTasks.slice(minIndex, maxIndex + 1).map((task, idx) => ({
            ...task,
            displayOrder: minIndex + idx + 1
          }));
          affectedTasksPhaseId = phase.phaseId;
        }
      }
    } else if (activeRow.type === 'ACTION') {
      // For actions, calculate affected actions BEFORE state update
      const phaseIndex = projectPlan.phases.findIndex(p => p.phaseId === activeRow.phaseId);
      if (phaseIndex >= 0) {
        const taskIndex = projectPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === activeRow.taskId);
        if (taskIndex >= 0) {
          const task = projectPlan.phases[phaseIndex].tasks[taskIndex];
          const activeActionIndex = task.actions.findIndex(a => a.actionId === (activeRow.data as PlanningAction).actionId);
          const overActionIndex = task.actions.findIndex(a => a.actionId === (overRow.data as PlanningAction).actionId);
          
          if (activeActionIndex >= 0 && overActionIndex >= 0) {
            // Calculate which actions will be affected (all actions between min and max index)
            const minIndex = Math.min(activeActionIndex, overActionIndex);
            const maxIndex = Math.max(activeActionIndex, overActionIndex);
            // Create a copy to calculate the new order
            const actionsCopy = [...task.actions];
            const reorderedActions = arrayMove(actionsCopy, activeActionIndex, overActionIndex);
            // Store affected actions with their new display orders
            affectedActions = reorderedActions.slice(minIndex, maxIndex + 1).map((action, idx) => ({
              ...action,
              displayOrder: minIndex + idx + 1
            }));
            affectedActionsTaskId = task.taskId;
          }
        }
      }
    }
    
    // Optimistically update UI first
    setProjectPlan(prevPlan => {
      const updatedPlan = { ...prevPlan };
      
      if (activeRow.type === 'PHASE') {
        // Reorder phases
        const activeIndex = updatedPlan.phases.findIndex(p => p.phaseId === (activeRow.data as PlanningPhase).phaseId);
        const overIndex = updatedPlan.phases.findIndex(p => p.phaseId === (overRow.data as PlanningPhase).phaseId);
        
        if (activeIndex >= 0 && overIndex >= 0) {
          const reorderedPhases = arrayMove(updatedPlan.phases, activeIndex, overIndex);
          reorderedPhases.forEach((phase, index) => {
            phase.displayOrder = index + 1;
          });
          updatedPlan.phases = reorderedPhases;
          
          entityId = (activeRow.data as PlanningPhase).phaseId;
          entityType = 'phases';
          newPosition = overIndex + 1;
        }
      } else if (activeRow.type === 'TASK') {
        // Reorder tasks within the same phase
        const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === activeRow.phaseId);
        if (phaseIndex >= 0) {
          const phase = updatedPlan.phases[phaseIndex];
          const activeTaskIndex = phase.tasks.findIndex(t => t.taskId === (activeRow.data as PlanningTask).taskId);
          const overTaskIndex = phase.tasks.findIndex(t => t.taskId === (overRow.data as PlanningTask).taskId);
          
          if (activeTaskIndex >= 0 && overTaskIndex >= 0) {
            const reorderedTasks = arrayMove(phase.tasks, activeTaskIndex, overTaskIndex);
            reorderedTasks.forEach((task, index) => {
              task.displayOrder = index + 1;
            });
            updatedPlan.phases[phaseIndex].tasks = reorderedTasks;
            
            entityId = (activeRow.data as PlanningTask).taskId;
            entityType = 'tasks';
            newPosition = overTaskIndex + 1;
          }
        }
      } else if (activeRow.type === 'ACTION') {
        // Reorder actions within the same task
        const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === activeRow.phaseId);
        if (phaseIndex >= 0) {
          const taskIndex = updatedPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === activeRow.taskId);
          if (taskIndex >= 0) {
            const task = updatedPlan.phases[phaseIndex].tasks[taskIndex];
            const activeActionIndex = task.actions.findIndex(a => a.actionId === (activeRow.data as PlanningAction).actionId);
            const overActionIndex = task.actions.findIndex(a => a.actionId === (overRow.data as PlanningAction).actionId);
            
            if (activeActionIndex >= 0 && overActionIndex >= 0) {
              const reorderedActions = arrayMove(task.actions, activeActionIndex, overActionIndex);
              reorderedActions.forEach((action, index) => {
                action.displayOrder = index + 1;
              });
              updatedPlan.phases[phaseIndex].tasks[taskIndex].actions = reorderedActions;
              
              entityId = (activeRow.data as PlanningAction).actionId;
              entityType = 'actions';
              newPosition = overActionIndex + 1;
            }
          }
        }
      }
      
      return updatedPlan;
    });

    // Call backend API to validate and persist the reorder
    if (entityId && entityType && newPosition !== undefined && projectPlan.projectId > 0) {
      // Create revert function
      const revertDragOperation = () => {
        setProjectPlan(currentPlan);
      };
      
      (async () => {
        try {
          const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
          
          // If reordering phases, use bulk update endpoint for efficiency
          if (entityType === 'phases' && affectedPhases && Array.isArray(affectedPhases)) {
            // Use bulk update endpoint to update all affected phases atomically
            const updates = {
              phases: affectedPhases.map((phase: PlanningPhase) => ({
                phaseId: phase.phaseId,
                displayOrder: phase.displayOrder,
              }))
            };
            
                        
            const response = await fetch(`${API_BASE_URL}/api/projects/${projectPlan.projectId}/planning/bulk`, {
              method: 'PUT',
              headers: { 
                'Content-Type': 'application/json',
                ...createAuthHeaders(),
              },
              credentials: 'include',
              body: JSON.stringify({ updates })
            });
            
            if (!response.ok) {
              let error;
              try {
                error = await response.json();
              } catch (parseError) {
                // If response is not JSON, create a generic error
                const errorText = await response.text();
                console.error('Bulk update error (non-JSON response):', errorText);
                error = {
                  message: `Failed to reorder: ${response.statusText}`,
                  code: 'VALIDATION_ERROR',
                };
              }
              
              // Revert UI change
              revertDragOperation();
              
              // Display backend error message
              if (error.code === 'PROJECT_LOCKED') {
                setBackendValidationError({
                  message: 'Project is locked. Reordering is disabled.',
                  code: 'PROJECT_LOCKED',
                });
                setSnackbar({ open: true, message: 'Project is locked. Reordering is disabled.', severity: 'error' });
                return;
              }
              
              if (error.code === 'VALIDATION_ERROR') {
                const errorMessage = buildErrorMessage(error);
                setBackendValidationError({
                  message: errorMessage,
                  code: 'VALIDATION_ERROR',
                  details: error.details,
                });
                setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                return;
              }
              
              setBackendValidationError({
                message: error.message || 'Failed to reorder',
                code: error.code,
              });
              setSnackbar({ open: true, message: error.message || 'Failed to reorder', severity: 'error' });
              return;
            }
            
            // Success - update local state with response if needed
            const result = await response.json();
            if (result.updated?.phases) {
              // Update local state with backend response to ensure consistency
              setProjectPlan(prevPlan => {
                const updatedPlan = { ...prevPlan };
                result.updated.phases.forEach((updatedPhase: any) => {
                  const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === updatedPhase.phaseId);
                  if (phaseIndex >= 0) {
                    updatedPlan.phases[phaseIndex].displayOrder = updatedPhase.displayOrder;
                  }
                });
                return updatedPlan;
              });
            }
            
            // Success - clear errors
            setBackendValidationError(null);
            setSnackbar({ open: true, message: 'Reordered successfully', severity: 'success' });
            return;
          }
          
          // If reordering tasks, use bulk update endpoint for efficiency
          if (entityType === 'tasks' && affectedTasks && Array.isArray(affectedTasks) && affectedTasksPhaseId) {
            // Use bulk update endpoint to update all affected tasks atomically
            const updates = {
              tasks: affectedTasks.map((task: PlanningTask) => ({
                taskId: task.taskId,
                displayOrder: task.displayOrder,
              }))
            };
            
            const response = await fetch(`${API_BASE_URL}/api/projects/${projectPlan.projectId}/planning/bulk`, {
              method: 'PUT',
              headers: { 
                'Content-Type': 'application/json',
                ...createAuthHeaders(),
              },
              credentials: 'include',
              body: JSON.stringify({ updates })
            });
            
            if (!response.ok) {
              const error = await response.json();
              
              // Revert UI change
              revertDragOperation();
              
              // Display backend error message
              if (error.code === 'PROJECT_LOCKED') {
                setBackendValidationError({
                  message: 'Project is locked. Reordering is disabled.',
                  code: 'PROJECT_LOCKED',
                });
                setSnackbar({ open: true, message: 'Project is locked. Reordering is disabled.', severity: 'error' });
                return;
              }
              
              if (error.code === 'VALIDATION_ERROR') {
                const errorMessage = buildErrorMessage(error);
                setBackendValidationError({
                  message: errorMessage,
                  code: 'VALIDATION_ERROR',
                  details: error.details,
                });
                setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                return;
              }
              
              setBackendValidationError({
                message: error.message || 'Failed to reorder',
                code: error.code,
              });
              setSnackbar({ open: true, message: error.message || 'Failed to reorder', severity: 'error' });
              return;
            }
            
            // Success - clear errors
            setBackendValidationError(null);
            setSnackbar({ open: true, message: 'Reordered successfully', severity: 'success' });
            return;
          }
          
          // If reordering actions, use bulk update endpoint for efficiency
          if (entityType === 'actions' && affectedActions && Array.isArray(affectedActions) && affectedActionsTaskId) {
            // Use bulk update endpoint to update all affected actions atomically
            const updates = {
              actions: affectedActions.map((action: PlanningAction) => ({
                actionId: action.actionId,
                displayOrder: action.displayOrder,
              }))
            };
            
            const response = await fetch(`${API_BASE_URL}/api/projects/${projectPlan.projectId}/planning/bulk`, {
              method: 'PUT',
              headers: { 
                'Content-Type': 'application/json',
                ...createAuthHeaders(),
              },
              credentials: 'include',
              body: JSON.stringify({ updates })
            });
            
            if (!response.ok) {
              const error = await response.json();
              
              // Revert UI change
              revertDragOperation();
              
              // Display backend error message
              if (error.code === 'PROJECT_LOCKED') {
                setBackendValidationError({
                  message: 'Project is locked. Reordering is disabled.',
                  code: 'PROJECT_LOCKED',
                });
                setSnackbar({ open: true, message: 'Project is locked. Reordering is disabled.', severity: 'error' });
                return;
              }
              
              if (error.code === 'VALIDATION_ERROR') {
                const errorMessage = buildErrorMessage(error);
                setBackendValidationError({
                  message: errorMessage,
                  code: 'VALIDATION_ERROR',
                  details: error.details,
                });
                setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                return;
              }
              
              setBackendValidationError({
                message: error.message || 'Failed to reorder',
                code: error.code,
              });
              setSnackbar({ open: true, message: error.message || 'Failed to reorder', severity: 'error' });
              return;
            }
            
            // Success - clear errors
            setBackendValidationError(null);
            setSnackbar({ open: true, message: 'Reordered successfully', severity: 'success' });
            return;
          }
          
          // Fallback for any other entity types (shouldn't happen, but keep for safety)
          const response = await fetch(`${API_BASE_URL}/api/${entityType}/${entityId}`, {
            method: 'PUT',
            headers: { 
              'Content-Type': 'application/json',
              ...createAuthHeaders(),
            },
            credentials: 'include',
            body: JSON.stringify({
              displayOrder: newPosition,
            })
          });
          
          if (!response.ok) {
            const error = await response.json();
            
            // Revert UI change
            if (revertDragOperation) {
              revertDragOperation();
            }
            
            // Display backend error message
            if (error.code === 'PROJECT_LOCKED') {
              setBackendValidationError({
                message: 'Project is locked. Reordering is disabled.',
                code: 'PROJECT_LOCKED',
              });
              setSnackbar({ open: true, message: 'Project is locked. Reordering is disabled.', severity: 'error' });
              return;
            }
            
              if (error.code === 'VALIDATION_ERROR') {
                const errorMessage = buildErrorMessage(error);
                setBackendValidationError({
                  message: errorMessage,
                  code: 'VALIDATION_ERROR',
                  details: error.details,
                });
                setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                
                // Show which entity and dependency caused failure
                if (error.details) {
                  console.error('Validation failed:', {
                    entityType: error.details.entityType,
                    entityName: error.details.entityName,
                    reason: error.details.reason
                  });
                }
                return;
              }
            
            // Handle other errors
            setBackendValidationError({
              message: error.message || 'Failed to reorder',
              code: error.code,
            });
            setSnackbar({ open: true, message: error.message || 'Failed to reorder', severity: 'error' });
            return;
          }
          
          // Success - update local state with response if needed
          await response.json();
          // The optimistic update already handled the UI, so we just clear errors
          setBackendValidationError(null);
          setSnackbar({ open: true, message: 'Reordered successfully', severity: 'success' });
          
        } catch (error) {
          console.error('Reorder error:', error);
          // Revert UI change
          if (revertDragOperation) {
            revertDragOperation();
          }
          setBackendValidationError({
            message: 'Failed to reorder. Please try again.',
            code: 'NETWORK_ERROR',
          });
          setSnackbar({ open: true, message: 'Failed to reorder. Please try again.', severity: 'error' });
        }
      })();
    }
  };

  const handleCloseContextMenu = useCallback(() => {
    // No-op: row actions use inline buttons; keep for any future context menu.
  }, []);

  const handleInsertRow = async (rowType: RowType, afterRow?: GridRow) => {
    if (!projectPlan.projectId || projectPlan.projectId <= 0) {
      setSnackbar({
        open: true,
        message: 'Please save the project first before adding items',
        severity: 'warning',
      });
      return;
    }

    try {
      if (rowType === 'PHASE') {
        // Create phase via API
        const calculatedDisplayOrder = projectPlan.phases.length + 1;
        const newPhase = await createPhase(projectPlan.projectId, {
          name: `Phase ${calculatedDisplayOrder}`,
          description: null,
          displayOrder: calculatedDisplayOrder,
          plannedStartDate: null,
          plannedEndDate: null,
          plannedDurationHours: 0,
        });
        
        // Validate displayOrder returned from backend
        // Backend has been fixed to accept and persist displayOrder on create
        // This validation remains as defensive programming
        if (newPhase.displayOrder !== calculatedDisplayOrder || newPhase.displayOrder <= 0) {
          console.warn('⚠️ Backend returned unexpected displayOrder for phase, correcting:', {
            phaseId: newPhase.phaseId,
            sentDisplayOrder: calculatedDisplayOrder,
            backendDisplayOrder: newPhase.displayOrder,
            correctingTo: calculatedDisplayOrder,
            note: 'Backend should persist displayOrder correctly. If this persists, check database triggers/defaults.',
          });
          try {
            const updatedPhase = await updatePhase(newPhase.phaseId, {
              displayOrder: calculatedDisplayOrder,
            });
            newPhase.displayOrder = updatedPhase.displayOrder || calculatedDisplayOrder;
                      } catch (updateError) {
            console.error(`❌ Failed to correct displayOrder for phase ${newPhase.phaseId}:`, updateError);
          }
        }
        
        // Automatically create a task and action for the new phase
        // This ensures the phase is valid (has at least 1 task with 1 action)
        const newTask = await createTask(newPhase.phaseId, {
          name: 'New Task',
          description: null,
          displayOrder: 1,
          plannedStartDate: null,
          plannedEndDate: null,
          plannedDurationHours: 0,
          prerequisiteTaskIds: [],
          parallelExecution: false,
        });
        
        // Automatically create an action for the new task
        const newAction = await createAction(newTask.taskId, {
          actionType: 'CREATE',
          durationHours: 8, // Default 8 hours
          notes: null,
          displayOrder: 1,
          plannedStartDate: null,
          plannedEndDate: null,
          plannedDurationHours: 8,
        });
        
        // Update state with server-assigned IDs
        // Always use calculated displayOrder to ensure correct order
        setProjectPlan(prevPlan => {
          const updatedPlan = { ...prevPlan };
          updatedPlan.phases.push({
            phaseId: newPhase.phaseId,
            name: newPhase.name,
            description: newPhase.description || '',
            startDate: newPhase.plannedStartDate,
            endDate: newPhase.plannedEndDate,
            duration: newPhase.plannedDurationHours || 0,
            tasks: [{
              taskId: newTask.taskId,
              name: newTask.name,
              description: newTask.description || '',
              assignedUsers: [],
              startDate: newTask.plannedStartDate,
              endDate: newTask.plannedEndDate,
              duration: newTask.plannedDurationHours || 0,
              prerequisites: newTask.prerequisites || [],
              parallelExecution: newTask.parallelExecution,
              actions: [{
                actionId: newAction.actionId,
                actionType: newAction.actionType,
                duration: newAction.duration || 8,
                assignedTo: [],
                notes: newAction.notes || '',
                displayOrder: 1,
                plannedStartDate: newAction.plannedStartDate,
                plannedEndDate: newAction.plannedEndDate,
              }],
              displayOrder: 1,
            }],
            displayOrder: calculatedDisplayOrder, // Use calculated value, not backend response
          });
          // Sort phases by displayOrder to ensure correct order
          updatedPlan.phases.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
          return calculateDurations(updatedPlan);
        });
        
        // Track the newly created action to suppress validation errors
        setNewlyCreatedActions(prev => new Set(prev).add(newAction.actionId));
        
        setSnackbar({ open: true, message: 'Phase created with task and action', severity: 'success' });
      } else if (rowType === 'TASK' && afterRow && afterRow.type === 'PHASE') {
        const phase = afterRow.data as PlanningPhase;
        if (phase.phaseId <= 0) {
          setSnackbar({
            open: true,
            message: 'Please save the phase first before adding tasks',
            severity: 'warning',
          });
          return;
        }
        
        // Create task via API
        const calculatedDisplayOrder = phase.tasks.length + 1;
        const newTask = await createTask(phase.phaseId, {
          name: 'New Task',
          description: null,
          displayOrder: calculatedDisplayOrder,
          plannedStartDate: null,
          plannedEndDate: null,
          plannedDurationHours: 0,
          prerequisiteTaskIds: [], // Backend expects prerequisiteTaskIds (not prerequisites)
          parallelExecution: false,
        });
        
        // Validate displayOrder returned from backend
        // Backend has been fixed to accept and persist displayOrder on create
        // This validation remains as defensive programming
        if (newTask.displayOrder !== calculatedDisplayOrder || newTask.displayOrder <= 0) {
          console.warn('⚠️ Backend returned unexpected displayOrder for task, correcting:', {
            taskId: newTask.taskId,
            sentDisplayOrder: calculatedDisplayOrder,
            backendDisplayOrder: newTask.displayOrder,
            correctingTo: calculatedDisplayOrder,
            note: 'Backend should persist displayOrder correctly. If this persists, check database triggers/defaults.',
          });
          try {
            const updatedTask = await updateTask(newTask.taskId, {
              displayOrder: calculatedDisplayOrder,
            });
            newTask.displayOrder = updatedTask.displayOrder || calculatedDisplayOrder;
                      } catch (updateError) {
            console.error(`❌ Failed to correct displayOrder for task ${newTask.taskId}:`, updateError);
          }
        }
        
        // Automatically create an action for the new task
        // This ensures the task is valid (has at least 1 action)
        const newAction = await createAction(newTask.taskId, {
          actionType: 'CREATE',
          durationHours: 8, // Default 8 hours
          notes: null,
          displayOrder: 1,
          plannedStartDate: null,
          plannedEndDate: null,
          plannedDurationHours: 8,
        });
        
        // Update state with server-assigned IDs
        // Always use calculated displayOrder to ensure correct order
        setProjectPlan(prevPlan => {
          const updatedPlan = { ...prevPlan };
        const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === phase.phaseId);
        if (phaseIndex >= 0) {
            updatedPlan.phases[phaseIndex].tasks.push({
              taskId: newTask.taskId,
              name: newTask.name,
              description: newTask.description || '',
            assignedUsers: [],
              startDate: newTask.plannedStartDate,
              endDate: newTask.plannedEndDate,
              duration: newTask.plannedDurationHours || 0,
              prerequisites: newTask.prerequisites || [],
              parallelExecution: newTask.parallelExecution,
              actions: [{
                actionId: newAction.actionId,
                actionType: newAction.actionType,
                duration: newAction.duration || 8,
                assignedTo: [],
                notes: newAction.notes || '',
                displayOrder: 1,
                plannedStartDate: newAction.plannedStartDate,
                plannedEndDate: newAction.plannedEndDate,
              }],
              displayOrder: calculatedDisplayOrder, // Use calculated value, not backend response
            });
            // Sort tasks by displayOrder to ensure correct order
            updatedPlan.phases[phaseIndex].tasks.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
        }
          return calculateDurations(updatedPlan);
        });
        
        // Track the newly created action to suppress validation errors
        setNewlyCreatedActions(prev => new Set(prev).add(newAction.actionId));
        
        setSnackbar({ open: true, message: 'Task created with action', severity: 'success' });
      } else if (rowType === 'ACTION' && afterRow && afterRow.type === 'TASK') {
        const task = afterRow.data as PlanningTask;
        if (task.taskId <= 0) {
          setSnackbar({
            open: true,
            message: 'Please save the task first before adding actions',
            severity: 'warning',
          });
          return;
        }
        
        // Create action via API
        // Calculate display order from max existing display order + 1 to avoid conflicts
        // This handles cases where actions were deleted and backend hasn't reordered
        // IMPORTANT: Filter out negative displayOrder values (backend errors) and use 0 as minimum
        const validDisplayOrders = task.actions
          .map(a => a.displayOrder || 0)
          .filter(order => order > 0); // Only consider positive displayOrder values
        const maxDisplayOrder = validDisplayOrders.length > 0 
          ? Math.max(...validDisplayOrders)
          : 0;
        const newDisplayOrder = maxDisplayOrder + 1;
        
        const newAction = await createAction(task.taskId, {
          actionType: 'CREATE',
          durationHours: 8, // Backend requires this field name (required) - default 8 hours
          notes: null,
          displayOrder: newDisplayOrder,
          plannedStartDate: null,
          plannedEndDate: null,
          plannedDurationHours: 8,
        });
        
        // Always use the displayOrder we calculated to ensure new actions appear at the bottom
        // The backend should return the same value, but if it doesn't, we use our calculated value
        // This prevents new actions from appearing in the wrong position when the project is reloaded
        const finalDisplayOrder = newDisplayOrder;
        
        // Validate displayOrder returned from backend
        // Backend has been fixed to accept and persist displayOrder on create
        // This validation remains as defensive programming in case of database-level issues (triggers, defaults)
        if (newAction.displayOrder !== newDisplayOrder || newAction.displayOrder <= 0) {
          console.warn('⚠️ Backend returned unexpected displayOrder, correcting:', {
            actionId: newAction.actionId,
            sentDisplayOrder: newDisplayOrder,
            backendDisplayOrder: newAction.displayOrder,
            correctingTo: newDisplayOrder,
            existingActions: task.actions.map(a => ({ actionId: a.actionId, displayOrder: a.displayOrder })),
            note: 'Backend should persist displayOrder correctly. If this persists, check database triggers/defaults.',
          });
          
          // Update the action's displayOrder immediately to fix it
          // This is defensive - backend should handle this correctly now
          try {
            const updatedAction = await updateAction(newAction.actionId, {
              displayOrder: newDisplayOrder,
            });
                        // Update newAction with corrected displayOrder for consistency
            newAction.displayOrder = updatedAction.displayOrder || newDisplayOrder;
          } catch (updateError) {
            console.error(`❌ Failed to correct displayOrder for action ${newAction.actionId}:`, updateError);
            // Continue anyway - we'll use the correct displayOrder in state
            // The bulk save will also send the correct displayOrder
          }
        }
        
        // Update state with server-assigned ID
        setProjectPlan(prevPlan => {
          const updatedPlan = { ...prevPlan };
        const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === afterRow.phaseId);
        if (phaseIndex >= 0) {
          const taskIndex = updatedPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === task.taskId);
          if (taskIndex >= 0) {
              updatedPlan.phases[phaseIndex].tasks[taskIndex].actions.push({
                actionId: newAction.actionId,
                actionType: newAction.actionType,
                duration: newAction.duration || 8, // Ensure duration is set (default to 8 hours if undefined)
              assignedTo: [],
                notes: newAction.notes || '',
                displayOrder: finalDisplayOrder, // Use our calculated displayOrder to ensure it's at the bottom
                plannedStartDate: newAction.plannedStartDate,
                plannedEndDate: newAction.plannedEndDate,
              });
              // Sort actions by displayOrder to ensure correct order (new action should be at bottom)
              updatedPlan.phases[phaseIndex].tasks[taskIndex].actions.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            }
          }
      return calculateDurations(updatedPlan);
    });
        
        // Track this as a newly created action to suppress validation errors
        setNewlyCreatedActions(prev => new Set(prev).add(newAction.actionId));
        
        setSnackbar({ open: true, message: 'Action created successfully', severity: 'success' });
      }
    } catch (error) {
      const apiError = error as PlanningExecutionApiError;
      setSnackbar({
        open: true,
        message: apiError.message || 'Failed to create item',
        severity: 'error',
      });
      console.error('Error creating item:', error);
    }
    
    handleCloseContextMenu();
  };

  const handleDeleteRow = async (row: GridRow) => {
    // Validate that we're not deleting the last item
    if (row.type === 'PHASE' && projectPlan.phases.length <= 1) {
      setSnackbar({
        open: true,
        message: 'Project must have at least one phase',
        severity: 'error',
      });
      return;
    } else if (row.type === 'TASK') {
      const phase = projectPlan.phases.find(p => p.phaseId === row.phaseId);
      if (phase && phase.tasks.length <= 1) {
        setSnackbar({
          open: true,
          message: 'Phase must have at least one task',
          severity: 'error',
        });
        return;
      }
    } else if (row.type === 'ACTION') {
      const phase = projectPlan.phases.find(p => p.phaseId === row.phaseId);
      if (phase) {
        const task = phase.tasks.find(t => t.taskId === row.taskId);
        if (task && task.actions.length <= 1) {
          setSnackbar({
            open: true,
            message: 'Task must have at least one action',
            severity: 'error',
          });
          return;
        }
      }
    }

    if (!window.confirm(`Are you sure you want to delete this ${row.type}?`)) {
      return;
    }

    // If it's a temporary ID (negative), just remove from state
    const itemId = row.type === 'PHASE' 
      ? (row.data as PlanningPhase).phaseId
      : row.type === 'TASK'
      ? (row.data as PlanningTask).taskId
      : (row.data as PlanningAction).actionId;
    
    if (itemId <= 0) {
      // Temporary item, just remove from state
    setProjectPlan(prevPlan => {
      const updatedPlan = { ...prevPlan };
      
      if (row.type === 'PHASE') {
          updatedPlan.phases = updatedPlan.phases.filter(p => p.phaseId !== itemId);
        updatedPlan.phases.forEach((p, index) => {
          p.displayOrder = index + 1;
        });
      } else if (row.type === 'TASK') {
        const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === row.phaseId);
        if (phaseIndex >= 0) {
            updatedPlan.phases[phaseIndex].tasks = updatedPlan.phases[phaseIndex].tasks.filter(t => t.taskId !== itemId);
          updatedPlan.phases[phaseIndex].tasks.forEach((t, index) => {
            t.displayOrder = index + 1;
          });
        }
      } else if (row.type === 'ACTION') {
        const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === row.phaseId);
        if (phaseIndex >= 0) {
          const taskIndex = updatedPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === row.taskId);
          if (taskIndex >= 0) {
              updatedPlan.phases[phaseIndex].tasks[taskIndex].actions = updatedPlan.phases[phaseIndex].tasks[taskIndex].actions.filter(a => a.actionId !== itemId);
            updatedPlan.phases[phaseIndex].tasks[taskIndex].actions.forEach((a, index) => {
              a.displayOrder = index + 1;
            });
          }
        }
      }
      
      return calculateDurations(updatedPlan);
    });
      return;
    }

    // Delete via API for persisted items
    try {
      if (row.type === 'PHASE') {
        await deletePhase(itemId);
      } else if (row.type === 'TASK') {
        await deleteTask(itemId);
      } else if (row.type === 'ACTION') {
        await deleteAction(itemId);
      }
      
      // Reload project plan from backend after deletion to ensure state sync
      // This is especially important for actions since backend may not reorder display orders
      // and we need accurate state for calculating new display orders
      const projectId = projectPlan.projectId;
      if (projectId) {
        try {
          const reloadedPlan = await loadProjectPlan(projectId);
          const recalculatedPlan = calculateDurations(reloadedPlan);
          setProjectPlan(recalculatedPlan);
        } catch (reloadError) {
          console.error('Failed to reload project plan after deletion:', reloadError);
          // Fallback to local state update if reload fails
          setProjectPlan(prevPlan => {
            const updatedPlan = { ...prevPlan };
            
            if (row.type === 'PHASE') {
              updatedPlan.phases = updatedPlan.phases.filter(p => p.phaseId !== itemId);
              updatedPlan.phases.forEach((p, index) => {
                p.displayOrder = index + 1;
              });
            } else if (row.type === 'TASK') {
              const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === row.phaseId);
              if (phaseIndex >= 0) {
                updatedPlan.phases[phaseIndex].tasks = updatedPlan.phases[phaseIndex].tasks.filter(t => t.taskId !== itemId);
                updatedPlan.phases[phaseIndex].tasks.forEach((t, index) => {
                  t.displayOrder = index + 1;
                });
              }
            } else if (row.type === 'ACTION') {
              const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === row.phaseId);
              if (phaseIndex >= 0) {
                const taskIndex = updatedPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === row.taskId);
                if (taskIndex >= 0) {
                  updatedPlan.phases[phaseIndex].tasks[taskIndex].actions = updatedPlan.phases[phaseIndex].tasks[taskIndex].actions.filter(a => a.actionId !== itemId);
                  updatedPlan.phases[phaseIndex].tasks[taskIndex].actions.forEach((a, index) => {
                    a.displayOrder = index + 1;
                  });
                }
              }
            }
            
            return calculateDurations(updatedPlan);
          });
        }
      }
      
      setSnackbar({ open: true, message: `${row.type} deleted successfully`, severity: 'success' });
    } catch (error) {
      const apiError = error as PlanningExecutionApiError;
      setSnackbar({
        open: true,
        message: apiError.message || `Failed to delete ${row.type}`,
        severity: 'error',
      });
      console.error(`Error deleting ${row.type}:`, error);
    }
    
    handleCloseContextMenu();
  };

  const handleCellEdit = async (rowId: string, field: string) => {
    // Prevent editing when project is locked
    if (projectLocked) {
      setSnackbar({ 
        open: true, 
        message: 'Cannot edit: Project is locked for execution', 
        severity: 'warning' 
      });
      return;
    }
    
    const row = gridRows.find(r => r.id === rowId);
    if (!row) return;

    // For user assignments, load User objects from API
    if (field === 'assignedUsers' && row.type === 'TASK') {
      const task = row.data as PlanningTask;
      if (task.taskId > 0) {
        try {
          const assignments = await getTaskAssignments(task.taskId);
          const users = assignments.map(a => ({
            userId: a.user.userId,
            email: a.user.email,
            displayName: a.user.displayName,
            isActive: true as const,
            role: null as string | null,
            lastLoginAt: null as string | null,
            roles: [] as string[],
            emailConfirmed: false,
            lockoutEnabled: false,
            lockoutEnd: null as string | null,
            accessFailedCount: 0,
            createdAt: new Date().toISOString(),
          }));
          currentUsersRef.current = users as User[]; // Update ref
          setEditingCell({ rowId, field, value: users as User[] });
          return;
        } catch (error) {
          console.error('Error loading task assignments:', error);
          // Fall back to empty array - allow user to still edit
          currentUsersRef.current = []; // Update ref
          setEditingCell({ rowId, field, value: [] });
          return;
        }
      } else {
        // New task, no assignments yet
        currentUsersRef.current = []; // Update ref
        setEditingCell({ rowId, field, value: [] });
        return;
      }
    } else if (field === 'assignedTo' && row.type === 'ACTION') {
      const action = row.data as PlanningAction;
      if (action.actionId > 0) {
        try {
          const assignments = await getActionAssignments(action.actionId);
          const users = assignments.map(a => ({
            userId: a.user.userId,
            email: a.user.email,
            displayName: a.user.displayName,
            isActive: true as const,
            role: null as string | null,
            lastLoginAt: null as string | null,
            roles: [] as string[],
            emailConfirmed: false,
            lockoutEnabled: false,
            lockoutEnd: null as string | null,
            accessFailedCount: 0,
            createdAt: new Date().toISOString(),
          }));
          currentUsersRef.current = users as User[]; // Update ref
          setEditingCell({ rowId, field, value: users as User[] });
          return;
        } catch (error) {
          console.error('Error loading action assignments:', error);
          // Fall back to empty array - allow user to still edit
          currentUsersRef.current = []; // Update ref
          setEditingCell({ rowId, field, value: [] });
          return;
        }
      } else {
        // New action, no assignments yet
        currentUsersRef.current = []; // Update ref
        setEditingCell({ rowId, field, value: [] });
        return;
      }
    }

    // For prerequisites, convert array of numbers to comma-separated string for editing
    if (field === 'prerequisites' && (row.type === 'TASK' || row.type === 'PHASE')) {
      if (row.type === 'TASK') {
        const task = row.data as PlanningTask;
        const prereqIds = (task.prerequisites || []).map(id => id.toString()).join(', ');
        setEditingCell({ rowId, field, value: prereqIds });
      } else if (row.type === 'PHASE') {
        const phase = row.data as PlanningPhase;
        const prereqIds = ((phase as any).prerequisites || []).map((id: number) => id.toString()).join(', ');
        setEditingCell({ rowId, field, value: prereqIds });
      }
      return;
    }

    let currentValue = getCellValue(row, field);
    // For duration, use raw value for editing
    if (field === 'duration') {
      currentValue = getCellValue(row, 'durationRaw');
      
      // Remove from newly created actions set when user starts editing duration
      // This allows validation to run after user has had a chance to set a value
      if (row.type === 'ACTION') {
        const action = row.data as PlanningAction;
        setNewlyCreatedActions(prev => {
          const updated = new Set(prev);
          updated.delete(action.actionId);
          return updated;
        });
      }
    }
    // For action type, use the current action type
    if (field === 'actionType' && row.type === 'ACTION') {
      currentValue = (row.data as PlanningAction).actionType;
    }
    // For action description, use notes field
    if (field === 'description' && row.type === 'ACTION') {
      currentValue = (row.data as PlanningAction).notes || '';
    }
    // For requiredDocumentCount, get the actual value from the action
    if (field === 'requiredDocumentCount' && row.type === 'ACTION') {
      const action = row.data as PlanningAction;
      const requiredCount = (action as any).requiredDocumentCount;
      currentValue = requiredCount != null ? String(requiredCount) : '';
    }
    // For rejectAction, use rejectActionId (number) for the dropdown value
    if (field === 'rejectAction' && row.type === 'ACTION') {
      const action = row.data as PlanningAction;
      const rid = (action as any).rejectActionId;
      currentValue = (rid != null && rid !== '') ? String(rid) : '';
    }
    // Ensure currentValue is always a string (convert undefined/null to empty string)
    const stringValue = currentValue != null ? String(currentValue) : '';
    setEditingCell({ rowId, field, value: stringValue });
  };

  const handleCellSave = async (rowId: string, field: string, newValue: string | User[] | number | null) => {
    // Store current state for revert
    const currentPlan = JSON.parse(JSON.stringify(projectPlan));
    
    // Set loading state for prerequisites
    if (field === 'prerequisites') {
      setSavingPrerequisites(rowId);
    }
    
    const row = gridRows.find(r => r.id === rowId);
    if (!row) {
      if (field === 'prerequisites') {
        setSavingPrerequisites(null);
      }
      return;
    }

    // Validate before saving
    let validationError: string | null = null;

    // Validate duration - allow 0 as valid, only reject negative or NaN
    if (field === 'duration') {
      // Ensure newValue is a string for parsing
      const durationStr = typeof newValue === 'string' ? newValue : String(newValue);
      const duration = parseFloat(durationStr);
      if (isNaN(duration) || duration < 0) {
        validationError = 'Duration must be a non-negative number';
        setValidationErrors(prev => {
          const newErrors = new Map(prev);
          newErrors.set(`${rowId}-${field}`, validationError!);
          return newErrors;
        });
        setEditingCell(null);
        return;
      }
      // Clear validation error if duration is valid (including 0)
      setValidationErrors(prev => {
        const newErrors = new Map(prev);
        newErrors.delete(`${rowId}-${field}`);
        return newErrors;
      });
    }

    // Validate user assignments (if User objects are provided, no email validation needed)
    if ((field === 'assignedUsers' || field === 'assignedTo') && typeof newValue === 'string') {
      const emails = newValue.split(',').map(s => s.trim()).filter(s => s);
      if (emails.length > 0) {
        validationError = validateEmails(emails);
        if (validationError) {
          setValidationErrors(prev => {
            const newErrors = new Map(prev);
            newErrors.set(`${rowId}-${field}`, validationError!);
            return newErrors;
          });
          setEditingCell(null);
          return;
        }
      }
    }

    // Validate prerequisites for circular dependencies
    if (field === 'prerequisites' && typeof newValue === 'string') {
      const prerequisiteStrings = newValue.split(',').map(s => s.trim()).filter(s => s);
      if (prerequisiteStrings.length > 0) {
        if (row.type === 'TASK') {
          // Convert to task IDs (numbers)
          const prerequisites = prerequisiteStrings.map(s => {
            const trimmed = s.trim();
            // Find task ID from task name or ID
            const foundTask = projectPlan.phases
              .flatMap(p => p.tasks)
              .find(t => t.taskId.toString() === trimmed || t.name === trimmed);
            return foundTask ? foundTask.taskId : parseInt(trimmed, 10);
          }).filter(id => !isNaN(id));
          
          // Create a temporary plan to check for circular dependencies
          const tempPlan = JSON.parse(JSON.stringify(projectPlan));
          const phaseIndex = tempPlan.phases.findIndex((p: PlanningPhase) => p.phaseId === row.phaseId);
          if (phaseIndex >= 0) {
            const taskIndex = tempPlan.phases[phaseIndex].tasks.findIndex((t: PlanningTask) => t.taskId === (row.data as PlanningTask).taskId);
            if (taskIndex >= 0) {
              tempPlan.phases[phaseIndex].tasks[taskIndex].prerequisites = prerequisites;
              validationError = checkCircularDependencies((row.data as PlanningTask).taskId, prerequisites, tempPlan);
              if (validationError) {
                setValidationErrors(prev => {
                  const newErrors = new Map(prev);
                  newErrors.set(`${rowId}-${field}`, validationError!);
                  return newErrors;
                });
                setEditingCell(null);
                return;
              }
            }
          }
        } else if (row.type === 'PHASE') {
          // Convert to phase IDs (numbers)
          const prerequisites = prerequisiteStrings.map(s => {
            const trimmed = s.trim();
            // Find phase ID from phase name or ID
            const foundPhase = projectPlan.phases.find(p => p.phaseId.toString() === trimmed || p.name === trimmed);
            return foundPhase ? foundPhase.phaseId : parseInt(trimmed, 10);
          }).filter(id => !isNaN(id));
          
          // Check for circular dependencies in phases (a phase can't depend on itself)
          const currentPhase = row.data as PlanningPhase;
          if (prerequisites.includes(currentPhase.phaseId)) {
            validationError = 'A phase cannot have itself as a prerequisite';
            setValidationErrors(prev => {
              const newErrors = new Map(prev);
              newErrors.set(`${rowId}-${field}`, validationError!);
              return newErrors;
            });
            setEditingCell(null);
            return;
          }
          
          // TODO: Add more sophisticated circular dependency checking for phases if needed
        }
      }
    }

    // Clear validation error for this field if validation passes
    setValidationErrors(prev => {
      const newErrors = new Map(prev);
      newErrors.delete(`${rowId}-${field}`);
      return newErrors;
    });

    setProjectPlan(prevPlan => {
      const updatedPlan = { ...prevPlan };
      
      if (row.type === 'PHASE') {
        const phase = row.data as PlanningPhase;
        const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === phase.phaseId);
        if (phaseIndex >= 0) {
          const updatedPhase = { ...updatedPlan.phases[phaseIndex] };
          switch (field) {
            case 'name':
              updatedPhase.name = newValue as string;
              break;
            case 'description':
              updatedPhase.description = newValue as string;
              break;
            case 'startDate':
              updatedPhase.startDate = (newValue as string) ? (newValue as string).split('T')[0] : null;
              break;
              case 'endDate':
                updatedPhase.endDate = (newValue as string) ? (newValue as string).split('T')[0] : null;
                break;
              case 'duration': {
                // Duration is stored in hours, but user can enter hours or days
                const phaseHours = parseFloat(newValue as string);
                if (!isNaN(phaseHours)) {
                  // If the value is less than 100, assume it's hours; otherwise assume it's already in hours
                  // For phases, we typically work with days, so if value > 24, might be hours already
                  updatedPhase.duration = phaseHours;
                }
                break;
              }
            case 'prerequisites': {
              // Convert comma-separated string to array of numbers (phase IDs)
              // Empty string means no prerequisites
              const phasePrereqString = (newValue as string).trim();
              (updatedPhase as any).prerequisites = phasePrereqString === ''
                ? []
                : phasePrereqString.split(',')
                    .map(s => parseInt(s.trim(), 10))
                    .filter(id => !isNaN(id));
              break;
            }
          }
          updatedPlan.phases[phaseIndex] = updatedPhase;
        }
      } else if (row.type === 'TASK') {
        const task = row.data as PlanningTask;
        const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === row.phaseId);
        if (phaseIndex >= 0) {
          const taskIndex = updatedPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === task.taskId);
          if (taskIndex >= 0) {
            const updatedTask = { ...updatedPlan.phases[phaseIndex].tasks[taskIndex] };
            switch (field) {
              case 'name':
                updatedTask.name = newValue as string;
                break;
              case 'description':
                updatedTask.description = newValue as string;
                break;
              case 'assignedUsers':
                if (Array.isArray(newValue)) {
                  // User objects - store emails for backward compatibility
                  updatedTask.assignedUsers = newValue.map(u => u.email);
                } else {
                  updatedTask.assignedUsers = (newValue as string).split(',').map(s => s.trim()).filter(s => s);
                }
                break;
              case 'startDate':
                updatedTask.startDate = (newValue as string) ? (newValue as string).split('T')[0] : null;
                break;
              case 'endDate':
                updatedTask.endDate = (newValue as string) ? (newValue as string).split('T')[0] : null;
                break;
              case 'duration': {
                // Duration is stored in hours
                const taskHours = parseFloat(newValue as string);
                if (!isNaN(taskHours)) {
                  updatedTask.duration = taskHours;
                }
                break;
              }
              case 'prerequisites':
                // Convert comma-separated string to array of numbers
                updatedTask.prerequisites = (newValue as string)
                  .split(',')
                  .map(s => parseInt(s.trim(), 10))
                  .filter(id => !isNaN(id));
                break;
            }
            updatedPlan.phases[phaseIndex].tasks[taskIndex] = updatedTask;
          }
        }
      } else if (row.type === 'ACTION') {
        const action = row.data as PlanningAction;
        const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === row.phaseId);
        if (phaseIndex >= 0) {
          const taskIndex = updatedPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === row.taskId);
          if (taskIndex >= 0) {
            const actionIndex = updatedPlan.phases[phaseIndex].tasks[taskIndex].actions.findIndex(a => a.actionId === action.actionId);
            if (actionIndex >= 0) {
              const updatedAction = { ...updatedPlan.phases[phaseIndex].tasks[taskIndex].actions[actionIndex] };
              switch (field) {
                case 'actionType': {
                  const oldActionType = updatedAction.actionType;
                  const newActionType = newValue as ActionType;
                  updatedAction.actionType = newActionType;
                  // If changing from REVISE, APPROVE, or REVIEW to another type, clear source document (F20.5, F21.2, Review Action)
                  if ((oldActionType === 'REVISE' || oldActionType === 'APPROVE' || oldActionType === 'REVIEW') && 
                      newActionType !== 'REVISE' && newActionType !== 'APPROVE' && newActionType !== 'REVIEW') {
                    updatedAction.sourceActionId = null;
                    updatedAction.sourceSpItemId = null;
                  }
                  // If changing from APPROVE to another type, clear reject action
                  if (oldActionType === 'APPROVE' && newActionType !== 'APPROVE') {
                    (updatedAction as any).rejectActionId = null;
                  }
                  break;
                }
              case 'description':
                updatedAction.notes = newValue as string; // Save description to notes field
                break;
              case 'duration': {
                const hours = parseFloat(newValue as string);
                if (!isNaN(hours) && hours >= 0) {
                  updatedAction.duration = hours;
                } else {
                  updatedAction.duration = 0;
                }
                break;
              }
              case 'assignedTo':
                if (Array.isArray(newValue)) {
                  // User objects - store emails for backward compatibility
                  updatedAction.assignedTo = newValue.map(u => u.email);
                } else {
                  updatedAction.assignedTo = (newValue as string).split(',').map(s => s.trim()).filter(s => s);
                }
                break;
              case 'sourceDocument': {
                // Convert string to number or null
                const sourceActionId = (newValue as string).trim() === '' 
                  ? null 
                  : parseInt(newValue as string, 10);
                updatedAction.sourceActionId = isNaN(sourceActionId!) ? null : sourceActionId;
                break;
              }
              case 'rejectAction': {
                const rejectActionId = (newValue as string).trim() === '' || newValue == null
                  ? null
                  : parseInt(newValue as string, 10);
                (updatedAction as any).rejectActionId = (rejectActionId != null && !isNaN(rejectActionId)) ? rejectActionId : null;
                break;
              }
              }
              updatedPlan.phases[phaseIndex].tasks[taskIndex].actions[actionIndex] = updatedAction;
            }
          }
        }
      }

      // For prerequisites, sourceDocument, and rejectAction, skip synchronous recalculation - user can use Recalculate button when ready
      // For other fields, we still recalculate synchronously (but could optimize later)
      if (field === 'prerequisites' || field === 'sourceDocument' || field === 'rejectAction') {
        return updatedPlan; // Return without recalculating
      }
      
      // Recalculate durations after update for other fields
      return calculateDurations(updatedPlan);
    });

    // Persist changes via API if item is persisted (positive ID)
    try {
      if (row.type === 'PHASE') {
        const phase = row.data as PlanningPhase;
        if (phase.phaseId > 0) {
          // Track if prerequisites were handled separately (via bulk endpoint)
          let prerequisitesHandled = false;
          const updateData: any = {};
          switch (field) {
            case 'name':
              updateData.name = newValue as string;
              break;
            case 'description':
              updateData.description = (newValue as string) || null;
              break;
            case 'startDate':
              updateData.plannedStartDate = (newValue as string) ? (newValue as string).split('T')[0] : null;
              break;
            case 'endDate':
              updateData.plannedEndDate = (newValue as string) ? (newValue as string).split('T')[0] : null;
              break;
            case 'duration':
              updateData.plannedDurationHours = parseFloat(newValue as string) || 0;
              break;
            case 'prerequisites': {
              // Convert comma-separated string to array of numbers (phase IDs)
              const prereqString = (newValue as string) || '';
              const prereqIds = prereqString.trim() === '' 
                ? [] 
                : prereqString.split(',').map(s => {
                    const trimmed = s.trim();
                    if (!trimmed) return null;
                    // Find phase ID from phase name or ID
                    const foundPhase = projectPlan.phases.find(p => p.phaseId.toString() === trimmed || p.name === trimmed);
                    return foundPhase ? foundPhase.phaseId : parseInt(trimmed, 10);
                  }).filter(id => id !== null && !isNaN(id)) as number[];
              
              // Prerequisites must be updated via bulk endpoint, not individual phase update
              // The UpdatePhaseRequest interface doesn't support prerequisitePhaseIds
              // IMPORTANT: We handle prerequisites separately and return early, so we don't add to updateData
              // This prevents the code below from trying to call updatePhase with empty updateData
              try {
                const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
                
                // Ensure we always send prerequisitePhaseIds, even if empty array
                const phaseUpdate: any = {
                  phaseId: phase.phaseId,
                };
                
                // Always include prerequisitePhaseIds field, even if empty
                phaseUpdate.prerequisitePhaseIds = prereqIds;
                
                const requestBody = {
                  updates: {
                    phases: [phaseUpdate]
                  }
                };
                
                                
                const response = await fetch(`${API_BASE_URL}/api/projects/${projectPlan.projectId}/planning/bulk`, {
                  method: 'PUT',
                  headers: { 
                    'Content-Type': 'application/json',
                    ...createAuthHeaders(),
                  },
                  credentials: 'include',
                  body: JSON.stringify(requestBody)
                });
                
                if (!response.ok) {
                  let error;
                  try {
                    error = await response.json();
                  } catch (parseError) {
                    // If response is not JSON, create a generic error
                    const errorText = await response.text();
                    console.error('Prerequisite update error (non-JSON response):', {
                      status: response.status,
                      statusText: response.statusText,
                      errorText
                    });
                    error = {
                      message: `Failed to update prerequisites: ${response.statusText}`,
                      code: 'VALIDATION_ERROR',
                    };
                  }
                  
                  // Build detailed message from backend when present (BULK_UPDATE_FAILED or VALIDATION_ERROR)
                  const errPayload = error as { results?: unknown; errors?: Array<{ type?: string; id?: number; message?: string }>; message?: string };
                  const errResults = errPayload.results;
                  let displayMessage = errPayload.message || 'Failed to update prerequisites';
                  if (error.code === 'BULK_UPDATE_FAILED') {
                    if (errResults) {
                      const results = errResults as {
                        phases?: { errors?: Array<{ phase?: { phaseId?: number }; error?: string }> };
                        tasks?: { errors?: Array<{ task?: { taskId?: number }; error?: string }> };
                        actions?: { errors?: Array<{ action?: { actionId?: number }; error?: string }> };
                      };
                      const parts: string[] = [];
                      const phaseErrs = results.phases?.errors as Array<{ phase?: { phaseId?: number }; error?: string }> | undefined;
                      if (phaseErrs?.length) {
                        parts.push(...phaseErrs.map(e => `Phase ${e.phase?.phaseId ?? '?'}: ${e.error ?? 'Unknown error'}`));
                      }
                      const taskErrs = results.tasks?.errors as Array<{ task?: { taskId?: number }; error?: string }> | undefined;
                      if (taskErrs?.length) {
                        parts.push(...taskErrs.map(e => `Task ${e.task?.taskId ?? '?'}: ${e.error ?? 'Unknown error'}`));
                      }
                      const actionErrs = results.actions?.errors as Array<{ action?: { actionId?: number }; error?: string }> | undefined;
                      if (actionErrs?.length) {
                        parts.push(...actionErrs.map(e => `Action ${e.action?.actionId ?? '?'}: ${e.error ?? 'Unknown error'}`));
                      }
                      if (parts.length) displayMessage = parts.join('; ');
                    } else if (Array.isArray(errPayload.errors) && errPayload.errors.length > 0 && errPayload.errors[0].message) {
                      displayMessage = errPayload.errors[0].message;
                    }
                  }

                  console.error('Prerequisite update failed:', {
                    status: response.status,
                    statusText: response.statusText,
                    errorCode: error.code,
                    results: errResults,
                    displayMessage
                  });

                  // Display backend error message
                  if (error.code === 'PROJECT_LOCKED') {
                    setBackendValidationError({
                      message: 'Project is locked. Prerequisite changes are disabled.',
                      code: 'PROJECT_LOCKED',
                    });
                    setSnackbar({ open: true, message: 'Project is locked. Prerequisite changes are disabled.', severity: 'error' });
                    throw new Error('Project is locked');
                  }

                  if (error.code === 'VALIDATION_ERROR' || error.code === 'BULK_UPDATE_FAILED') {
                    const errorMessage = error.code === 'BULK_UPDATE_FAILED' ? displayMessage : buildErrorMessage(error);
                    setBackendValidationError({
                      message: errorMessage,
                      code: error.code === 'BULK_UPDATE_FAILED' ? 'BULK_UPDATE_FAILED' : 'VALIDATION_ERROR',
                      details: errResults ?? error.details,
                    });
                    setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                    throw new Error(errorMessage);
                  }

                  setBackendValidationError({
                    message: displayMessage,
                    code: error.code,
                  });
                  setSnackbar({ open: true, message: displayMessage, severity: 'error' });
                  throw new Error(displayMessage);
                }
                
                // Check response body even on 200 OK - backend might return success=false
                const result = await response.json();
                
                                
                // Check if the backend indicates failure
                // Note: result.updated.phases is a COUNT (number), not an array
                if (result.success === false || (result.updated && result.updated.phases === 0)) {
                  // Check for specific error messages
                  const errorMessage = result.message || 
                    (result.errors && result.errors.length > 0 
                      ? result.errors.map((e: any) => `${e.type} ${e.id}: ${e.message}`).join(', ')
                      : 'No updates were successful');
                  
                  console.error('Bulk update failed (cell edit):', {
                    success: result.success,
                    updatedPhases: result.updated?.phases,
                    errors: result.errors,
                    errorMessage
                  });
                  
                  setBackendValidationError({
                    message: errorMessage,
                    code: 'VALIDATION_ERROR',
                    details: result.errors?.[0] || result.details,
                  });
                  setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                  throw new Error(errorMessage);
                }
                
                // Success - update local state
                setBackendValidationError(null);
                
                // Update local state to reflect the saved prerequisites
                // Note: result.updated.phases is a count, not an array, so we use what we sent
                setProjectPlan(prevPlan => {
                  const updatedPlan = { ...prevPlan };
                  const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === phase.phaseId);
                  if (phaseIndex >= 0) {
                    // Use what we sent since backend doesn't return the updated phase data in the response
                    (updatedPlan.phases[phaseIndex] as any).prerequisites = prereqIds;
                  }
                  return updatedPlan;
                });
                
                setSnackbar({ open: true, message: 'Prerequisites updated successfully', severity: 'success' });
                
                // Close editing cell immediately
                setEditingCell(null);
                setSavingPrerequisites(null);
                return; // Exit early since we handled the update - don't call updatePhase below
              } catch (error: any) {
                console.error('Prerequisite update error:', error);
                setSavingPrerequisites(null);
                // Revert local state change
                setProjectPlan(currentPlan);
                if (error.message && !error.message.includes('Project is locked')) {
                  // Error already displayed above
                }
                return; // Exit early on error - don't call updatePhase below
              }
            }
            break; // Break out of switch
          }
          // Only call updatePhase if we have fields to update AND prerequisites weren't handled separately
          // (prerequisites are handled via bulk endpoint above and return early)
          if (Object.keys(updateData).length > 0 && !prerequisitesHandled) {
            await updatePhase(phase.phaseId, updateData);
          }
        }
      } else if (row.type === 'TASK') {
        const task = row.data as PlanningTask;
        if (task.taskId > 0) {
          // Handle user assignments separately via TaskAssignments API
          if (field === 'assignedUsers' && Array.isArray(newValue)) {
            try {
                            
              // Get current assignments
              const currentAssignments = await getTaskAssignments(task.taskId);
                            
              const currentUserIds = new Set(currentAssignments.map(a => a.userId));
              const newUserIds = new Set(newValue.map(u => u.userId));
              
              // Remove users that are no longer assigned
              const usersToRemove = currentAssignments.filter(a => !newUserIds.has(a.userId));
                            
              for (const assignment of usersToRemove) {
                try {
                  await removeTaskAssignment(task.taskId, assignment.userId);
                                  } catch (error) {
                  console.error(`handleCellSave: Failed to remove assignment for task ${task.taskId}, user ${assignment.userId}:`, error);
                  throw error;
                }
              }
              
              // Add new users
              const usersToAdd = newValue.filter(u => !currentUserIds.has(u.userId));
                            
              for (const user of usersToAdd) {
                try {
                  await assignUserToTask(task.taskId, user.userId);
                } catch (error) {
                  console.error(`handleCellSave: Failed to add assignment for task ${task.taskId}, user ${user.userId}:`, error);
                  throw error;
                }
              }
              
              // Reload assignments to ensure state is in sync
                            const updatedAssignments = await getTaskAssignments(task.taskId);
                            
              const updatedUsers = updatedAssignments.map(a => a.user);
              const updatedEmails = updatedUsers.map(u => u.email);
              
              // Verify assignments were saved correctly
              if (updatedAssignments.length !== newValue.length) {
                console.warn(`handleCellSave: Assignment count mismatch for task ${task.taskId}:`, {
                  expected: newValue.length,
                  actual: updatedAssignments.length,
                  expectedUsers: newValue.map(u => u.email),
                  actualUsers: updatedEmails
                });
              }
              
              // Update local state with the actual assignments from API
              setProjectPlan(prevPlan => {
                const updatedPlan = { ...prevPlan };
                const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === row.phaseId);
                if (phaseIndex >= 0) {
                  const taskIndex = updatedPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === task.taskId);
                  if (taskIndex >= 0) {
                    updatedPlan.phases[phaseIndex].tasks[taskIndex].assignedUsers = updatedEmails;
                                      }
                }
                return updatedPlan;
              });
              
              // Close editing cell after state update
              setEditingCell(null);
              
                          } catch (error) {
              console.error(`handleCellSave: Error updating task assignments for task ${task.taskId}:`, error);
              console.error(`handleCellSave: Error details:`, {
                error,
                errorType: typeof error,
                errorMessage: error instanceof Error ? error.message : String(error),
                errorStack: error instanceof Error ? error.stack : undefined
              });
              throw error;
            }
            // Skip the rest of the API update logic for assignedUsers
            return;
          } else {
            const updateData: any = {};
            switch (field) {
              case 'name':
                updateData.name = newValue as string;
                break;
              case 'description':
                updateData.description = (newValue as string) || null;
                break;
              case 'startDate':
                updateData.plannedStartDate = (newValue as string) ? (newValue as string).split('T')[0] : null;
                break;
              case 'endDate':
                updateData.plannedEndDate = (newValue as string) ? (newValue as string).split('T')[0] : null;
                break;
              case 'duration':
                updateData.plannedDurationHours = parseFloat(newValue as string) || 0;
                break;
              case 'prerequisites': {
                // Convert task ID strings to numbers
                // Backend expects prerequisiteTaskIds (not prerequisites)
                // Empty string means no prerequisites
                const prereqString = (newValue as string) || '';
                const prereqIds = prereqString.trim() === '' 
                  ? [] 
                  : prereqString.split(',').map(s => {
                      const trimmed = s.trim();
                      if (!trimmed) return null;
                      // Find task ID from task name or ID
                      const foundTask = projectPlan.phases
                        .flatMap(p => p.tasks)
                        .find(t => t.taskId.toString() === trimmed || t.name === trimmed);
                      return foundTask ? foundTask.taskId : parseInt(trimmed, 10);
                    }).filter(id => id !== null && !isNaN(id)) as number[];
                
                // Update local state immediately using setProjectPlan
                setProjectPlan(prevPlan => {
                  const updatedPlan = { ...prevPlan };
                  const phaseIdx = updatedPlan.phases.findIndex(p => p.phaseId === row.phaseId);
                  if (phaseIdx >= 0) {
                    const taskIdx = updatedPlan.phases[phaseIdx].tasks.findIndex(t => t.taskId === task.taskId);
                    if (taskIdx >= 0) {
                      updatedPlan.phases[phaseIdx].tasks[taskIdx].prerequisites = prereqIds;
                    }
                  }
                  return updatedPlan;
                });
                
                // Save prerequisites immediately via API
                updateData.prerequisiteTaskIds = prereqIds;
                break; // Continue to API call below
              }
            }
            if (Object.keys(updateData).length > 0) {
              // For other fields, make API call as usual
              await updateTask(task.taskId, updateData);
              
              // If prerequisites were saved, close editing cell and show success
              if (field === 'prerequisites') {
                setEditingCell(null);
                setTimeout(() => setSavingPrerequisites(null), 300);
                setSnackbar({ 
                  open: true, 
                  message: 'Prerequisites saved successfully.', 
                  severity: 'success' 
                });
              }
            }
          }
        }
      } else if (row.type === 'ACTION') {
        const action = row.data as PlanningAction;
        if (action.actionId > 0) {
          const updateData: any = {};
          switch (field) {
            case 'actionType':
              const oldActionType = action.actionType;
              const newActionType = newValue as ActionType;
              updateData.actionType = newActionType;
              // If changing from REVISE, APPROVE, or REVIEW to another type, clear source document (F20.5, F21.2, Review Action)
              if ((oldActionType === 'REVISE' || oldActionType === 'APPROVE' || oldActionType === 'REVIEW') && 
                  newActionType !== 'REVISE' && newActionType !== 'APPROVE' && newActionType !== 'REVIEW') {
                updateData.sourceActionId = null;
                updateData.sourceSpItemId = null;
              }
              // If changing from APPROVE to another type, clear reject action
              if (oldActionType === 'APPROVE' && newActionType !== 'APPROVE') {
                updateData.rejectActionId = null;
              }
              break;
            case 'description':
              updateData.notes = (newValue as string) || null;
              break;
            case 'sourceDocument':
              // Convert string to number or null
              const sourceActionId = (newValue as string).trim() === '' 
                ? null 
                : parseInt(newValue as string, 10);
              updateData.sourceActionId = isNaN(sourceActionId!) ? null : sourceActionId;
              break;
            case 'rejectAction':
              const rejectActionIdVal = (newValue as string).trim() === '' || newValue == null
                ? null
                : parseInt(newValue as string, 10);
              updateData.rejectActionId = (rejectActionIdVal != null && !isNaN(rejectActionIdVal)) ? rejectActionIdVal : null;
              break;
            case 'duration':
              // Parse the duration value - it should already be in hours from EnhancedDurationInput
              // Ensure newValue is a string for parsing
              const durationStr = typeof newValue === 'string' ? newValue : String(newValue);
              const hours = parseFloat(durationStr);
                            if (!isNaN(hours) && hours >= 0) {
                // Try sending only 'duration' first - backend may only accept this field for updates
                const hoursNum = typeof hours === 'number' ? hours : parseFloat(String(hours));
                updateData.duration = hoursNum;
                // Don't send plannedDurationHours - backend may reject if both are sent
                              } else {
                // If parsing fails, don't update - this should not happen due to validation
                console.warn('Duration parsing failed, skipping update:', { newValue, durationStr, hours });
                return; // Exit early to avoid "No fields provided to update" error
              }
              break;
            case 'requiredDocumentCount':
              // Parse the required document count value
                            let countValue: number | null;
              if (newValue === null || newValue === '' || newValue === undefined) {
                countValue = null;
              } else if (typeof newValue === 'number') {
                countValue = newValue;
              } else {
                const parsed = parseInt(String(newValue), 10);
                countValue = isNaN(parsed) ? null : parsed;
              }
                            if (countValue !== null && !isNaN(countValue) && countValue >= 0) {
                updateData.requiredDocumentCount = countValue;
                              } else if (countValue === null) {
                updateData.requiredDocumentCount = null;
                              } else {
                console.warn('Invalid requiredDocumentCount value, skipping update:', { newValue, countValue });
                return; // Exit early to avoid invalid update
              }
              break;
            case 'assignedTo':
              // User assignments for actions via ActionAssignments API
              if (Array.isArray(newValue)) {
                try {
                  // Validate all users are project users
                  const invalidUsers = newValue.filter(u => !projectUserIds.has(u.userId));
                  if (invalidUsers.length > 0) {
                    throw new Error(`Cannot assign users who are not in the project: ${invalidUsers.map(u => u.email).join(', ')}`);
                  }

                  // Get current assignments
                  const currentAssignments = await getActionAssignments(action.actionId);
                  const currentUserIds = new Set(currentAssignments.map(a => a.userId));
                  const newUserIds = newValue.map(u => u.userId);
                  const newUserIdsSet = new Set(newUserIds);
                  
                  // Determine users to remove and add
                  const usersToRemove = currentAssignments.filter(a => !newUserIdsSet.has(a.userId));
                  const usersToAdd = newValue.filter(u => !currentUserIds.has(u.userId));
                  
                  // Remove users that are no longer assigned
                  for (const assignment of usersToRemove) {
                    await removeActionAssignment(action.actionId, assignment.userId);
                  }
                  
                  // Add new users using multi-assign API if multiple, single API if one
                  if (usersToAdd.length > 0) {
                    if (usersToAdd.length === 1) {
                      await assignUserToAction(action.actionId, usersToAdd[0].userId);
                    } else {
                      await assignUsersToAction(action.actionId, usersToAdd.map(u => u.userId));
                    }
                  }
                  
                  // Reload assignments to ensure state is in sync
                  const updatedAssignments = await getActionAssignments(action.actionId);
                  const updatedUsers = updatedAssignments.map(a => a.user);
                  const updatedEmails = updatedUsers.map(u => u.email);
                  
                  // Update local state with the actual assignments from API
                  setProjectPlan(prevPlan => {
                    const updatedPlan = { ...prevPlan };
                    const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === row.phaseId);
                    if (phaseIndex >= 0) {
                      const taskIndex = updatedPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === row.taskId);
                      if (taskIndex >= 0) {
                        const actionIndex = updatedPlan.phases[phaseIndex].tasks[taskIndex].actions.findIndex(a => a.actionId === action.actionId);
                        if (actionIndex >= 0) {
                          updatedPlan.phases[phaseIndex].tasks[taskIndex].actions[actionIndex].assignedTo = updatedEmails;
                        }
                      }
                    }
                    return updatedPlan;
                  });
                  
                  // Close editing cell after state update
                  setEditingCell(null);
                } catch (error) {
                  console.error('Error updating action assignments:', error);
                  throw error;
                }
                // Skip the rest of the API update logic for assignedTo
                return;
              }
              break;
          }

          if (Object.keys(updateData).length > 0) {
            // Ensure all values are properly formatted (numbers as numbers, not strings)
            const sanitizedUpdateData: any = {};
            for (const [key, value] of Object.entries(updateData)) {
              // Allow null values for requiredDocumentCount and sourceActionId (to clear the value)
              if ((key === 'requiredDocumentCount' || key === 'sourceActionId') && value === null) {
                sanitizedUpdateData[key] = null;
                continue;
              }
              if (value !== undefined && value !== null) {
                // If it's a number field, ensure it's a number and not NaN
                if (key === 'plannedDurationHours' || key === 'duration' || key === 'requiredDocumentCount') {
                  const numValue = typeof value === 'number' ? value : parseFloat(String(value));
                  if (!isNaN(numValue) && numValue >= 0) {
                    sanitizedUpdateData[key] = numValue;
                  } else {
                    console.warn(`Skipping ${key} because it's NaN or negative`);
                  }
                } else {
                  sanitizedUpdateData[key] = value;
                }
              } else if (value === null && key === 'sourceActionId') {
                // Explicitly allow null for sourceActionId to clear the source document
                sanitizedUpdateData[key] = null;
              }
            }

            // Double-check that we have at least one valid field
            if (Object.keys(sanitizedUpdateData).length === 0) {
              console.error('Sanitized updateData is empty after processing:', {
                originalUpdateData: updateData,
                originalKeys: Object.keys(updateData),
                field,
                newValue,
                actionId: action.actionId
              });
              return; // Don't make API call with empty data
            }

            try {
              const updatedAction = await updateAction(action.actionId, sanitizedUpdateData);

              // Update local state with the response to ensure UI reflects saved changes
              if (field === 'duration') {
                // Backend returns durationHours (or duration), map it to duration for our local state
                const savedDuration = (updatedAction as any).durationHours ?? updatedAction.duration ?? updatedAction.plannedDurationHours;
                if (savedDuration !== undefined) {
                  setProjectPlan(prevPlan => {
                    const updatedPlan = { ...prevPlan };
                    const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === row.phaseId);
                    if (phaseIndex >= 0) {
                      const taskIndex = updatedPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === row.taskId);
                      if (taskIndex >= 0) {
                        const actionIndex = updatedPlan.phases[phaseIndex].tasks[taskIndex].actions.findIndex(a => a.actionId === action.actionId);
                        if (actionIndex >= 0) {
                          updatedPlan.phases[phaseIndex].tasks[taskIndex].actions[actionIndex].duration = savedDuration;
                        }
                      }
                    }
                    return updatedPlan;
                  });
                }
              } else if (field === 'sourceDocument') {
                // Update sourceActionId from response
                const savedSourceActionId = (updatedAction as any).sourceActionId ?? null;
                setProjectPlan(prevPlan => {
                  const updatedPlan = { ...prevPlan };
                  const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === row.phaseId);
                  if (phaseIndex >= 0) {
                    const taskIndex = updatedPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === row.taskId);
                    if (taskIndex >= 0) {
                      const actionIndex = updatedPlan.phases[phaseIndex].tasks[taskIndex].actions.findIndex(a => a.actionId === action.actionId);
                      if (actionIndex >= 0) {
                        updatedPlan.phases[phaseIndex].tasks[taskIndex].actions[actionIndex].sourceActionId = savedSourceActionId;
                      }
                    }
                  }
                  return updatedPlan;
                });
                setEditingCell(null);
              } else if (field === 'requiredDocumentCount') {
                // Update requiredDocumentCount from response
                const savedRequiredCount = (updatedAction as any).requiredDocumentCount ?? null;
                setProjectPlan(prevPlan => {
                  const updatedPlan = { ...prevPlan };
                  const phaseIndex = updatedPlan.phases.findIndex(p => p.phaseId === row.phaseId);
                  if (phaseIndex >= 0) {
                    const taskIndex = updatedPlan.phases[phaseIndex].tasks.findIndex(t => t.taskId === row.taskId);
                    if (taskIndex >= 0) {
                      const actionIndex = updatedPlan.phases[phaseIndex].tasks[taskIndex].actions.findIndex(a => a.actionId === action.actionId);
                      if (actionIndex >= 0) {
                        (updatedPlan.phases[phaseIndex].tasks[taskIndex].actions[actionIndex] as any).requiredDocumentCount = savedRequiredCount;
                      }
                    }
                  }
                  return updatedPlan;
                });
                setEditingCell(null);
              }
            } catch (error) {
              console.error(`Failed to save action ${field}:`, error);
              throw error;
            }
          } else {
            console.warn('No update data for action duration save - updateData is empty', {
              field,
              newValue,
              actionId: action.actionId
            });
          }
        }
      }
    } catch (error) {
      const apiError = error as PlanningExecutionApiError;
      setSnackbar({
        open: true,
        message: apiError.message || 'Failed to save changes',
        severity: 'error',
      });
      console.error('Error saving cell value:', error);
      // Note: State was already updated optimistically, could rollback here if needed
    }

    setEditingCell(null);
  };

  // Memoize helper functions to prevent unnecessary re-renders
  const getValidationError = useCallback((rowId: string, field: string): string | null => {
    const errorKey = `${rowId}-${field}`;
    const error = validationErrors.get(errorKey);
    
    // Suppress duration validation errors for newly created actions
    // This allows users to add actions without immediately seeing validation errors
    if (error && field === 'duration' && rowId.startsWith('action-')) {
      const actionId = parseInt(rowId.replace('action-', ''), 10);
      if (!isNaN(actionId) && newlyCreatedActions.has(actionId)) {
        return null; // Suppress error for newly created actions
      }
    }
    
    return error || null;
  }, [validationErrors, newlyCreatedActions]);

  const getRowValidationError = useCallback((row: GridRow): string | null => {
    if (row.type === 'PHASE') {
      return validationErrors.get(`phase-${(row.data as PlanningPhase).phaseId}`) || null;
    } else if (row.type === 'TASK') {
      return validationErrors.get(`task-${(row.data as PlanningTask).taskId}`) || null;
    }
    return null;
  }, [validationErrors]);

  // Check if a date is calculated (all task/phase/project dates are calculated based on actions/prerequisites)
  const isDateCalculated = useCallback((row: GridRow): boolean => {
    // Project, Phase and Task dates are always calculated based on actions and prerequisites
    if (row.type === 'PROJECT' || row.type === 'PHASE' || row.type === 'TASK') {
      return true;
    }
    // Actions don't have dates
    return false;
  }, []);

  // Check if duration is calculated (all project/phase/task durations are calculated from actions)
  const isDurationCalculated = useCallback((row: GridRow): boolean => {
    // Project, Phase and Task durations are always calculated from actions
    if (row.type === 'PROJECT' || row.type === 'PHASE' || row.type === 'TASK') {
      return true;
    }
    // Action durations are manually entered
    return false;
  }, []);

  const getCellValue = useCallback((row: GridRow, field: string): string => {
    switch (row.type) {
      case 'PROJECT': {
        const project = row.data as ProjectPlan;
        switch (field) {
          case 'name': return project.projectName;
          case 'description': return 'Project summary';
          case 'startDate': {
            // Project start date is stored as string in YYYY-MM-DD format
            if (!project.projectStartDate) return '';
            if (typeof project.projectStartDate === 'string') {
              return project.projectStartDate.split('T')[0];
            }
            // If it's a Date object, format it
            if ((project.projectStartDate as unknown) instanceof Date && !isNaN((project.projectStartDate as unknown as Date).getTime())) {
              return format(project.projectStartDate as unknown as Date, 'yyyy-MM-dd');
            }
            return '';
          }
          case 'endDate': {
            // Calculate project end date from phases (max end date of all phases)
            let projectEndDate: Date | string | null = null;
            
            project.phases.forEach(phase => {
              if (phase.endDate) {
                let phaseEndDate: Date | null = null;
                
                if (typeof phase.endDate === 'string') {
                  const date = new Date(phase.endDate + 'T00:00:00');
                  if (!isNaN(date.getTime())) {
                    phaseEndDate = date;
                  }
                } else if ((phase.endDate as unknown) instanceof Date && !isNaN((phase.endDate as unknown as Date).getTime())) {
                  phaseEndDate = phase.endDate as unknown as Date;
                }
                
                if (phaseEndDate && (!projectEndDate || phaseEndDate > (projectEndDate instanceof Date ? projectEndDate : new Date((projectEndDate as string) + 'T00:00:00')))) {
                  projectEndDate = phaseEndDate;
                }
              }
            });
            
            if (!projectEndDate) return '';
            
            // Format the date
            if (typeof projectEndDate === 'string') {
              return (projectEndDate as string).split('T')[0];
            }
            if ((projectEndDate as unknown) instanceof Date && !isNaN((projectEndDate as Date).getTime())) {
              return format(projectEndDate as Date, 'yyyy-MM-dd');
            }
            return '';
          }
          case 'duration': {
            // Calculate duration from start and end dates
            const startDateStr = project.projectStartDate;
            if (!startDateStr) return '0 days';
            
            // Calculate end date from phases
            let projectEndDate: Date | null = null;
            project.phases.forEach(phase => {
              if (phase.endDate) {
                let phaseEndDate: Date | null = null;
                if (typeof phase.endDate === 'string') {
                  const date = new Date(phase.endDate + 'T00:00:00');
                  if (!isNaN(date.getTime())) phaseEndDate = date;
                } else if ((phase.endDate as unknown) instanceof Date && !isNaN((phase.endDate as unknown as Date).getTime())) {
                  phaseEndDate = phase.endDate as unknown as Date;
                }
                if (phaseEndDate && (!projectEndDate || phaseEndDate > projectEndDate)) {
                  projectEndDate = phaseEndDate;
                }
              }
            });
            
            if (!projectEndDate) return '0 days';
            
            const startDate = typeof startDateStr === 'string' 
              ? new Date(startDateStr + 'T00:00:00')
              : (startDateStr as unknown as Date);
            
            if ((startDate as unknown) instanceof Date && !isNaN((startDate as Date).getTime())) {
              const diffTime = (projectEndDate as Date).getTime() - (startDate as Date).getTime();
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days
              return `${diffDays} ${diffDays === 1 ? 'cal. day' : 'cal. days'}`;
            }
            return '0 cal. days';
          }
          case 'durationRaw': {
            // Calculate duration in hours (8 hours per day)
            const startDateStr = project.projectStartDate;
            if (!startDateStr) return '0';
            
            let projectEndDate: Date | null = null;
            project.phases.forEach(phase => {
              if (phase.endDate) {
                let phaseEndDate: Date | null = null;
                if (typeof phase.endDate === 'string') {
                  const date = new Date(phase.endDate + 'T00:00:00');
                  if (!isNaN(date.getTime())) phaseEndDate = date;
                } else if ((phase.endDate as unknown) instanceof Date && !isNaN((phase.endDate as unknown as Date).getTime())) {
                  phaseEndDate = phase.endDate as unknown as Date;
                }
                if (phaseEndDate && (!projectEndDate || phaseEndDate > projectEndDate)) {
                  projectEndDate = phaseEndDate;
                }
              }
            });
            
            if (!projectEndDate) return '0';
            
            const startDate = typeof startDateStr === 'string' 
              ? new Date(startDateStr + 'T00:00:00')
              : (startDateStr as unknown as Date);
            
            if ((startDate as unknown) instanceof Date && !isNaN((startDate as Date).getTime())) {
              const diffTime = (projectEndDate as Date).getTime() - (startDate as Date).getTime();
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
              return `${diffDays * 8}`; // 8 hours per day
            }
            return '0';
          }
          default: return '';
        }
      }
      case 'PHASE': {
        const phase = row.data as PlanningPhase;
        switch (field) {
          case 'name': return phase.name;
          case 'description': return phase.description;
          case 'startDate': {
            if (!phase.startDate) return '';
            if (typeof phase.startDate === 'string') return phase.startDate.split('T')[0];
            // Validate Date object before formatting
            if ((phase.startDate as unknown) instanceof Date && !isNaN((phase.startDate as unknown as Date).getTime())) {
              return format(phase.startDate as unknown as Date, 'yyyy-MM-dd');
            }
            return '';
          }
          case 'endDate': {
            if (!phase.endDate) return '';
            if (typeof phase.endDate === 'string') return phase.endDate.split('T')[0];
            // Validate Date object before formatting
            if ((phase.endDate as unknown) instanceof Date && !isNaN((phase.endDate as unknown as Date).getTime())) {
              return format(phase.endDate as unknown as Date, 'yyyy-MM-dd');
            }
            return '';
          }
          case 'duration': {
            const daysValue = (phase.duration / 8).toFixed(1);
            return `${daysValue} wrk days`;
          }
          case 'durationRaw': return `${phase.duration}`;
          case 'prerequisites': {
            // Phase prerequisites can be phase IDs or task IDs (PhasePrerequisites.PrerequisiteType = 'PHASE' | 'TASK')
            const prereqIds = (phase as any).prerequisites || [];
            const prereqNames: string[] = [];
            prereqIds.forEach((prereqId: number) => {
              const foundPhase = projectPlan.phases.find(p => p.phaseId === prereqId);
              if (foundPhase) {
                prereqNames.push(`${foundPhase.displayOrder} - ${foundPhase.name}`);
                return;
              }
              for (const p of projectPlan.phases) {
                const foundTask = p.tasks.find(t => t.taskId === prereqId);
                if (foundTask) {
                  prereqNames.push(`${p.displayOrder}.${foundTask.displayOrder} - ${foundTask.name}`);
                  return;
                }
              }
            });
            return prereqNames.length > 0 ? prereqNames.join(', ') : '';
          }
          default: return '';
        }
      }
      case 'TASK': {
        const task = row.data as PlanningTask;
        switch (field) {
          case 'name': return task.name;
          case 'description': return task.description;
          case 'assignedUsers': return task.assignedUsers.join(', ');
          case 'startDate': {
            if (!task.startDate) return '';
            if (typeof task.startDate === 'string') return task.startDate.split('T')[0];
            // Validate Date object before formatting
            if ((task.startDate as unknown) instanceof Date && !isNaN((task.startDate as unknown as Date).getTime())) {
              return format(task.startDate as unknown as Date, 'yyyy-MM-dd');
            }
            return '';
          }
          case 'endDate': {
            if (!task.endDate) return '';
            if (typeof task.endDate === 'string') return task.endDate.split('T')[0];
            // Validate Date object before formatting
            if ((task.endDate as unknown) instanceof Date && !isNaN((task.endDate as unknown as Date).getTime())) {
              return format(task.endDate as unknown as Date, 'yyyy-MM-dd');
            }
            return '';
          }
          case 'duration': {
            const daysValue = (task.duration / 8).toFixed(1);
            return `${daysValue} wrk days`;
          }
          case 'durationRaw': return `${task.duration}`;
          case 'prerequisites': {
            // Convert task IDs to task names for display
            const prereqNames: string[] = [];
            task.prerequisites.forEach((prereqId) => {
              // Find the task by ID
              for (const phase of projectPlan.phases) {
                const foundTask = phase.tasks.find(t => t.taskId === prereqId);
                if (foundTask) {
                  const phaseOrder = phase.displayOrder;
                  const taskOrder = foundTask.displayOrder;
                  prereqNames.push(`${phaseOrder}.${taskOrder} - ${foundTask.name}`);
                  break;
                }
              }
            });
            return prereqNames.length > 0 ? prereqNames.join(', ') : '';
          }
          case 'parallelExecution': return task.parallelExecution ? 'Yes' : 'No';
          default: return '';
        }
      }
      case 'ACTION': {
        const action = row.data as PlanningAction;
        switch (field) {
          case 'actionType': return action.actionType;
          case 'description': return (action as PlanningAction).description ?? action.notes; // Prefer API description, fallback to notes
          case 'duration': return action.duration !== undefined && action.duration !== null ? `${action.duration}h` : '0h';
          case 'durationRaw': return action.duration !== undefined && action.duration !== null ? `${action.duration}` : '0';
          case 'assignedTo': return action.assignedTo.join(', ');
          case 'startDate': {
            if (!action.plannedStartDate) return '';
            const s = typeof action.plannedStartDate === 'string' ? action.plannedStartDate.split('T')[0] : '';
            return s || '';
          }
          case 'endDate': {
            if (!action.plannedEndDate) return '';
            const e = typeof action.plannedEndDate === 'string' ? action.plannedEndDate.split('T')[0] : '';
            return e || '';
          }
          case 'sourceDocument': {
            // Display source action/document information
            if (action.actionType !== 'REVISE' && action.actionType !== 'APPROVE' && action.actionType !== 'REVIEW') {
              return '';
            }
            if (action.sourceActionId) {
              // Find the source action to get its name/document info
              let sourceActionName = '';
              // @ts-ignore - intentionally unused
              let _sourceTaskName = '';
              for (const phase of projectPlan.phases) {
                for (const task of phase.tasks) {
                  const sourceAction = task.actions.find(a => a.actionId === action.sourceActionId);
                  if (sourceAction) {
                    sourceActionName = sourceAction.name ?? `${sourceAction.actionType} - ${task.name}`;
                    _sourceTaskName = task.name;
                    break;
                  }
                }
                if (sourceActionName) break;
              }
              
              // Try to get document title from source CREATE action's documents
              // First, get the association from the target action to find sourceActionDocumentId
              const targetAssociations = sourceDocumentAssociationsCache.get(action.actionId) || [];
              const sourceAssociation = targetAssociations.find(doc => 
                doc.documentType === 'SOURCE' && 
                doc.sourceActionId === action.sourceActionId
              );
              
              if (sourceAssociation && sourceAssociation.sourceActionDocumentId) {
                // Look up the actual document from the source CREATE action
                const sourceCreateDocuments = createActionDocumentsCache.get(action.sourceActionId) || [];
                const sourceDocument = sourceCreateDocuments.find(doc => 
                  doc.actionDocumentId === sourceAssociation.sourceActionDocumentId
                );
                
                if (sourceDocument && sourceDocument.documentTitle) {
                  // Show action name with specific document title from source CREATE action
                  return `${sourceActionName} (${sourceDocument.documentTitle})`;
                } else if (sourceDocument && sourceDocument.documentNumber) {
                  // Fallback to document number if no title
                  return `${sourceActionName} (Document ${sourceDocument.documentNumber})`;
                } else if (sourceAssociation.documentNumber) {
                  // Fallback to document number from association
                  return `${sourceActionName} (Document ${sourceAssociation.documentNumber})`;
                }
              } else if (sourceAssociation && sourceAssociation.documentNumber) {
                // Fallback to document number from association if no sourceActionDocumentId
                return `${sourceActionName} (Document ${sourceAssociation.documentNumber})`;
              }
              
              // Fallback to just action name if no document association found
              return sourceActionName || `Action ${action.sourceActionId}`;
            }
            return '';
          }
          case 'rejectAction': {
            // Only APPROVE actions can have a reject action (rollback target)
            if (action.actionType !== 'APPROVE') return '';
            if (!action.rejectActionId) return '';
            for (const phase of projectPlan.phases) {
              for (const task of phase.tasks) {
                const rejectAction = task.actions.find(a => a.actionId === action.rejectActionId);
                if (rejectAction) {
                  const phaseOrder = phase.displayOrder;
                  const taskOrder = task.displayOrder;
                  const actionOrder = rejectAction.displayOrder;
                  const planId = `${phaseOrder}.${taskOrder}.${actionOrder}`;
                  return rejectAction.name ? `${planId} - ${rejectAction.name}` : planId;
                }
              }
            }
            return `Action ${action.rejectActionId}`;
          }
          case 'requiredDocumentCount': {
            // Only show for CREATE actions
            if (action.actionType !== 'CREATE') {
              return '';
            }
            // Try to get from action data, or fetch from API
            const requiredCount = (action as any).requiredDocumentCount;
            return requiredCount ? requiredCount.toString() : '';
          }
          default: return '';
        }
      }
      default: return '';
    }
  }, [projectPlan, sourceDocumentAssociationsCache, createActionDocumentsCache]);


  return (
    <Box sx={{ flexGrow: 1, backgroundColor: 'white', backgroundImage: 'none' }}>
      <AppBar 
        position="static"
        sx={{ 
          backgroundColor: '#003366', // Match main header Fraport blue
        }}
      >
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ mr: 3, color: 'white' }}>
            Project Planning
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {/* Export JSON hidden in UI; remove display: 'none' to show again. Gated to Fraport users. */}
          {user && isFraportUser(user) && (
          <Box sx={{ display: 'none' }}>
            <Tooltip title="Export JSON">
              <IconButton
                sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }, mr: 1 }}
                onClick={() => {
                  try {
                    const saved = localStorage.getItem('projectPlan');
                    if (saved) {
                      const blob = new Blob([saved], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `project-plan-${projectPlan.projectId}-${format(new Date(), 'yyyy-MM-dd')}.json`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      setSnackbar({ open: true, message: 'Project plan exported successfully', severity: 'success' });
                    } else {
                      setSnackbar({ open: true, message: 'No saved project plan to export', severity: 'info' });
                    }
                  } catch {
                    setSnackbar({ open: true, message: 'Error exporting project plan', severity: 'error' });
                  }
                }}
              >
                <Download />
              </IconButton>
            </Tooltip>
          </Box>
          )}
          {projectStatus === 'PLANNING' && projectPlan?.projectId > 0 && user && isFraportUser(user) && (
            <>
              <Tooltip title="Export to Excel">
                <IconButton
                  sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }, mr: 1 }}
                  onClick={async () => {
                    try {
                      await downloadSeededExcelTemplate(projectPlan.projectId);
                      setSnackbar({ open: true, message: 'Excel template downloaded', severity: 'success' });
                    } catch (err) {
                      const apiErr = err as ProjectApiError;
                      setSnackbar({ open: true, message: apiErr.message || 'Export failed', severity: 'error' });
                    }
                  }}
                >
                  <GridOn />
                </IconButton>
              </Tooltip>
              <Tooltip title="Replace plan from Excel">
                <IconButton
                  sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }, mr: 1 }}
                  onClick={() => setExcelReplaceWizardOpen(true)}
                >
                  <CloudUpload />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title="Expand All">
            <IconButton
              sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }, mr: 1 }}
              onClick={() => handleExpandCollapseAll(true)}
            >
              <UnfoldMore />
            </IconButton>
          </Tooltip>
          <Tooltip title="Collapse All">
            <IconButton
              sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }, mr: 1 }}
              onClick={() => handleExpandCollapseAll(false)}
            >
              <UnfoldLess />
            </IconButton>
          </Tooltip>
          <Tooltip title={showApprovalRollbackOverlay ? 'Hide approval rollback links' : 'Show approval rollback links'}>
            <IconButton
              sx={{
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                mr: 1,
                ...(showApprovalRollbackOverlay && { backgroundColor: 'rgba(255, 255, 255, 0.2)' }),
              }}
              onClick={() => setShowApprovalRollbackOverlay((v) => !v)}
            >
              <AccountTree />
            </IconButton>
          </Tooltip>
          {projectStatus === 'PLANNING' && (
            <Tooltip title="Lock Project">
              <IconButton
                sx={{ backgroundColor: '#ff9800', color: 'white', '&:hover': { backgroundColor: '#f57c00' }, '&:disabled': { backgroundColor: 'rgba(255, 152, 0, 0.3)', color: 'rgba(255, 255, 255, 0.5)' }, mr: 1 }}
                onClick={() => setLockDialogOpen(true)}
                disabled={isLocking || !projectPlan.phases || projectPlan.phases.length === 0}
              >
                <Lock />
              </IconButton>
            </Tooltip>
          )}
          {projectLocked && (
            <Tooltip title="Project is locked. Planning modifications are disabled.">
              <IconButton disabled sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                <LockIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Save (recalculates dates then saves)">
            <IconButton
              sx={{ backgroundColor: 'white', color: '#003366', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }, '&:disabled': { backgroundColor: 'rgba(255, 255, 255, 0.3)', color: 'rgba(255, 255, 255, 0.5)' } }}
              disabled={projectLocked || isSaving || isRecalculating}
              onClick={async () => {
              if (validationErrors.size > 0) {
                setSnackbar({ 
                  open: true, 
                  message: `Cannot save: ${validationErrors.size} validation error(s) found. Please fix errors before saving.`, 
                  severity: 'error' 
                });
                return;
              }
              let planForSave: typeof projectPlan = projectPlan;
              try {
                setIsRecalculating(true);
                setRecalcValidationErrors(null);
                const useBackendRecalculation = isSystemAdmin && projectStatus === 'PLANNING' && urlProjectId && !isNaN(parseInt(urlProjectId, 10));
                if (useBackendRecalculation) {
                  const projectId = parseInt(urlProjectId, 10);
                  const phasePrereqUpdates = projectPlan.phases
                    .filter((p) => p.phaseId > 0)
                    .map((p) => ({
                      phaseId: p.phaseId,
                      prerequisitePhaseIds: Array.isArray((p as unknown as { prerequisites?: number[] }).prerequisites)
                        ? (p as unknown as { prerequisites: number[] }).prerequisites
                        : [],
                    }));
                  if (phasePrereqUpdates.length > 0) {
                    try {
                      await bulkSavePlanning(projectId, { phases: phasePrereqUpdates, tasks: [], actions: [] });
                    } catch (saveErr) {
                      console.warn('Pre-recalc persist phase prerequisites failed:', saveErr);
                    }
                  }
                  setRecalcProgress(null);
                  const recalcResponse = await recalculateProjectDates(projectId, { progress: true });
                  if (recalcResponse.progressLog?.length) {
                    setRecalcProgress(recalcResponse.progressLog[recalcResponse.progressLog.length - 1]);
                  }
                  const planResponse: ProjectPlanResponse =
                    recalcResponse.project != null && recalcResponse.phases != null
                      ? { project: recalcResponse.project, phases: recalcResponse.phases }
                      : await getProjectPlan(projectId);
                  const mappedPlan = mapGetPlanResponseToProjectPlan(planResponse);
                  const planWithDurations = calculateDurationsOnly(mappedPlan);
                  justDidBackendRecalculationRef.current = true;
                  setProjectPlan(planWithDurations);
                  planForSave = planWithDurations;
                  if (recalcResponse.validationErrors?.length) {
                    setRecalcValidationErrors(recalcResponse.validationErrors);
                  } else {
                    setRecalcValidationErrors(null);
                  }
                } else {
                  const recalculatedPlan = calculateDurations(projectPlan);
                  setProjectPlan(recalculatedPlan);
                  planForSave = recalculatedPlan;
                }
              } catch (recalcErr) {
                console.warn('Recalculate before save failed; saving current plan.', recalcErr);
                setSnackbar({ open: true, message: 'Recalculate failed; saving current plan.', severity: 'warning' });
              } finally {
                setRecalcProgress(null);
                setIsRecalculating(false);
              }
              try {
                setIsSaving(true);
                setApiError(null);
                
                // Call backend validation API before save
                if (planForSave.projectId && planForSave.projectId > 0) {
                  try {
                    const validationResult = await validateProjectPlan(planForSave.projectId, planForSave);
                    
                    if (!validationResult.valid && validationResult.errors && validationResult.errors.length > 0) {
                      // Display all validation errors
                      setValidationErrorDialog({
                        open: true,
                        errors: validationResult.errors.map(err => ({
                          type: err.type,
                          id: err.id,
                          error: err.error || err.message || 'Validation error',
                        })),
                      });
                      setIsSaving(false);
                      return; // Don't proceed with save
                    }
                    
                    // Check for other validation errors
                    if (validationResult.code === 'VALIDATION_ERROR') {
                      setBackendValidationError({
                        message: validationResult.message || 'Validation failed',
                        code: 'VALIDATION_ERROR',
                        details: validationResult.details,
                      });
                      setValidationErrorDialog({
                        open: true,
                        errors: validationResult.errors || [],
                      });
                      setIsSaving(false);
                      return; // Don't proceed with save
                    }
                  } catch (validationError) {
                    console.error('Validation API error:', validationError);
                    // Continue with save if validation API fails (don't block save)
                    // The backend will still validate on save
                  }
                }
                
                // Convert dates to ISO strings for API (dates are stored as strings in types)
                // CRITICAL: Use projectStartDateString directly - it's the native value from the TextField
                // NO conversion - use the raw string value
                // IMPORTANT: Convert Date objects to strings, don't convert them to null
                const planToSave = {
                  ...planForSave,
                  projectStartDate: projectStartDateString || (toDateOnly(planForSave.projectStartDate) ?? (typeof planForSave.projectStartDate === 'string' 
                    ? planForSave.projectStartDate.split('T')[0]
                    : getNextBusinessDayDateString())),
                  phases: planForSave.phases.map(phase => ({
                    ...phase,
                    // Convert Date objects to strings, preserve strings, convert null/undefined to null
                    startDate: toDateOnly(phase.startDate) ?? (typeof phase.startDate === 'string' ? phase.startDate.split('T')[0] : null),
                    endDate: toDateOnly(phase.endDate) ?? (typeof phase.endDate === 'string' ? phase.endDate.split('T')[0] : null),
                    tasks: phase.tasks.map(task => ({
                      ...task,
                      // Convert Date objects to strings, preserve strings, convert null/undefined to null
                      startDate: toDateOnly(task.startDate) ?? (typeof task.startDate === 'string' ? task.startDate.split('T')[0] : null),
                      endDate: toDateOnly(task.endDate) ?? (typeof task.endDate === 'string' ? task.endDate.split('T')[0] : null),
                    })),
                  })),
                };
                
                // Save project via API (recalculate already run above)
                // CRITICAL: Use projectStartDateString directly - it's the raw value from the TextField
                // NO conversion through Date objects to avoid timezone shifts
                const dateToSave = projectStartDateString || (typeof planToSave.projectStartDate === 'string' 
                  ? planToSave.projectStartDate.split('T')[0]
                  : getNextBusinessDayDateString());
                
                if (planForSave.projectId && planForSave.projectId > 0) {
                  // Update existing project (server recalculates schedule from ProjectStartDate when PLANNING)
                  await updateProject(planForSave.projectId, {
                    name: planForSave.projectName,
                    projectStartDate: dateToSave,
                  });
                  try {
                    const refreshed = await getProjectPlan(planForSave.projectId);
                    const mapped = mapGetPlanResponseToProjectPlan(refreshed);
                    planForSave = calculateDurationsOnly(mergeScheduleDatesFromServer(planForSave, mapped));
                    justDidBackendRecalculationRef.current = true;
                    setProjectPlan(planForSave);
                  } catch (refreshErr) {
                    console.warn('Save: could not refresh plan after project update:', refreshErr);
                  }
                } else {
                  // Create new project
                  const response = await createProject({
                    name: planForSave.projectName,
                    projectStartDate: dateToSave,
                  });
                  // Update projectId with server-assigned ID
                  setProjectPlan(prev => ({ ...prev, projectId: response.projectId }));
                }
                
                // Save all local changes (prerequisites, source documents, etc.) to backend using bulk save
                const bulkUpdates: BulkSaveRequest['updates'] = {
                  phases: [],
                  tasks: [],
                  actions: [],
                };
                
                // Collect all phase updates (prerequisites AND dates)
                // CRITICAL: Use planForSave directly (not planToSave) to get Date objects before they're converted to null
                for (const phase of planForSave.phases) {
                  if (phase.phaseId > 0) {
                    const prereqs = (phase as any).prerequisites || [];
                    // Convert dates to ISO strings (YYYY-MM-DD format)
                    // Handle both Date objects and strings
                    const startDateStr = toDateOnly(phase.startDate) ?? (typeof phase.startDate === 'string' ? phase.startDate.split('T')[0] : null);
                    const endDateStr = toDateOnly(phase.endDate) ?? (typeof phase.endDate === 'string' ? phase.endDate.split('T')[0] : null);
                    
                                        
                    // Validate displayOrder - must be positive
                    // If displayOrder is invalid (undefined, null, or zero), calculate a valid one based on position
                    let validDisplayOrder = phase.displayOrder || 0;
                    if (validDisplayOrder <= 0) {
                      // Invalid displayOrder - calculate based on position in array
                      const phaseIndex = planForSave.phases.findIndex(p => p.phaseId === phase.phaseId);
                      validDisplayOrder = phaseIndex >= 0 ? phaseIndex + 1 : 1;
                      console.warn(`⚠️ Phase ${phase.phaseId} has invalid displayOrder (${phase.displayOrder}), using calculated value: ${validDisplayOrder}`);
                    }
                    
                    // Only include date fields if they have values (don't send null/undefined)
                    const phaseUpdate: any = {
                      phaseId: phase.phaseId,
                      prerequisitePhaseIds: Array.isArray(prereqs) ? prereqs : [],
                      displayOrder: validDisplayOrder, // Use validated displayOrder to preserve drag-and-drop order
                    };
                    
                    if (startDateStr) {
                      phaseUpdate.plannedStartDate = startDateStr;
                    }
                    if (endDateStr) {
                      phaseUpdate.plannedEndDate = endDateStr;
                    }
                    
                    bulkUpdates.phases!.push(phaseUpdate);
                  }
                }
                
                // Collect all task updates (prerequisites AND dates)
                // CRITICAL: Use planForSave directly (not planToSave) to get Date objects before they're converted to null
                for (const phase of planForSave.phases) {
                  for (const task of phase.tasks) {
                    if (task.taskId > 0) {
                      const prereqs = task.prerequisites || [];
                      // Convert dates to ISO strings (YYYY-MM-DD format)
                      // Handle both Date objects and strings
                      const startDateStr = toDateOnly(task.startDate) ?? (typeof task.startDate === 'string' ? task.startDate.split('T')[0] : null);
                      const endDateStr = toDateOnly(task.endDate) ?? (typeof task.endDate === 'string' ? task.endDate.split('T')[0] : null);
                      
                                            
                      // Only include date fields if they have values (don't send null/undefined)
                      const taskUpdate: any = {
                        taskId: task.taskId,
                        prerequisiteTaskIds: Array.isArray(prereqs) ? prereqs : [],
                        displayOrder: task.displayOrder, // Include displayOrder to preserve drag-and-drop order
                      };
                      
                      if (startDateStr) {
                        taskUpdate.plannedStartDate = startDateStr;
                      }
                      if (endDateStr) {
                        taskUpdate.plannedEndDate = endDateStr;
                      }
                      
                      bulkUpdates.tasks!.push(taskUpdate);
                    }
                  }
                }
                
                // Collect all action updates (source documents, required document count, etc.)
                // CRITICAL: Use planForSave directly (not planToSave) to get the latest source document data
                for (const phase of planForSave.phases) {
                  for (const task of phase.tasks) {
                    for (const action of task.actions) {
                      if (action.actionId > 0) {
                        // Validate displayOrder - must be positive
                        // If displayOrder is invalid (negative or zero), calculate a valid one based on position
                        let validDisplayOrder = action.displayOrder || 0;
                        if (validDisplayOrder <= 0) {
                          // Invalid displayOrder - calculate based on position in array
                          const actionIndex = task.actions.findIndex(a => a.actionId === action.actionId);
                          validDisplayOrder = actionIndex >= 0 ? actionIndex + 1 : 1;
                          console.warn(`⚠️ Action ${action.actionId} has invalid displayOrder (${action.displayOrder}), using calculated value: ${validDisplayOrder}`);
                        }
                        
                        const actionUpdate: any = {
                          actionId: action.actionId,
                          displayOrder: validDisplayOrder, // Use validated displayOrder
                        };
                        
                        // Save source document fields for REVISE, APPROVE, and REVIEW actions
                        if (action.actionType === 'REVISE' || action.actionType === 'APPROVE' || action.actionType === 'REVIEW') {
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          const sourceActionId = (action as any).sourceActionId;
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          const sourceSpItemId = (action as any).sourceSpItemId;
                          
                          actionUpdate.sourceActionId = sourceActionId || null;
                          actionUpdate.sourceSpItemId = sourceSpItemId || null;
                          
                                                  }
                        
                        // Save required document count for CREATE actions
                        if (action.actionType === 'CREATE') {
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          const requiredCount = (action as any).requiredDocumentCount;
                          if (requiredCount !== undefined && requiredCount !== null) {
                            actionUpdate.requiredDocumentCount = requiredCount;
                                                      }
                        }
                        
                        // Save reject action for APPROVE actions
                        if (action.actionType === 'APPROVE') {
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          const rejectActionId = (action as any).rejectActionId;
                          actionUpdate.rejectActionId = rejectActionId !== undefined && rejectActionId !== null ? rejectActionId : null;
                        }
                        
                        bulkUpdates.actions!.push(actionUpdate);
                      }
                    }
                  }
                }
                
                // Execute bulk save (much faster than individual calls)
                // Fallback to individual saves if bulk endpoint is not available (404)
                if (bulkUpdates.phases!.length > 0 || bulkUpdates.tasks!.length > 0 || bulkUpdates.actions!.length > 0) {
                  try {
                    const bulkResult = await bulkSavePlanning(planToSave.projectId, bulkUpdates);

                    // Safely check for errors - handle case where errors might be undefined
                    const hasErrors = bulkResult.errors && Array.isArray(bulkResult.errors) && bulkResult.errors.length > 0;
                    
                    if (!bulkResult.success || hasErrors) {
                      const errorMessages = hasErrors 
                        ? bulkResult.errors.map(e => `${e.type} ${e.id}: ${e.message}`).join(', ')
                        : 'Unknown error occurred during save';
                      console.error('❌ BULK SAVE HAD ERRORS:', {
                        success: bulkResult.success,
                        errorCount: hasErrors ? bulkResult.errors.length : 0,
                        errors: bulkResult.errors || [],
                        errorMessages,
                        updated: bulkResult.updated
                      });
                      setSnackbar({ 
                        open: true, 
                        message: `Some updates failed: ${errorMessages}`, 
                        severity: 'warning' 
                      });
                    }
                  } catch (error) {
                    const apiError = error as ProjectApiError;

                    console.error('❌ BULK SAVE EXCEPTION:', {
                      error,
                      apiError,
                      code: apiError.code,
                      message: apiError.message,
                      stack: error instanceof Error ? error.stack : undefined,
                      response: (error as any).response,
                      status: (error as any).status,
                      statusText: (error as any).statusText
                    });
                    
                    // If bulk endpoint doesn't exist (404), fallback to individual saves
                    if (apiError.code === 'NOT_FOUND') {
                      console.warn('Bulk save endpoint not available, falling back to individual saves');
                      try {
                        // Fallback: Save phases individually
                        // Note: UpdatePhaseRequest doesn't support prerequisitePhaseIds, so skip phase prerequisites in fallback
                        // Phase prerequisites will need to be saved via bulk endpoint when available
                        const phasePromises: Promise<any>[] = [];
                        // Only save phase prerequisites if updatePhase supports it (currently it doesn't)
                        // For now, we'll skip phase prerequisites in the fallback
                        if (bulkUpdates.phases!.length > 0) {
                          console.warn('Phase prerequisites cannot be saved via individual updatePhase calls. Bulk endpoint required.');
                        }
                        
                        // Fallback: Save tasks individually
                        // NOTE: This fallback does NOT save displayOrder because updateTask doesn't support it
                        // This is why the warning appears - displayOrder cannot be saved via individual updates
                        console.warn('⚠️ FALLBACK MODE: Using individual task updates (displayOrder will NOT be saved!)');
                        const taskPromises = bulkUpdates.tasks!.map(taskUpdate => {
                                                    return updateTask(taskUpdate.taskId, {
                            prerequisiteTaskIds: taskUpdate.prerequisiteTaskIds || [],
                            // displayOrder is NOT supported in updateTask - this is why it's not persisting!
                          }).catch(err => {
                            console.error(`Failed to save task ${taskUpdate.taskId}:`, err);
                            return null;
                          });
                        });
                        
                        // Fallback: Save actions individually
                        const actionPromises = bulkUpdates.actions!.map(actionUpdate => 
                          updateAction(actionUpdate.actionId, {
                            sourceActionId: actionUpdate.sourceActionId ?? undefined,
                            sourceSpItemId: actionUpdate.sourceSpItemId ?? undefined,
                            requiredDocumentCount: actionUpdate.requiredDocumentCount ?? undefined,
                          }).catch(err => {
                            console.error(`Failed to save action ${actionUpdate.actionId}:`, err);
                            return null;
                          })
                        );
                        
                        // Execute all saves in parallel
                        await Promise.all([...phasePromises, ...taskPromises, ...actionPromises]);
                                              } catch (fallbackError) {
                        console.error('Error in fallback individual saves:', fallbackError);
                        setSnackbar({ 
                          open: true, 
                          message: 'Some updates may not have been saved. Please check and retry if needed.', 
                          severity: 'warning' 
                        });
                      }
                    } else {
                      // Other errors (not 404) - including 500 errors
                      console.error('Error in bulk save:', error);
                      
                      // Show more specific error message for 500 errors
                      const errorMessage = apiError.code === 'NETWORK_ERROR' && apiError.message.includes('500')
                        ? 'Server error: The bulk save operation failed. Please check backend logs for details.'
                        : `Save failed: ${apiError.message || 'Unknown error'}. Please check and retry if needed.`;
                      
                      setSnackbar({ 
                        open: true, 
                        message: errorMessage, 
                        severity: 'error' // Changed to 'error' since this is a critical failure
                      });
                    }
                  }
                }
                
                // Save document titles/descriptions for CREATE actions with placeholders
                // This needs to be done separately as it's not part of the bulk save
                // Use stored associations (updated via onUpdateAssociations callback) or fetch from backend
                try {
                  const documentSavePromises: Promise<any>[] = [];
                  
                  // Iterate through all CREATE actions in the project plan
                  for (const phase of planToSave.phases) {
                    for (const task of phase.tasks) {
                      for (const action of task.actions) {
                        if (action.actionType === 'CREATE' && action.actionId > 0) {
                          const requiredCount = (action as any).requiredDocumentCount;
                          
                          // Only save if this action has requiredDocumentCount > 1
                          if (requiredCount && requiredCount > 1) {
                            // Use stored associations (which include unsaved changes from dialog) or fetch from backend
                            let associations = actionDocumentAssociations.get(action.actionId);
                            
                            if (!associations || associations.length === 0) {
                              // Fetch current associations from backend as fallback
                              try {
                                associations = await apiService.getActionDocuments(action.actionId);
                                                              } catch (fetchErr) {
                                console.warn(`Failed to fetch associations for action ${action.actionId}:`, fetchErr);
                                continue; // Skip this action if we can't fetch associations
                              }
                            } else {
                                                          }
                            
                            // Filter for placeholder documents (those without files)
                            const placeholderDocs = associations.filter(doc => {
                              const hasNoFile = !doc.spItemId && !doc.localFileId;
                              const explicitlyPlaceholder = doc.isPlaceholder === true;
                              return (hasNoFile || explicitlyPlaceholder) && (doc.isRequired !== false);
                            });
                            
                            if (placeholderDocs.length > 0) {
                              // Prepare updates for titles/descriptions
                              const updates = placeholderDocs.map(doc => ({
                                actionDocumentId: doc.actionDocumentId,
                                documentTitle: doc.documentTitle?.trim() || null,
                                documentDescription: doc.documentDescription?.trim() || null
                              }));
                              
                              // Save all placeholders (even if titles/descriptions are null, to ensure consistency)
                                                            documentSavePromises.push(
                                apiService.updateActionDocuments(action.actionId, updates)
                                  .then(result => {
                                                                        return result;
                                  })
                                  .catch(err => {
                                    console.error(`Failed to save document titles/descriptions for action ${action.actionId}:`, err);
                                    return null; // Don't fail the entire save if one document save fails
                                  })
                              );
                            }
                          }
                        }
                      }
                    }
                  }
                  
                  // Execute all document saves in parallel
                  if (documentSavePromises.length > 0) {
                    await Promise.all(documentSavePromises);
                                      }
                } catch (docSaveError) {
                  console.error('Error saving document titles/descriptions:', docSaveError);
                  // Don't show error to user - document saves are secondary to project save
                }
                
                // Reload project from backend to ensure UI matches database
                justDidSaveRef.current = true;

                try {
                  const reloadedPlan = await loadProjectPlan(planToSave.projectId);

                  const phase170Sent = bulkUpdates.phases?.find(p => p.phaseId === 170);
                  const phase170Received = reloadedPlan.phases.find(p => p.phaseId === 170);
                  const sentEndDate = phase170Sent?.plannedEndDate;
                  const receivedEndDate = toDateOnly(phase170Received?.endDate) ?? phase170Received?.endDate;
                  const datesMatch = sentEndDate === receivedEndDate;

                  if (!datesMatch) {
                    console.error('❌ BACKEND DATE MISMATCH DETECTED!', {
                      sent: sentEndDate,
                      received: receivedEndDate,
                      issue: 'Backend is returning a date that does not match what was sent. This indicates a backend timezone conversion or date persistence issue.',
                    });
                  }

                  setProjectPlan(reloadedPlan);

                  setTimeout(() => {
                    justDidSaveRef.current = false;
                  }, 3000);
                  
                  // Save to localStorage asynchronously (don't block UI)
                  const planJson = JSON.stringify(reloadedPlan);
                  setTimeout(() => {
                    try {
                      localStorage.setItem(`projectPlan-${planForSave.projectId || 'new'}`, planJson);
                      localStorage.setItem('projectPlan', planJson);
                    } catch {
                      // Ignore localStorage errors (quota exceeded, etc.)
                    }
                  }, 0);
                  
                  setSnackbar({ open: true, message: 'Project saved successfully', severity: 'success' });
                } catch (reloadError) {
                  // If reload fails, still update with what we saved (better than nothing)
                  console.error('Failed to reload project after save:', reloadError);
                  setProjectPlan(planToSave);
                  
                  // Save to localStorage asynchronously (don't block UI)
                  const planJson = JSON.stringify(planToSave);
                  setTimeout(() => {
                    try {
                      localStorage.setItem(`projectPlan-${planForSave.projectId || 'new'}`, planJson);
                      localStorage.setItem('projectPlan', planJson);
                    } catch {
                      // Ignore localStorage errors (quota exceeded, etc.)
                    }
                  }, 0);
                  
                  setSnackbar({ 
                    open: true, 
                    message: 'Project saved, but failed to reload from server. Please refresh the page.', 
                    severity: 'warning' 
                  });
                }
              } catch (error) {
                const apiError = error as ProjectApiError;
                const errorMessage = apiError.message || 'Error saving project';
                setApiError(errorMessage);
                setSnackbar({ open: true, message: errorMessage, severity: 'error' });
                console.error('Error saving project:', error);
              } finally {
                setIsSaving(false);
              }
            }}
          >
            {isSaving ? <CircularProgress size={20} sx={{ color: '#003366' }} /> : <Save />}
          </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {isLoading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              Loading project data...
            </Typography>
          </Box>
        )}
        {isRecalculating && (
          <Box sx={{ mb: 2 }} role="status" aria-live="polite" aria-busy="true">
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              {recalcProgress?.statusMessage ?? 'Recalculating schedule…'}
            </Typography>
            {recalcProgress != null && (recalcProgress.totalActions > 0 || recalcProgress.totalPhases > 0) && (
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                {recalcProgress.totalActions > 0 && `Actions: ${recalcProgress.completedActions}/${recalcProgress.totalActions}`}
                {recalcProgress.totalActions > 0 && recalcProgress.totalPhases > 0 && ' · '}
                {recalcProgress.totalPhases > 0 && `Phases: ${recalcProgress.completedPhases}/${recalcProgress.totalPhases}`}
                {recalcProgress.round != null && ` · Round ${recalcProgress.round}`}
              </Typography>
            )}
          </Box>
        )}
        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError(null)}>
            {apiError}
          </Alert>
        )}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">{projectPlan.projectName}</Typography>
          <TextField
            label="Project Start Date"
            type="date"
            value={projectStartDateString}
            onChange={(e) => {
              // Parse date string as local date (not UTC)
              const dateStr = e.target.value; // Format: YYYY-MM-DD
              // Update project start date
              
              // Update the string immediately (this is what the input displays)
              setProjectStartDateString(dateStr);
              
              // CRITICAL: Also update projectPlan.projectStartDate directly with the date string
              // This ensures the date is saved correctly without any timezone conversion
              setProjectPlan(prev => {
                return { ...prev, projectStartDate: dateStr };
              });
              
              // Also update the Date object for calculations
              const [year, month, day] = dateStr.split('-').map(Number);
              if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                // Create Date object in LOCAL timezone (not UTC)
                // Use noon to avoid DST edge cases
                const newDate = new Date(year, month - 1, day, 12, 0, 0, 0);
                // Create Date object for calculations
                setProjectStartDate(newDate);
              }
            }}
            InputLabelProps={{ shrink: true }}
            disabled={projectLocked}
            sx={{ minWidth: 200 }}
          />
        </Box>


        {backendValidationError && (
          <Box sx={{ mb: 2 }}>
            <ValidationErrorDisplay 
              error={backendValidationError} 
              onClose={() => setBackendValidationError(null)}
            />
          </Box>
        )}

        {projectLocked && (
          <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  const projectId = urlProjectId ? parseInt(urlProjectId, 10) : undefined;
                  if (projectId) {
                    navigate(`/execution?projectId=${projectId}`);
                  }
                }}
              >
                Go to Execution View
              </Button>
            }
          >
            This project is locked for execution. Planning data cannot be modified.
          </Alert>
        )}

        {recalcValidationErrors && recalcValidationErrors.length > 0 && !isLoading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Project dates may be inaccurate due to the following errors:
            </Typography>
            <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
              {recalcValidationErrors.map((err, idx) => (
                <li key={idx}>
                  <Typography variant="body2">{err.message}</Typography>
                  {err.actions && err.actions.length > 0 && (
                    <Box component="ul" sx={{ margin: 0, paddingLeft: 2, mt: 0.5 }}>
                      {err.actions.map((a) => (
                        <li key={a.actionId}>
                          <Typography variant="body2" component="span">
                            Action {a.actionId} ({a.taskName})
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  )}
                </li>
              ))}
            </Box>
          </Alert>
        )}

        {validationErrors.size > 0 && !isLoading && loadingPhases.size === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Validation Errors ({validationErrors.size}):
            </Typography>
            <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
              {Array.from(validationErrors.entries()).map(([key, message]) => (
                <li key={key}>
                  <Typography variant="body2">{message}</Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}

        {/* Column Visibility Controls */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Tooltip title="Column Visibility">
            <IconButton
              size="small"
              onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
              sx={{ border: '1px solid', borderColor: 'divider' }}
            >
              <ViewColumn />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={columnMenuAnchor}
            open={Boolean(columnMenuAnchor)}
            onClose={() => setColumnMenuAnchor(null)}
            PaperProps={{ sx: { maxHeight: 400, width: 250 } }}
          >
            <MenuItem disabled>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Quick Views</Typography>
            </MenuItem>
            <MenuItem onClick={() => {
              setVisibleColumns(new Set(PLANNING_QUICK_VIEW_PRESETS.overview));
              setColumnMenuAnchor(null);
            }}>
              Overview
            </MenuItem>
            <MenuItem onClick={() => {
              setVisibleColumns(new Set(PLANNING_QUICK_VIEW_PRESETS.details));
              setColumnMenuAnchor(null);
            }}>
              All Details
            </MenuItem>
            <MenuItem onClick={() => {
              setVisibleColumns(new Set(PLANNING_QUICK_VIEW_PRESETS.dates));
              setColumnMenuAnchor(null);
            }}>
              Dates Focus
            </MenuItem>
            <MenuItem disabled sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1, pt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Columns</Typography>
            </MenuItem>
            {PLANNING_COLUMNS.map(col => (
              <MenuItem
                key={col.key}
                onClick={() => {
                  setVisibleColumns(prev => {
                    const next = new Set(prev);
                    if (next.has(col.key)) {
                      next.delete(col.key);
                    } else {
                      next.add(col.key);
                    }
                    return next;
                  });
                }}
                dense
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  {visibleColumns.has(col.key) ? (
                    <Visibility sx={{ fontSize: 18, color: 'primary.main' }} />
                  ) : (
                    <VisibilityOff sx={{ fontSize: 18, color: 'text.disabled' }} />
                  )}
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {col.label || 'Actions'}
                  </Typography>
                  {col.group && (
                    <Chip label={col.group} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Box>

        <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={handleDragEnd}>
          <SortableContext items={gridRows.map(r => r.id)} strategy={verticalListSortingStrategy}>
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'row', alignItems: 'flex-start', width: '100%' }}>
              <ApprovalRollbackOverlay
                open={showApprovalRollbackOverlay}
                projectPlan={projectPlan}
                scrollContainerRef={planningTableScrollRef}
              />
              {isRecalculating && (
                <Box
                  role="status"
                  aria-live="polite"
                  aria-busy="true"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={40} aria-hidden="true" />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      {recalcProgress?.statusMessage ?? 'Recalculating schedule…'}
                    </Typography>
                    {recalcProgress != null && (recalcProgress.totalActions > 0 || recalcProgress.totalPhases > 0) && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                        {recalcProgress.totalActions > 0 && `Actions: ${recalcProgress.completedActions}/${recalcProgress.totalActions}`}
                        {recalcProgress.totalActions > 0 && recalcProgress.totalPhases > 0 && ' · '}
                        {recalcProgress.totalPhases > 0 && `Phases: ${recalcProgress.completedPhases}/${recalcProgress.totalPhases}`}
                        {recalcProgress.round != null && ` · Round ${recalcProgress.round}`}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              <TableContainer 
                ref={planningTableScrollRef}
                component={Paper} 
                sx={{ 
                  flex: 1,
                  minWidth: 0,
                  maxHeight: 'calc(100vh - 300px)', 
                  border: '1px solid #e0e0e0',
                  opacity: isRecalculating ? 0.5 : 1,
                  pointerEvents: isRecalculating ? 'none' : 'auto',
                }}
              >
              <Table stickyHeader size="small" sx={{ borderCollapse: 'separate' }}>
                <TableHead>
                  <TableRow>
                    {PLANNING_COLUMNS.map(col => (
                      visibleColumns.has(col.key) && (
                        <TableCell 
                          key={col.key}
                          sx={{ 
                            minWidth: col.key === 'number' ? 50 : col.key === 'actions' ? 50 : undefined,
                            width: col.key === 'number' || col.key === 'actions' ? 50 : undefined,
                            backgroundColor: '#f5f5f5', 
                            border: '1px solid #e0e0e0', 
                            fontWeight: 'bold',
                            display: { xs: col.priority === 'low' ? 'none' : 'table-cell', sm: 'table-cell' }
                          }}
                        >
                          {col.label || ''}
                        </TableCell>
                      )
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gridRows.map((row) => (
                    <MemoizedSortableRow
                      key={row.id}
                      row={row}
                      projectPlan={projectPlan}
                      editingCell={editingCell}
                      validationErrors={validationErrors}
                      onCellEdit={handleCellEdit}
                      onCellSave={handleCellSave}
                      getCellValue={getCellValue}
                      getValidationError={getValidationError}
                      getRowValidationError={getRowValidationError}
                      isDateCalculated={isDateCalculated}
                      isDurationCalculated={isDurationCalculated}
                      setEditingCell={setEditingCell}
                      onToggleExpand={handleToggleExpand}
                      expandedRows={expandedRows}
                      visibleColumns={visibleColumns}
                      currentUsersRef={currentUsersRef}
                      userJustSelectedRef={userJustSelectedRef}
                      saveInProgressRef={saveInProgressRef}
                      projectLocked={projectLocked}
                      projectUserIds={projectUserIds}
                      projectUsers={projectUsers}
                      isRecalculating={isRecalculating}
                      setSelectedActionForDocuments={setSelectedActionForDocuments}
                      setDocumentAssociationDialogOpen={setDocumentAssociationDialogOpen}
                      setRejectActionMenuAnchor={setRejectActionMenuAnchor}
                      handleInsertRow={handleInsertRow}
                      handleDeleteRow={handleDeleteRow}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>
          </SortableContext>
        </DndContext>

        {/* Reject Action dropdown menu (for APPROVE actions) */}
        <Menu
          anchorEl={rejectActionMenuAnchor?.el ?? null}
          open={Boolean(rejectActionMenuAnchor)}
          onClose={() => setRejectActionMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MenuItem
            onClick={() => {
              if (rejectActionMenuAnchor) {
                handleCellSave(rejectActionMenuAnchor.rowId, 'rejectAction', '');
                setRejectActionMenuAnchor(null);
              }
            }}
          >
            <em>None</em>
          </MenuItem>
          {rejectActionMenuAnchor && (() => {
            const row = gridRows.find((r) => r.id === rejectActionMenuAnchor.rowId);
            const currentActionId = row && row.type === 'ACTION' ? (row.data as PlanningAction).actionId : null;
            return projectPlan.phases.flatMap((phase) =>
              phase.tasks.flatMap((task) =>
                task.actions
                  .filter(
                    (a) =>
                      a.actionId !== currentActionId &&
                      (a.actionType === 'CREATE' || a.actionType === 'REVISE') &&
                      currentActionId != null &&
                      isRejectTargetAllowed(projectPlan, currentActionId, a.actionId)
                  )
                  .map((a) => {
                    const planId = `${phase.displayOrder}.${task.displayOrder}.${a.displayOrder}`;
                    const label = a.name ? `${planId} - ${a.name}` : planId;
                    return (
                      <MenuItem
                        key={a.actionId}
                        onClick={() => {
                          if (rejectActionMenuAnchor) {
                            handleCellSave(rejectActionMenuAnchor.rowId, 'rejectAction', String(a.actionId));
                            setRejectActionMenuAnchor(null);
                          }
                        }}
                      >
                        {label}
                      </MenuItem>
                    );
                  })
              )
            );
          })()}
        </Menu>

        {/* Assignment Validation Error Dialog */}
        <Dialog
          open={assignmentValidationError.open}
          onClose={() => setAssignmentValidationError({ open: false, actions: [] })}
          aria-labelledby="assignment-validation-dialog-title"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="assignment-validation-dialog-title" sx={{ color: '#d32f2f' }}>
            Cannot Lock Project - Missing Action Assignments
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="assignment-validation-dialog-description" sx={{ mb: 2 }}>
              The project cannot be locked because {assignmentValidationError.actions.length} action(s) are missing assigned users.
              Please assign users to all actions before locking the project.
            </DialogContentText>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Actions Missing Assignments:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                {assignmentValidationError.actions.map((action, index) => (
                  <Box component="li" key={index} sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {action.actionName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Task: "{action.taskName}" • Phase: "{action.phaseName}"
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setAssignmentValidationError({ open: false, actions: [] })}
              variant="contained"
              color="primary"
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Lock Project Confirmation Dialog */}
        <Dialog
          open={lockDialogOpen}
          onClose={() => !isLocking && setLockDialogOpen(false)}
          aria-labelledby="lock-dialog-title"
          aria-describedby="lock-dialog-description"
        >
          <DialogTitle id="lock-dialog-title">
            Lock Project for Execution
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="lock-dialog-description">
              Are you sure you want to lock this project? Once locked:
              <ul style={{ marginTop: '8px', marginBottom: '8px' }}>
                <li>Planning data cannot be modified</li>
                <li>An execution plan will be created automatically</li>
                <li>You will be redirected to the Execution View</li>
              </ul>
              This action cannot be undone. Make sure all planning data is correct before locking.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setLockDialogOpen(false)} 
              disabled={isLocking}
              color="inherit"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleLockProject} 
              disabled={isLocking}
              color="warning"
              variant="contained"
              startIcon={<Lock />}
            >
              {isLocking ? 'Locking...' : 'Lock Project'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Replace plan from Excel wizard (PLANNING only) */}
        {projectStatus === 'PLANNING' && projectPlan?.projectId > 0 && (
          <ExcelImportWizard
            open={excelReplaceWizardOpen}
            onClose={() => setExcelReplaceWizardOpen(false)}
            mode="replace"
            projectId={projectPlan.projectId}
            projectName={projectPlan.projectName}
            projectStartDate={projectPlan.projectStartDate}
            onSuccess={async (id) => {
              setExcelReplaceWizardOpen(false);
              try {
                const loaded = await loadProjectPlan(id);
                setProjectPlan(loaded);
                setProjectStatus('PLANNING');
              } catch (err) {
                console.error('Reload after Excel replace failed:', err);
                setSnackbar({ open: true, message: 'Plan replaced; refresh the page if you do not see updates.', severity: 'info' });
              }
            }}
          />
        )}

        {/* Validation Error Dialog */}
        <Dialog
          open={validationErrorDialog.open}
          onClose={() => setValidationErrorDialog({ open: false, errors: [] })}
          aria-labelledby="validation-error-dialog-title"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="validation-error-dialog-title" sx={{ color: '#d32f2f' }}>
            Validation Errors
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="validation-error-dialog-description" sx={{ mb: 2 }}>
              Cannot save project plan. Please fix the following errors:
            </DialogContentText>
            <List>
              {validationErrorDialog.errors.map((err, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={`${err.type} ${err.id}`}
                    secondary={err.error}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setValidationErrorDialog({ open: false, errors: [] })}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Document Association Dialog */}
        <Dialog
          open={documentAssociationDialogOpen}
          onClose={() => {
            setDocumentAssociationDialogOpen(false);
            setSelectedActionForDocuments(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Document Associations
            <IconButton
              aria-label="close"
              onClick={() => {
                setDocumentAssociationDialogOpen(false);
                setSelectedActionForDocuments(null);
              }}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedActionForDocuments && (
              <DocumentAssociationPanel
                actionId={selectedActionForDocuments.actionId}
                actionType={selectedActionForDocuments.actionType}
                requiredDocumentCount={selectedActionForDocuments.requiredDocumentCount}
                projectId={projectPlan.projectId}
                projectPlan={projectPlan}
                onCreateAssociations={() => {
                }}
                onUpdateAssociations={(associations) => {
                                    // Store associations for this action so we can save titles/descriptions when project is saved
                  if (selectedActionForDocuments) {
                    setActionDocumentAssociations(prev => {
                      const newMap = new Map(prev);
                      newMap.set(selectedActionForDocuments.actionId, associations);
                      return newMap;
                    });
                  }
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDocumentAssociationDialogOpen(false);
                setSelectedActionForDocuments(null);
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Document Selection Dialog for Drag-and-Drop (Multi-Document CREATE Actions) */}
        <Dialog
          open={documentSelectionDialogOpen}
          onClose={() => {
            setDocumentSelectionDialogOpen(false);
            setPendingDragOperation(null);
            setAvailableSourceDocuments([]);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Select Source Document
            <IconButton
              aria-label="close"
              onClick={() => {
                setDocumentSelectionDialogOpen(false);
                setPendingDragOperation(null);
                setAvailableSourceDocuments([]);
              }}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {pendingDragOperation && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  The source CREATE action has multiple documents. Please select which specific document should be used as the source for the {pendingDragOperation.targetAction.actionType} action.
                </Typography>
                
                <Typography variant="subtitle2" gutterBottom>
                  Source Action: {pendingDragOperation.sourceAction.actionType} - {pendingDragOperation.sourceAction.name || 'Unnamed'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Target Action: {pendingDragOperation.targetAction.actionType} - {pendingDragOperation.targetAction.name || 'Unnamed'}
                </Typography>

                <Divider sx={{ my: 2 }} />

                {availableSourceDocuments.length === 0 ? (
                  <Alert severity="info">
                    No documents available in the source CREATE action yet. Please create documents first.
                  </Alert>
                ) : (
                  <List>
                    {availableSourceDocuments
                      .sort((a, b) => (a.documentNumber || 0) - (b.documentNumber || 0))
                      .map((doc) => (
                      <ListItem
                        key={doc.actionDocumentId}
                        button
                        onClick={async () => {
                          try {
                            // Create document association with selected document
                            const createdAssociations = await apiService.createActionDocuments(pendingDragOperation.targetAction.actionId, [{
                              documentType: 'SOURCE',
                              sourceActionId: pendingDragOperation.sourceAction.actionId,
                              sourceActionDocumentId: doc.actionDocumentId,
                              documentNumber: doc.documentNumber || 1,
                              isRequired: true,
                            }]);

                            // Update cache with new associations for source document display
                            setSourceDocumentAssociationsCache(prev => {
                              const newMap = new Map(prev);
                              newMap.set(pendingDragOperation.targetAction.actionId, createdAssociations);
                              return newMap;
                            });

                            // Also update sourceActionId on action for backward compatibility
                            setProjectPlan(prevPlan => {
                              const updatedPlan = { ...prevPlan };
                              // Find the target action in the project plan
                              for (const phase of updatedPlan.phases) {
                                for (const task of phase.tasks) {
                                  const actionIndex = task.actions.findIndex(
                                    a => a.actionId === pendingDragOperation.targetAction.actionId
                                  );
                                  if (actionIndex >= 0) {
                                    task.actions[actionIndex].sourceActionId = pendingDragOperation.sourceAction.actionId;
                                    return updatedPlan;
                                  }
                                }
                              }
                              return updatedPlan;
                            });

                            setSnackbar({
                              open: true,
                              message: 'Source document set. Click Save to persist changes.',
                              severity: 'success'
                            });

                            // Close dialog
                            setDocumentSelectionDialogOpen(false);
                            setPendingDragOperation(null);
                            setAvailableSourceDocuments([]);
                          } catch (err: any) {
                            console.error('Failed to create document association:', err);
                            setSnackbar({
                              open: true,
                              message: err.message || 'Failed to set source document. Please try again.',
                              severity: 'error'
                            });
                          }
                        }}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            borderColor: 'primary.main',
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Description />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body1" fontWeight="medium">
                              {`${doc.documentTitle || doc.fileName || 'Document'} – ${pendingDragOperation?.targetAction?.actionType ?? 'Document'}`}
                            </Typography>
                          }
                          secondary={
                            <>
                              {doc.documentDescription && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {doc.documentDescription}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                  Document #{doc.documentNumber}
                                </Typography>
                                {doc.isPlaceholder && (
                                  <Chip label="Placeholder" size="small" color="default" />
                                )}
                                {doc.fileName && (
                                  <Chip label={`${doc.fileName} – ${pendingDragOperation?.targetAction?.actionType ?? 'Document'}`} size="small" color="primary" />
                                )}
                              </Box>
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDocumentSelectionDialogOpen(false);
                setPendingDragOperation(null);
                setAvailableSourceDocuments([]);
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity} 
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Actor Workload by Month */}
        <ActorWorkloadDisplay projectPlan={projectPlan} holidayDates={holidayDates} projectUsers={projectUsers} />

        {/* Gantt Chart View */}
        {(() => {
          const ganttData = projectPlanToGanttData(projectPlan);
          return <GanttChartView {...ganttData} title="Gantt Chart View" />;
        })()}

        {/* Project User Management */}
        {urlProjectId && !isNaN(parseInt(urlProjectId, 10)) && parseInt(urlProjectId, 10) > 0 && (
          <Box sx={{ mt: 4 }}>
            <ProjectUserManagement 
              projectId={parseInt(urlProjectId, 10)} 
              onUsersUpdated={() => {
                // Refresh project users when users are added/removed/invited
                const projectId = parseInt(urlProjectId, 10);
                if (!isNaN(projectId) && projectId > 0) {
                  loadProjectUsers(projectId);
                }
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Actor Workload Display Component
interface ActorWorkloadDisplayProps {
  projectPlan: ProjectPlan;
  holidayDates: Set<string>; // Set of YYYY-MM-DD holiday dates
  projectUsers?: ProjectUser[]; // Project users to get user types
}

interface ActorWorkload {
  actor: string;
  monthlyHours: Map<string, number>; // Map of month key (YYYY-MM) to hours
  totalHours: number;
}

const ActorWorkloadDisplay = ({ projectPlan, holidayDates, projectUsers = [] }: ActorWorkloadDisplayProps) => {
  const [expanded, setExpanded] = useState(false);
  
  // Create a map from email to userType
  const emailToUserTypeMap = useMemo(() => {
    const map = new Map<string, string | null>();
    projectUsers.forEach(pu => {
      const email = pu.user?.email ?? pu.userEmail ?? '';
      const userType = pu.user?.userType ?? pu.userType ?? pu.userUserType ?? null;
      if (email) {
        map.set(email, userType);
      }
    });
    return map;
  }, [projectUsers]);
  
  // Helper function to get user type label
  const getUserTypeLabel = (userType: string | null): string => {
    if (!userType) return 'Unassigned';
    switch (userType.toUpperCase()) {
      case 'TENANT':
        return 'Tenant';
      case 'FRAPORT':
        return 'Fraport';
      case 'CLIENT':
        return 'Client';
      default:
        return userType;
    }
  };
  
  // Helper function to format date as YYYY-MM-DD
  const formatDateAsYYYYMMDD = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Helper function to check if a date is a business day (not weekend and not holiday)
  const isBusinessDay = (date: Date): boolean => {
    // Check if weekend
    if (isWeekend(date)) {
      return false;
    }
    // Check if holiday
    const dateStr = formatDateAsYYYYMMDD(date);
    return !holidayDates.has(dateStr);
  };

  // Calculate actor workload by month
  const actorWorkloads = useMemo((): ActorWorkload[] => {
    const workloads = new Map<string, ActorWorkload>();
    
    // Use the project start date as the baseline
    // Convert string to Date object for date-fns functions
    const projectStartDateStr = projectPlan.projectStartDate;
    if (!projectStartDateStr) {
      return [];
    }
    const projectStartDate = typeof projectStartDateStr === 'string' 
      ? new Date(projectStartDateStr + 'T00:00:00')
      : projectStartDateStr;
    
    // Get project end date from phases (use calculated dates from the plan)
    let projectEndDate: Date | null = null;
    
    projectPlan.phases.forEach(phase => {
      if (phase.endDate) {
        const phaseEndDateObj = new Date(phase.endDate + 'T00:00:00');
        if (!projectEndDate || phaseEndDateObj > projectEndDate) {
          projectEndDate = phaseEndDateObj;
        }
      }
    });
    
    if (!projectEndDate) {
      return [];
    }
    
    // Helper function to add working hours (same logic as main component, skip weekends and holidays)
    const addWorkingHoursLocal = (startDate: Date, hours: number): Date => {
      if (hours === 0) {
        return new Date(startDate);
      }
      
      const WORK_DAY_START = 8; // 8 AM
      const WORK_DAY_END = 17; // 5 PM
      
      let currentDate = new Date(startDate);
      let remainingHours = hours;
      
      // Ensure we start at the beginning of a work day
      currentDate.setHours(WORK_DAY_START, 0, 0, 0);
      
      // Skip weekends and holidays if we start on one
      while (!isBusinessDay(currentDate)) {
        currentDate = addDays(currentDate, 1);
      }
      
      // Process hours day by day
      while (remainingHours > 0) {
        const hoursAvailableToday = WORK_DAY_END - currentDate.getHours();
        
        if (remainingHours <= hoursAvailableToday) {
          // All remaining hours fit in today
          currentDate.setHours(currentDate.getHours() + remainingHours);
          remainingHours = 0;
        } else {
          // Use all available hours today, move to next working day
          remainingHours -= hoursAvailableToday;
          currentDate = addDays(currentDate, 1);
          currentDate.setHours(WORK_DAY_START, 0, 0, 0);
          
          // Skip weekends and holidays
          while (!isBusinessDay(currentDate)) {
            currentDate = addDays(currentDate, 1);
          }
        }
      }
      
      return currentDate;
    };
    
    // Process all actions using the actual calculated dates from the project plan
    projectPlan.phases.forEach(phase => {
      phase.tasks.forEach(task => {
        // Use the actual calculated task dates from the plan
        if (!task.startDate || !task.endDate || task.actions.length === 0) {
          return;
        }
        
        // Calculate cumulative start date for each action within the task
        // Actions are sequential, so we need to calculate their dates based on task start
        let currentActionStart = new Date(task.startDate);
        currentActionStart.setHours(8, 0, 0, 0);
        
        // Skip weekends if needed
        while (isWeekend(currentActionStart)) {
          currentActionStart = addDays(currentActionStart, 1);
        }
        
        // Process each action sequentially
        task.actions.forEach(action => {
          if (!action.assignedTo || action.assignedTo.length === 0) {
            // Still advance the start date for next action even if no one is assigned
            currentActionStart = addWorkingHoursLocal(currentActionStart, action.duration);
            return;
          }
          
          // Store action start date before calculating end date
          const actionStartDate = new Date(currentActionStart);
          
          // Calculate action end date (start + duration in working hours)
          const actionEndDate = addWorkingHoursLocal(currentActionStart, action.duration);
          
          // Ensure action doesn't exceed task end date (use task end date if available)
          const taskEndDateObj = task.endDate ? (typeof task.endDate === 'string' ? new Date(task.endDate + 'T00:00:00') : task.endDate) : null;
          const effectiveEndDate = (taskEndDateObj && actionEndDate > taskEndDateObj) 
            ? taskEndDateObj 
            : actionEndDate;
          
          // Distribute hours across assigned users
          const hoursPerUser = action.duration / action.assignedTo.length;
          
          // Move start date forward for next action
          currentActionStart = new Date(actionEndDate);
          
          action.assignedTo.forEach(actor => {
            if (!workloads.has(actor)) {
              workloads.set(actor, {
                actor,
                monthlyHours: new Map<string, number>(),
                totalHours: 0,
              });
            }
            
            const workload = workloads.get(actor)!;
            
            // Calculate which months this action spans
            // Start from project start month, end at project end month
            const actionStartMonth = startOfMonth(actionStartDate);
            const actionEndMonth = endOfMonth(effectiveEndDate);
            
            // Generate months for this action (only if we have valid dates)
            const actionMonths = eachMonthOfInterval({ 
              start: actionStartMonth, 
              end: actionEndMonth 
            });
            
            // Only process months that are >= project start date
            const projectStartMonth = startOfMonth(projectStartDate);
            
            actionMonths.forEach(month => {
              // Skip months before project start
              if (month < projectStartMonth) {
                return;
              }
              
              const monthStart = startOfMonth(month);
              const monthEnd = endOfMonth(month);
              
              // Check if action overlaps with this month
              if (effectiveEndDate >= monthStart && actionStartDate <= monthEnd) {
                // Calculate overlap dates (clamp to month boundaries)
                const overlapStart = actionStartDate > monthStart ? actionStartDate : monthStart;
                const overlapEnd = effectiveEndDate < monthEnd ? effectiveEndDate : monthEnd;
                
                // Count business days in the overlap period
                let businessDaysInOverlap = 0;
                let currentDate = new Date(overlapStart);
                currentDate.setHours(0, 0, 0, 0);
                const overlapEndDate = new Date(overlapEnd);
                overlapEndDate.setHours(23, 59, 59, 999);
                
                while (currentDate <= overlapEndDate) {
                  if (isBusinessDay(currentDate)) {
                    businessDaysInOverlap++;
                  }
                  currentDate = addDays(currentDate, 1);
                }
                
                // Count total business days in the action
                let totalBusinessDays = 0;
                let actionDate = new Date(actionStartDate);
                actionDate.setHours(0, 0, 0, 0);
                const actionEndDateObj = new Date(effectiveEndDate);
                actionEndDateObj.setHours(23, 59, 59, 999);
                
                while (actionDate <= actionEndDateObj) {
                  if (isBusinessDay(actionDate)) {
                    totalBusinessDays++;
                  }
                  actionDate = addDays(actionDate, 1);
                }
                
                // Distribute hours proportionally based on business days (not calendar time)
                const hoursInMonth = totalBusinessDays > 0 
                  ? (businessDaysInOverlap / totalBusinessDays) * hoursPerUser
                  : 0;
                
                // Use the month from the Date object directly to avoid timezone issues
                const monthKey = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`;
                const currentHours = workload.monthlyHours.get(monthKey) || 0;
                workload.monthlyHours.set(monthKey, currentHours + hoursInMonth);
              }
            });
            
            workload.totalHours += hoursPerUser;
          });
        });
      });
    });
    
    return Array.from(workloads.values()).sort((a, b) => a.actor.localeCompare(b.actor));
  }, [projectPlan]);
  
  // Get all unique months for column headers
  const allMonths = useMemo(() => {
    const months = new Set<string>();
    actorWorkloads.forEach(workload => {
      workload.monthlyHours.forEach((_, monthKey) => {
        months.add(monthKey);
      });
    });
    return Array.from(months).sort();
  }, [actorWorkloads]);
  
  // Group workloads by user type and calculate totals based on rounded days (not hours)
  const groupedWorkloads = useMemo(() => {
    const groups = new Map<string | null, { workloads: ActorWorkload[], totals: Map<string, number>, totalDays: number }>();
    
    actorWorkloads.forEach(workload => {
      const userType = emailToUserTypeMap.get(workload.actor) ?? null;
      
      if (!groups.has(userType)) {
        groups.set(userType, {
          workloads: [],
          totals: new Map<string, number>(),
          totalDays: 0,
        });
      }
      
      const group = groups.get(userType)!;
      group.workloads.push(workload);
      
      // Sum up monthly rounded days (not hours) to match displayed values
      workload.monthlyHours.forEach((hours, monthKey) => {
        const days = Math.round(hours / 8);
        const current = group.totals.get(monthKey) || 0;
        group.totals.set(monthKey, current + days);
      });
      
      // Don't accumulate group.totalDays here - we'll calculate it from the monthly totals
    });
    
    // Sort workloads within each group
    groups.forEach(group => {
      group.workloads.sort((a, b) => a.actor.localeCompare(b.actor));
      
      // Calculate group totalDays by summing the monthly rounded days
      group.totalDays = Array.from(group.totals.values()).reduce((sum, days) => sum + days, 0);
    });
    
    return groups;
  }, [actorWorkloads, emailToUserTypeMap]);
  
  // Calculate overall project totals based on rounded days from groups
  const projectTotals = useMemo(() => {
    const totals = new Map<string, number>();
    
    // Sum up from group totals (which are already in rounded days)
    groupedWorkloads.forEach(group => {
      group.totals.forEach((days, monthKey) => {
        const current = totals.get(monthKey) || 0;
        totals.set(monthKey, current + days);
      });
    });
    
    // Calculate project totalDays by summing the monthly rounded days
    const totalDays = Array.from(totals.values()).reduce((sum, days) => sum + days, 0);
    
    return { totals, totalDays };
  }, [groupedWorkloads]);
  
  // Define user type display order
  const userTypeOrder = ['FRAPORT', 'CLIENT', 'TENANT', null];

  return (
    <Accordion 
      expanded={expanded} 
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{ mt: 3 }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Actor Workload by Month
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {actorWorkloads.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No actors assigned to actions in this project.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Actor</TableCell>
                  {allMonths.map(monthKey => (
                    <TableCell 
                      key={monthKey} 
                      align="right" 
                      sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                    >
                      {format(new Date(monthKey + '-01T00:00:00'), 'MMM yyyy')}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userTypeOrder.map(userType => {
                  const group = groupedWorkloads.get(userType);
                  if (!group || group.workloads.length === 0) return null;
                  
                  const userTypeLabel = getUserTypeLabel(userType);
                  
                  return (
                    <Fragment key={userType ?? 'unassigned'}>
                      {/* User type header row */}
                      <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                        <TableCell colSpan={allMonths.length + 2} sx={{ fontWeight: 'bold', py: 1 }}>
                          {userTypeLabel}
                        </TableCell>
                      </TableRow>
                      
                      {/* Individual actor rows */}
                      {group.workloads.map(workload => {
                        // Calculate total days by summing rounded days from each month (to match displayed values)
                        const actorTotalDays = allMonths.reduce((sum, monthKey) => {
                          const hours = workload.monthlyHours.get(monthKey) || 0;
                          const days = Math.round(hours / 8);
                          return sum + days;
                        }, 0);
                        
                        return (
                          <TableRow key={workload.actor}>
                            <TableCell sx={{ fontWeight: 'medium', pl: 4 }}>{workload.actor}</TableCell>
                            {allMonths.map(monthKey => {
                              const hours = workload.monthlyHours.get(monthKey) || 0;
                              const days = Math.round(hours / 8);
                              return (
                                <TableCell key={monthKey} align="right">
                                  <Typography variant="body2">
                                    {hours > 0 ? (
                                      <>{days}d</>
                                    ) : (
                                      <Typography component="span" sx={{ color: 'text.secondary' }}>-</Typography>
                                    )}
                                  </Typography>
                                </TableCell>
                              );
                            })}
                            <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                              <Typography variant="body2">
                                {actorTotalDays}d
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      
                      {/* User type subtotal row */}
                      <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                        <TableCell sx={{ fontWeight: 'bold', pl: 2 }}>
                          {userTypeLabel} Total
                        </TableCell>
                        {allMonths.map(monthKey => {
                          const days = group.totals.get(monthKey) || 0;
                          return (
                            <TableCell key={monthKey} align="right" sx={{ fontWeight: 'bold' }}>
                              <Typography variant="body2">
                                {days > 0 ? (
                                  <>{days}d</>
                                ) : (
                                  <Typography component="span" sx={{ color: 'text.secondary' }}>-</Typography>
                                )}
                              </Typography>
                            </TableCell>
                          );
                        })}
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          <Typography variant="body2">
                            {group.totalDays}d
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
                
                {/* Overall project total row */}
                <TableRow sx={{ backgroundColor: '#e0e0e0', borderTop: 2, borderColor: 'divider' }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    Project Total
                  </TableCell>
                  {allMonths.map(monthKey => {
                    const days = projectTotals.totals.get(monthKey) || 0;
                    return (
                      <TableCell key={monthKey} align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                        <Typography variant="body2">
                          {days > 0 ? (
                            <>{days}d</>
                          ) : (
                            <Typography component="span" sx={{ color: 'text.secondary' }}>-</Typography>
                          )}
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    <Typography variant="body2">
                      {projectTotals.totalDays}d
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ProjectPlanningPage;

